/**
 * Wall Newspaper builder — main app
 *
 * Konva-based canvas editor for school/holiday wall newspapers. Multi-A4 layout.
 * Tools: select / text / image / draw / shape / sticker / background.
 * Phase 1: tools, layers, properties, undo/redo. Phase 1.x: ZIP save/load,
 * jsPDF multi-page tile, sticker library, templates.
 *
 * Uses window.WN_I18N (set in blade), Konva (loaded vendor), and
 * standard Timbrica globals: __ae, __aeCritical, __toast, __toolSettings,
 * __validateFileSize, __validateFileType.
 */
(function () {
    'use strict';

    const SLUG = 'wall-newspaper';
    const I18N = window.WN_I18N || {};
    const t = (key, fallback) => I18N[key] || fallback || key;

    // ─── Constants ────────────────────────────────────────────────────
    // 300 DPI = 11.811 px/mm. A4 = 210×297 mm.
    const DPI = 300;
    const PX_PER_MM = DPI / 25.4;
    const A4_W_MM = 210;
    const A4_H_MM = 297;

    // Hard cap so iOS Safari does not OOM at 270+ MP canvases.
    const MAX_COLS = 4;
    const MAX_ROWS = 3;

    const HISTORY_CAP = 60;

    // Display fonts — see <style> @font-face declarations in the blade file.
    // The first item is the default; "system" labels resolve to fontFamily
    // strings Konva can hand to canvas without further setup.
    const FONT_FAMILIES = [
        { id: 'Inter',             label: 'Inter (default)' },
        { id: 'Pacifico',          label: 'Pacifico (cursive)' },
        { id: 'Caveat',            label: 'Caveat (handwritten)' },
        { id: 'Playfair Display',  label: 'Playfair (serif)' },
        { id: 'Lobster',           label: 'Lobster (display)' },
        { id: 'Times',             label: 'Times (serif)' },
        { id: 'Courier New',       label: 'Courier (mono)' },
    ];

    // WordArt presets — applied on top of fill/stroke. `null` means restore base.
    const WORDART_PRESETS = ['none', 'rainbow', 'shadow3d', 'outline', 'glow'];

    // Default palette — works as both background swatches and quick-color picker.
    const PALETTE = [
        '#ffffff', '#f7e9b9', '#ffd6c2', '#ffc4dc', '#cfe9ff', '#d4f0c8',
        '#fff3a0', '#ffaf63', '#ff6b9a', '#5cb3ff', '#7dd87a', '#ffd54f',
        '#111111', '#444444', '#888888', '#c0c0c0', '#3b82f6', '#e91e63',
    ];

    // Visible name for each element type — used in layers panel.
    const TYPE_LABELS = {
        frame:   () => t('layer_frame', 'Photo slot'),
        text:    () => t('layer_text', 'Text'),
        image:   () => t('layer_image', 'Photo'),
        shape:   () => t('layer_shape', 'Shape'),
        drawing: () => t('layer_drawing', 'Drawing'),
        sticker: () => t('layer_sticker', 'Sticker'),
    };

    // ─── Globals helpers ──────────────────────────────────────────────
    const $ = (id) => document.getElementById(id);
    const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));

    function ae(event, payload) {
        try { window.__ae?.(event, Object.assign({ slug: SLUG }, payload || {})); } catch (e) { /* noop */ }
    }
    function aeCritical(event, payload) {
        try { window.__aeCritical?.(event, Object.assign({ slug: SLUG }, payload || {})); } catch (e) { /* noop */ }
    }

    function toast(msg, type) {
        if (window.__toast) { window.__toast(msg, type || 'info'); return; }
        const el = $('wnToast');
        if (!el) return;
        el.textContent = msg;
        el.hidden = false;
        clearTimeout(toast._t);
        toast._t = setTimeout(() => { el.hidden = true; }, 2200);
    }

    function announce(msg) { try { window.__announce?.(msg); } catch (e) { /* noop */ } }

    function ulid() {
        const ENC = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
        let s = '';
        let ts = Date.now();
        for (let i = 0; i < 10; i++) { s = ENC[ts % 32] + s; ts = Math.floor(ts / 32); }
        const r = new Uint8Array(16);
        crypto.getRandomValues(r);
        for (let i = 0; i < 16; i++) s += ENC[r[i] % 32];
        return s;
    }

    // ─── Konva readiness (Promise-mutex) ──────────────────────────────
    let _konvaReady = null;
    function whenKonvaReady() {
        if (_konvaReady) return _konvaReady;
        _konvaReady = new Promise((resolve, reject) => {
            const start = Date.now();
            (function poll() {
                if (window.Konva) return resolve(window.Konva);
                if (Date.now() - start > 15000) {
                    aeCritical('konva_timeout', { ms: Date.now() - start });
                    return reject(new Error('Konva failed to load'));
                }
                setTimeout(poll, 80);
            })();
        });
        return _konvaReady;
    }

    // ─── Application state ────────────────────────────────────────────
    const state = {
        project: null,    // current project.json
        konva: null,      // { stage, layers: { bg, content, overlay }, tr, bgRect, selectionRect }
        tool: 'select',
        zoom: 1,
        dirty: false,
        history: { stack: [], ptr: -1, busy: false },
        sizeChoice: {
            mode: 'tiled', pages: 6,
            cols: 3, rows: 2, orient: 'landscape', margin_mm: 10, overlap_mm: 10,
        },
        /* Viewport camera, in board coordinates: the world point sitting at
           the top-left of the visible area. The Konva stage is only as big as
           the visible area and this moves under it. See applyCamera(). */
        cam: { x: 0, y: 0 },
        spaceHeld: false,
        // Asset storage — Map<id, { blob, url, source }>. Image elements reference asset_id.
        assets: new Map(),
        // Drawing state for shape/draw tools (active drag-to-create operation)
        drawing: null,
        // Last picked colors for quick reuse
        lastColor: '#111111',
        lastFill: '#5cb3ff',
        lastBg: '#ffffff',
    };

    // ─── Canvas dimensions ────────────────────────────────────────────
    // The arithmetic lives in geometry.js so the exporter cannot drift from
    // the editor. What comes back is the FINISHED, glued poster size — smaller
    // than cols × sheet, because each sheet keeps a printer margin and each
    // seam spends one overlap band. See geometry.js for the diagram.
    function boardLayout() {
        const c = state.project.canvas;
        return window.WNGeom.layoutFromCanvas(c);
    }

    function layoutFromChoice(choice) {
        return window.WNGeom.layout({
            paper: 'A4',
            orient: choice.orient,
            mode: choice.mode,
            pages: choice.pages,
            cols: choice.cols,
            rows: choice.rows,
            margin_mm: choice.margin_mm,
            overlap_mm: choice.overlap_mm,
        });
    }

    /** Is this board a set of separate A4 pages rather than one glued poster? */
    function isPagesMode() {
        return state.project?.canvas?.layout_mode === 'pages';
    }

    /**
     * Default names for the pages of a classroom corner.
     *
     * These are the rubrics teachers actually use — the ones that appear on
     * every published set of классный уголок templates. A page called
     * "График дежурства" is findable in the page map; a page called "B2" is
     * not, and the whole point of this mode is that you come back next month
     * to reprint exactly one of them.
     */
    function defaultPageTitles(n) {
        const raw = t('corner_default_pages', '');
        const list = raw ? raw.split('|').map((x) => x.trim()).filter(Boolean) : [];
        const out = [];
        for (let i = 0; i < n; i++) {
            out.push(list[i] || t('corner_page_n', 'Page :n').replace(':n', i + 1));
        }
        return out;
    }

    function pageTitle(index) {
        const titles = state.project?.canvas?.page_titles;
        if (Array.isArray(titles) && titles[index]) return titles[index];
        return t('corner_page_n', 'Page :n').replace(':n', index + 1);
    }

    /** What to call sheet (c, r) in the UI and on the paper. */
    function sheetName(c, r) {
        const L = boardLayout();
        if (L.mode === 'pages') return pageTitle(L.pageIndex(c, r));
        return window.WNGeom.sheetLabel(c, r);
    }


    // ─── Consent banner inset ─────────────────────────────────────────
    /**
     * Keep dialog actions clear of the cookie-consent banner.
     *
     * The 152-ФЗ banner is fixed to bottom:0 at z-index 9998 — above every
     * modal on the page. On a 390 px-wide phone the size picker's "Create"
     * button lands exactly underneath it, so a first-time visitor taps the
     * banner instead of the button and the tool appears to do nothing. Raising
     * the modal over the banner is not an option: consent has to stay
     * reachable. Reserving its height underneath the dialog is.
     *
     * Measured rather than hard-coded, because the banner wraps to two or
     * three lines depending on locale and viewport.
     */
    function syncConsentInset() {
        let h = 0;
        const b = document.querySelector('.cc-banner');
        if (b && document.body.contains(b)) {
            const cs = getComputedStyle(b);
            if (cs.display !== 'none' && cs.visibility !== 'hidden') h = b.getBoundingClientRect().height || 0;
        }
        document.documentElement.style.setProperty('--wn-consent-h', Math.round(h) + 'px');
    }

    function watchConsentInset() {
        syncConsentInset();
        // The banner is dismissed by a click and hides on the next tick.
        document.addEventListener('click', () => setTimeout(syncConsentInset, 80), true);
        window.addEventListener('resize', syncConsentInset);
    }

    // ─── Formatting helpers ───────────────────────────────────────────
    // Sheet counts are pluralised server-side (the blade resolves every form
    // 0..12 through trans_choice), because Russian needs three forms and
    // Arabic five, and no JS-side approximation gets both right. Before this
    // existed the size line read "печатать 6 листа".
    function sheetsCountLabel(n) {
        const forms = I18N.sheets_count_forms;
        if (Array.isArray(forms) && forms[n]) return forms[n];
        return n + ' ' + t('sheets_word', 'sheets');
    }

    /** mm → cm string, one decimal only when it carries information. */
    function fmtCm(mm) {
        const cm = mm / 10;
        return (Math.abs(cm - Math.round(cm)) < 0.05) ? String(Math.round(cm)) : cm.toFixed(1);
    }

    /* Board coordinates are 300-dpi pixels, which is the right unit for the
       renderer and a useless one for a person: the position panel used to say
       a heading was at "x = 2799". Everything the user types or reads is
       centimetres of actual paper. */
    function pxToCm(px) { return px / PX_PER_MM / 10; }
    function cmToPx(cm) { return cm * 10 * PX_PER_MM; }
    function cmField(px) { return (Math.round(pxToCm(px) * 10) / 10).toFixed(1); }

    // ─── Size picker ──────────────────────────────────────────────────
    /* Named starting points. A wall newspaper is chosen by where it will hang,
       not by a column count, so the presets lead and the sliders follow. Sizes
       are computed live from the same geometry the exporter uses, so the cm on
       the card is the cm you can measure on the wall. */
    const SIZE_PRESETS = [
        { key: 'sheet',    cols: 1, rows: 1, orient: 'portrait'  },
        { key: 'board',    cols: 2, rows: 1, orient: 'landscape' },
        { key: 'classic',  cols: 3, rows: 2, orient: 'landscape' },
        { key: 'tall',     cols: 2, rows: 3, orient: 'portrait'  },
        { key: 'wall',     cols: 4, rows: 3, orient: 'landscape' },
    ];

    /* Pocket counts that classroom stands are actually sold in. A teacher
       chooses "the stand on the wall has six pockets", not a grid. */
    const PAGE_PRESETS = [
        { key: 'p3',  pages: 3  },
        { key: 'p4',  pages: 4  },
        { key: 'p6',  pages: 6  },
        { key: 'p8',  pages: 8  },
        { key: 'p12', pages: 12 },
    ];

    function openSizePicker() {
        const modal = $('wnSizeModal');
        const cols = $('wnSizeCols');
        const rows = $('wnSizeRows');
        const pagesIn = $('wnSizePages');
        const colsOut = $('wnSizeColsOut');
        const rowsOut = $('wnSizeRowsOut');
        const marginIn = $('wnSizeMargin');
        const overlapIn = $('wnSizeOverlap');
        const info = $('wnSizeInfo');
        const preview = $('wnSizePreview');
        const orientBtns = $$('.wn-orient-btn', modal);
        const modeBtns = $$('.wn-mode-card', modal);

        cols.max = String(MAX_COLS);
        rows.max = String(MAX_ROWS);
        pagesIn.max = String(window.WNGeom.MAX_PAGES);
        cols.value = String(state.sizeChoice.cols);
        rows.value = String(state.sizeChoice.rows);
        pagesIn.value = String(state.sizeChoice.pages || 6);
        marginIn.value = String(state.sizeChoice.margin_mm ?? window.WNGeom.DEFAULT_MARGIN_MM);
        overlapIn.value = String(state.sizeChoice.overlap_mm ?? window.WNGeom.DEFAULT_OVERLAP_MM);
        let orient = state.sizeChoice.orient;
        let mode = state.sizeChoice.mode === 'pages' ? 'pages' : 'tiled';

        function current() {
            return {
                mode,
                pages: parseInt(pagesIn.value, 10),
                cols: parseInt(cols.value, 10),
                rows: parseInt(rows.value, 10),
                orient,
                margin_mm: parseInt(marginIn.value, 10),
                overlap_mm: parseInt(overlapIn.value, 10),
                seed: !!($('wnSizeSeed') && $('wnSizeSeed').checked),
            };
        }

        function syncOrientButtons() {
            orientBtns.forEach((b) => {
                const active = b.dataset.orient === orient;
                b.classList.toggle('is-active', active);
                b.setAttribute('aria-pressed', active ? 'true' : 'false');
            });
        }

        function syncModeButtons() {
            modeBtns.forEach((b) => {
                const active = b.dataset.mode === mode;
                b.classList.toggle('is-active', active);
                b.setAttribute('aria-checked', active ? 'true' : 'false');
            });
            const pagesMode = mode === 'pages';
            // Controls that mean nothing in the other mode are removed, not
            // disabled: a greyed "glue overlap" on a set of separate pages
            // still suggests the pages might get glued.
            $('wnPagesCountRow').hidden = !pagesMode;
            $('wnSizeCustom').hidden = pagesMode;
            $('wnSizeOverlapRow').hidden = pagesMode;
            $('wnSizeOverlapHint').hidden = pagesMode;
            const desc = $('wnSizeDesc');
            if (desc) desc.textContent = pagesMode
                ? t('size_picker_desc_pages', '')
                : t('size_picker_desc', '');
        }

        function renderPresets() {
            const host = $('wnSizePresets');
            if (!host) return;
            const cur = current();
            host.innerHTML = '';
            const list = mode === 'pages' ? PAGE_PRESETS : SIZE_PRESETS;
            for (const p of list) {
                const opts = mode === 'pages'
                    ? { mode: 'pages', pages: p.pages, orient: cur.orient, margin_mm: cur.margin_mm }
                    : { cols: p.cols, rows: p.rows, orient: p.orient, margin_mm: cur.margin_mm, overlap_mm: cur.overlap_mm };
                const L = window.WNGeom.layout(opts);
                const active = mode === 'pages'
                    ? cur.pages === p.pages
                    : (cur.cols === p.cols && cur.rows === p.rows && cur.orient === p.orient);
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'wn-preset' + (active ? ' is-active' : '');
                btn.setAttribute('role', 'radio');
                btn.setAttribute('aria-checked', active ? 'true' : 'false');
                const cells = [];
                const total = L.cols * L.rows;
                for (let i = 0; i < total; i++) {
                    cells.push(i < L.pages ? '<i></i>' : '<i class="is-empty"></i>');
                }
                const glyphAspect = (L.poster_w_mm / L.poster_h_mm).toFixed(3);
                // In pages mode the size that matters is ONE page, not the
                // editing surface: nothing on the wall is the surface.
                const dim = mode === 'pages'
                    ? `${fmtCm(L.usable_w_mm)}×${fmtCm(L.usable_h_mm)} ${t('cm_label', 'cm')}`
                    : `${fmtCm(L.poster_w_mm)}×${fmtCm(L.poster_h_mm)} ${t('cm_label', 'cm')}`;
                btn.innerHTML =
                    '<span class="wn-preset-glyph-box"><span class="wn-preset-glyph' + (mode === 'pages' ? ' is-pages' : '') + '" style="grid-template-columns:repeat(' + L.cols + ',1fr);aspect-ratio:' + glyphAspect + '">' + cells.join('') + '</span></span>'
                    + '<span class="wn-preset-name">' + escapeHtml(t('size_preset_' + p.key, p.key)) + '</span>'
                    + '<span class="wn-preset-dim">' + escapeHtml(dim) + '</span>';
                btn.onclick = () => {
                    if (mode === 'pages') {
                        pagesIn.value = String(p.pages);
                    } else {
                        cols.value = String(p.cols);
                        rows.value = String(p.rows);
                        orient = p.orient;
                        syncOrientButtons();
                    }
                    update();
                };
                host.appendChild(btn);
            }
        }

        function update() {
            const cur = current();
            colsOut.textContent = cur.cols;
            rowsOut.textContent = cur.rows;
            $('wnSizePagesOut').textContent = cur.pages;
            $('wnSizeMarginOut').innerHTML = cur.margin_mm + '&nbsp;' + escapeHtml(t('mm_label', 'mm'));
            $('wnSizeOverlapOut').innerHTML = cur.overlap_mm + '&nbsp;' + escapeHtml(t('mm_label', 'mm'));

            const L = window.WNGeom.layout(cur);
            const total = L.pages;

            info.innerHTML = L.mode === 'pages'
                ? `<strong>${escapeHtml(sheetsCountLabel(total))}</strong>`
                    + ` <span class="wn-size-info-sep">·</span> ${t('size_each_page', 'each')} `
                    + `${fmtCm(L.usable_w_mm)} × ${fmtCm(L.usable_h_mm)} ${escapeHtml(t('cm_label', 'cm'))}`
                : `<strong>${fmtCm(L.poster_w_mm)} × ${fmtCm(L.poster_h_mm)} ${escapeHtml(t('cm_label', 'cm'))}</strong>`
                    + ` <span class="wn-size-info-sep">·</span> ${escapeHtml(sheetsCountLabel(total))}`;

            // Aspect-true sheet preview. The old one stretched every cell into
            // a fixed-height box, so a portrait board and a landscape one drew
            // the same rectangle.
            preview.classList.toggle('is-pages', L.mode === 'pages');
            preview.style.aspectRatio = (L.poster_w_mm / L.poster_h_mm).toFixed(3);
            preview.style.gridTemplateColumns = 'repeat(' + L.cols + ', 1fr)';
            preview.style.gridTemplateRows = 'repeat(' + L.rows + ', 1fr)';
            preview.innerHTML = '';
            for (let y = 0; y < L.rows; y++) {
                for (let x = 0; x < L.cols; x++) {
                    const cell = document.createElement('div');
                    const exists = L.pageExists(x, y);
                    cell.className = 'wn-sheet-cell' + (exists ? '' : ' is-empty');
                    cell.textContent = exists
                        ? (L.mode === 'pages' ? String(L.pageIndex(x, y) + 1) : window.WNGeom.sheetLabel(x, y))
                        : '';
                    preview.appendChild(cell);
                }
            }
            renderPresets();
        }

        cols.oninput = update;
        rows.oninput = update;
        pagesIn.oninput = update;
        marginIn.oninput = update;
        overlapIn.oninput = update;
        orientBtns.forEach((btn) => {
            btn.onclick = () => { orient = btn.dataset.orient; syncOrientButtons(); update(); };
        });
        modeBtns.forEach((btn) => {
            btn.onclick = () => {
                mode = btn.dataset.mode === 'pages' ? 'pages' : 'tiled';
                // Pockets are portrait; a glued poster is usually landscape.
                orient = mode === 'pages' ? 'portrait' : orient;
                syncModeButtons();
                syncOrientButtons();
                update();
            };
        });
        syncOrientButtons();
        syncModeButtons();
        update();

        $('wnSizeCancel').onclick = () => { modal.hidden = true; };
        $('wnSizeOk').onclick = () => {
            state.sizeChoice = current();
            modal.hidden = true;
            createBlankProject();
        };
        syncConsentInset();
        modal.hidden = false;
        ae('size_picker_open', {});
    }

    // ─── Project lifecycle ────────────────────────────────────────────
    async function createBlankProject() {
        const { orient } = state.sizeChoice;
        const L = layoutFromChoice(state.sizeChoice);
        state.project = {
            version: '1.0',
            meta: {
                id: ulid(),
                title: t('untitled', 'Untitled'),
                created_at: new Date().toISOString(),
                modified_at: new Date().toISOString(),
                locale: window.__LOCALE || document.documentElement.lang || 'en',
                app_version: '1.0.0',
            },
            canvas: {
                // `geometry: 'v2'` marks a board whose tiles are cut with a
                // printer margin and a duplicated glue overlap. Boards without
                // it are the old edge-to-edge kind and must keep their exact
                // pixel geometry when reopened — see WNGeom.layoutFromCanvas.
                geometry: 'v2',
                // 'tiled' = one poster glued from overlapping sheets.
                // 'pages' = N separate A4 pages, one per classroom-stand pocket.
                layout_mode: L.mode,
                pages: L.pages,
                page_titles: L.mode === 'pages' ? defaultPageTitles(L.pages) : null,
                paper: 'A4',
                orient,
                sheets: { cols: L.cols, rows: L.rows },
                dpi: DPI,
                overlap_mm: L.overlap_mm,
                margin_mm: L.margin_mm,
                width_mm: L.poster_w_mm,
                height_mm: L.poster_h_mm,
                width_px: L.width_px,
                height_px: L.height_px,
                background: { type: 'color', color: '#ffffff' },
            },
            guides: { show_grid: false, grid_mm: 10, snap_enabled: true, show_sheet_borders: true },
            groups: [],
            elements: [],
        };
        $('wnTitle').value = state.project.meta.title;
        _blockCascade = 0;
        state.history.stack = [];
        state.history.ptr = -1;
        state.dirty = false;
        state.assets.clear();

        await openWorkspace();
        // Seeded BEFORE the first history entry, so the starter is the board's
        // baseline rather than something the first undo can wipe.
        const seeded = (L.mode === 'pages' && state.sizeChoice.seed !== false) ? seedCornerPages() : 0;
        pushHistory();
        ae('project_create_blank', {
            cols: L.cols, rows: L.rows, orient, mode: L.mode, pages: L.pages, seeded,
        });
    }

    async function openWorkspace() {
        $('wnStart').hidden = true;
        $('wnWorkspace').hidden = false;
        // Timbrica global CSS hides all children of `[class$="-workspace"]` until
        // it carries `.active`. Add it so our top bar / main / bottom bar render.
        $('wnWorkspace').classList.add('active');
        document.body.classList.add('wn-workspace-active');

        try {
            await whenKonvaReady();
        } catch (e) {
            toast(t('konva_load_fail', 'Could not load graphics engine. Refresh and retry.'), 'error');
            return;
        }
        buildStage();
        renderPageNav();
        renderLayers();
        renderProperties();
        installKeyboardShortcuts();
        installCanvasDragDrop();
        installPasteHandler();
        installViewportGestures();
        installContextMenu();
        // Layout has not settled yet — Timbrica's `[class$="-workspace"]:not(.active)`
        // cascade may have just been overridden, so the canvas-area's clientWidth
        // can read 0 if we measure synchronously. Defer zoomToFit two frames so
        // the browser has actually resolved the new layout.
        requestAnimationFrame(() => requestAnimationFrame(() => {
            // A 3×2 board is 2.2× as wide as it is tall; fitting all of it into
            // a portrait phone leaves a 130 px strip you cannot work in. One
            // sheet filling the width is the readable unit there, and the sheet
            // map is one tap away for moving between them.
            if (window.innerWidth <= PHONE_MAX) zoomToSheet();
            else zoomToFit();
        }));
        announce('Workspace ready');

        // Workspace tour for first-timers
        maybeStartOnboarding('workspace');
    }

    // ─── Konva stage init ─────────────────────────────────────────────
    function buildStage() {
        const stageEl = $('wnStage');
        const Konva = window.Konva;
        const cv = state.project.canvas;

        // Tear down a previous Konva stage on the same container so we don't
        // leak Stage instances when the workflow goes workspace → workspace
        // (e.g. opening a template after creating a blank project, or recovery).
        if (state.konva?.stage) {
            try { state.konva.stage.destroy(); } catch (e) { /* noop */ }
            state.konva = null;
        }

        // The stage is the size of the VISIBLE AREA, not of the board.
        //
        // It used to be the board: a 3×2 sheet canvas is 9933×4606 px at
        // 300 dpi, and Konva allocated that bitmap once per layer. At fit zoom
        // the scaled-down stage kept that harmless, but zooming to 100% asked
        // the browser for three 46 MP canvases (~180 MB each), and on a 4×3
        // board at the old 2× ceiling for four times that again — a tab crash,
        // reachable with two clicks on the zoom button.
        //
        // With a camera the cost is fixed at viewport size no matter how large
        // the board is, and panning becomes something the tool controls, which
        // is what makes it usable by touch at all: Konva sets touch-action:none
        // on its container, so the old native-scroll approach could not be
        // panned with a finger.
        const vp = viewportSize();
        stageEl.style.width = vp.w + 'px';
        stageEl.style.height = vp.h + 'px';

        const stage = new Konva.Stage({
            container: stageEl,
            width: vp.w,
            height: vp.h,
        });
        const bg = new Konva.Layer({ listening: false });
        const content = new Konva.Layer();
        const overlay = new Konva.Layer({ listening: false });

        // Paper. A tiled board is ONE sheet of artwork; a pages board is N
        // separate sheets with a gap between them, and drawing that gap is the
        // whole point — it is what says "these end up in different pockets"
        // before any label does.
        //
        // The drop shadow and hairline edge exist because a white board on a
        // near-white working area had nothing marking where it started or
        // stopped; at fit zoom the only visible feature was the seam guides.
        const L0 = window.WNGeom.layoutFromCanvas(cv);
        const paperFill = cv.background?.color || '#ffffff';
        const paperRects = [];
        const mkPaper = (x, y, w, h) => {
            const r = new Konva.Rect({
                x, y, width: w, height: h,
                fill: paperFill, listening: false,
                stroke: 'rgba(15,23,42,.20)',
                // Stroke and shadow are specified in board pixels, so they have
                // to scale with zoom to stay a constant thickness on screen;
                // that is what refreshPaperChrome() does on every camera change.
                strokeWidth: 1,
                shadowColor: 'rgba(15,23,42,.35)',
                shadowBlur: 24,
                shadowOffsetY: 8,
                shadowOpacity: 1,
            });
            r.setAttr('isPaper', true);
            bg.add(r);
            paperRects.push(r);
            return r;
        };

        let bgRect;
        if (L0.mode === 'pages') {
            for (const sr of L0.sheetRectsPX()) mkPaper(sr.x, sr.y, sr.w, sr.h);
            // Keep a handle for the background-colour picker; it repaints all.
            bgRect = paperRects[0];
        } else {
            bgRect = mkPaper(0, 0, cv.width_px, cv.height_px);
        }

        // Sheet seams. What is drawn is the GLUE BAND — the strip of artwork
        // that prints on both neighbours — not a hairline. That band is the
        // only place where the assembled poster can show a paper edge, so
        // showing its real width is what lets someone keep a face out of it.
        // A board with no overlap falls back to a single dashed line.
        const showSeams = window.__toolSettings?.load?.(SLUG, 'show_seams', true) !== false
            && state.project.guides?.show_sheet_borders;
        const L = boardLayout();
        const bands = L.mode === 'pages' ? [] : L.overlapBandsPX();
        if (L.mode === 'pages') {
            // Separate pages need no seam guides — the gap between them is the
            // seam. What they do need is the printable area marked, because
            // artwork outside it is simply lost on that page.
            for (const sr of L.sheetRectsPX()) {
                const t = L.tileRectPX(sr.c, sr.r);
                const safe = new Konva.Rect({
                    x: t.x, y: t.y, width: t.w, height: t.h,
                    stroke: '#5cb3ff', strokeWidth: 2, dash: [14, 10],
                    opacity: 0.45, listening: false,
                });
                safe.setAttr('isSeam', true);
                safe.visible(showSeams);
                overlay.add(safe);
            }
        } else if (bands.length) {
            for (const b of bands) {
                const band = new Konva.Rect({
                    x: b.x, y: b.y, width: b.w, height: b.h,
                    fill: '#5cb3ff', opacity: 0.10, listening: false,
                });
                band.setAttr('isSeam', true);
                band.visible(showSeams);
                overlay.add(band);
                const mk = (pts) => {
                    const ln = new Konva.Line({
                        points: pts, stroke: '#5cb3ff', strokeWidth: 2,
                        dash: [16, 12], opacity: 0.55, listening: false,
                    });
                    ln.setAttr('isSeam', true);
                    ln.visible(showSeams);
                    overlay.add(ln);
                };
                if (b.axis === 'v') {
                    mk([b.x, 0, b.x, cv.height_px]);
                    mk([b.x + b.w, 0, b.x + b.w, cv.height_px]);
                } else {
                    mk([0, b.y, cv.width_px, b.y]);
                    mk([0, b.y + b.h, cv.width_px, b.y + b.h]);
                }
            }
        } else {
            const strideW = cv.width_px / cv.sheets.cols;
            const strideH = cv.height_px / cv.sheets.rows;
            for (let i = 1; i < cv.sheets.cols; i++) {
                const ln = new Konva.Line({
                    points: [i * strideW, 0, i * strideW, cv.height_px],
                    stroke: '#5cb3ff', strokeWidth: 2, dash: [16, 12], opacity: 0.5, listening: false,
                });
                ln.setAttr('isSeam', true);
                ln.visible(showSeams);
                overlay.add(ln);
            }
            for (let i = 1; i < cv.sheets.rows; i++) {
                const ln = new Konva.Line({
                    points: [0, i * strideH, cv.width_px, i * strideH],
                    stroke: '#5cb3ff', strokeWidth: 2, dash: [16, 12], opacity: 0.5, listening: false,
                });
                ln.setAttr('isSeam', true);
                ln.visible(showSeams);
                overlay.add(ln);
            }
        }

        // A quiet sheet label in each tile's corner. On a 4×3 board, "which
        // sheet am I looking at" is a real question once you zoom past fit,
        // and the answer belongs on the board rather than only in the map.
        const tr = new Konva.Transformer({
            anchorSize: 11,
            borderStroke: '#3b82f6',
            anchorStroke: '#1d4ed8',
            anchorFill: '#fff',
            ignoreStroke: true,
            keepRatio: true,
            rotationSnaps: [0, 15, 30, 45, 60, 75, 90, 105, 120, 135, 150, 165,
                180, 195, 210, 225, 240, 255, 270, 285, 300, 315, 330, 345],
            rotationSnapTolerance: 4,
        });
        content.add(tr);

        // Selection rectangle (rubber-band) — hidden by default.
        const selectionRect = new Konva.Rect({
            fill: 'rgba(59,130,246,0.15)',
            stroke: '#3b82f6',
            strokeWidth: 1,
            visible: false,
            listening: false,
        });
        overlay.add(selectionRect);

        stage.add(bg);
        stage.add(content);
        stage.add(overlay);

        state.konva = { stage, layers: { bg, content, overlay }, tr, bgRect, paperRects, selectionRect };

        // After state.konva exists — renderSheetLabels() reads it and would
        // otherwise return early on the very first build, leaving every sheet
        // unnamed until something else happened to redraw them.
        renderSheetLabels();

        // Stage-level event handlers (per-tool dispatch happens inside).
        stage.on('mousedown touchstart', onStagePointerDown);
        stage.on('mousemove touchmove',  onStagePointerMove);
        stage.on('mouseup touchend',     onStagePointerUp);
        stage.on('click tap',            onStageClick);
        stage.on('wheel',                onStageWheel);

        tr.on('transformend', () => { commitTransform(); pushHistory(); positionElToolbar(); });
        tr.on('dragend',      () => { commitTransform(); pushHistory(); positionElToolbar(); });
        tr.on('dragmove transform', () => { positionElToolbar(); });
    }

    // ─── Floating element toolbar ─────────────────────────────────────
    // A small floating row of buttons (Duplicate / To-front / To-back / Delete)
    // anchored above the currently-selected element. Mirrors the design handoff
    // "wn-el-toolbar" pattern. Single-selection only — for multi-select the
    // right-panel alignment toolbar takes over. Hidden during edit / no selection.
    function positionElToolbar() {
        const tb = $('wnElToolbar');
        if (!tb) return;
        const nodes = selectedNodes();
        // Hide if not exactly 1 element selected, or while inline text editor open.
        if (!nodes || nodes.length !== 1 || _editingTextNode) { tb.hidden = true; return; }
        const node = nodes[0];
        const stage = state.konva?.stage;
        if (!stage) { tb.hidden = true; return; }
        try {
            // Ask for the rect relative to the content layer, which carries no
            // transform of its own — that is board space — then project it
            // through the camera. Going via the stage would mix in the stage
            // transform and the toolbar would drift as soon as you panned.
            const r = node.getClientRect({ relativeTo: state.konva.layers.content });
            const p = boardToScreen(r.x, r.y);
            const vp = viewportSize();
            const w = r.width * state.zoom;
            let left = p.x;
            let top = p.y - 38;               // 28 (toolbar) + 4 + 6 padding
            if (top < 4) top = p.y + r.height * state.zoom + 8;  // flip below when clipped
            // Keep it inside the viewport instead of letting it slide off with
            // an element dragged to the edge.
            left = Math.max(4, Math.min(left, vp.w - Math.max(120, w) - 4));
            top = Math.max(4, Math.min(top, vp.h - 40));
            tb.style.left = left + 'px';
            tb.style.top = top + 'px';
            tb.hidden = false;
        } catch (e) {
            tb.hidden = true;
        }
    }

    function wireElToolbar() {
        const tb = $('wnElToolbar');
        if (!tb) return;
        tb.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-cmd]');
            if (!btn) return;
            const nodes = selectedNodes();
            if (!nodes || nodes.length !== 1) return;
            const cmd = btn.dataset.cmd;
            if (cmd === 'duplicate') duplicateNodes(nodes);
            else if (cmd === 'to-front') {
                nodes.forEach((n) => n.moveToTop());
                state.konva.tr.moveToTop();
                state.konva.layers.content.batchDraw();
                markDirty(); pushHistory(); renderLayers();
            } else if (cmd === 'to-back') {
                nodes.forEach((n) => n.moveToBottom());
                state.konva.layers.content.batchDraw();
                markDirty(); pushHistory(); renderLayers();
            } else if (cmd === 'delete') {
                deleteNodes(nodes);
            }
            ae('el_toolbar_action', { cmd });
            positionElToolbar();
        });
    }

    // ─── Coordinate helpers ───────────────────────────────────────────
    function stagePointerPos() {
        // getPointerPosition() is in stage-container pixels and does NOT
        // include the stage transform, so the camera has to be added back to
        // get board coordinates.
        const p = state.konva.stage.getPointerPosition();
        if (!p) return { x: 0, y: 0 };
        return { x: p.x / state.zoom + state.cam.x, y: p.y / state.zoom + state.cam.y };
    }

    // ─── Sheet map / mini-map ─────────────────────────────────────────
    /* The bottom strip is a scale model of the board: one cell per printed
       sheet, in the board's real proportions, with a box showing what the
       viewport is currently looking at. On a 4×3 board most of the artwork is
       off-screen at any useful zoom, and a row of buttons labelled A1…D3 does
       not tell you where you are — the outline does. */
    let _pageFocus = { c: 0, r: 0 };

    function renderPageNav() {
        const nav = $('wnPageNav');
        if (!nav || !state.project) return;
        const L = boardLayout();
        nav.innerHTML = '';

        const info = document.createElement('span');
        info.className = 'wn-pagenav-info';
        info.innerHTML = L.mode === 'pages'
            ? `<strong>${L.pages}</strong> ${escapeHtml(t('paper_a4', 'A4'))}`
                + ` <span class="wn-pagenav-dot">·</span> `
                + `<strong>${fmtCm(L.usable_w_mm)}</strong>×<strong>${fmtCm(L.usable_h_mm)}</strong> ${escapeHtml(t('cm_label', 'cm'))}`
            : `<strong>${L.pages}</strong> ${escapeHtml(t('paper_a4', 'A4'))}`
                + ` <span class="wn-pagenav-dot">·</span> `
                + `<strong>${fmtCm(L.poster_w_mm)}</strong>×<strong>${fmtCm(L.poster_h_mm)}</strong> ${escapeHtml(t('cm_label', 'cm'))}`;
        nav.appendChild(info);

        const map = document.createElement('div');
        map.className = 'wn-minimap';
        map.style.gridTemplateColumns = `repeat(${L.cols}, 1fr)`;
        map.style.gridTemplateRows = `repeat(${L.rows}, 1fr)`;
        map.style.aspectRatio = (L.poster_w_mm / L.poster_h_mm).toFixed(3);
        map.setAttribute('aria-label', t('pagenav_label', 'Sheets'));

        for (let r = 0; r < L.rows; r++) {
            for (let c = 0; c < L.cols; c++) {
                if (!L.pageExists(c, r)) {
                    const hole = document.createElement('span');
                    hole.className = 'wn-minimap-hole';
                    map.appendChild(hole);
                    continue;
                }
                const cell = document.createElement('button');
                cell.type = 'button';
                cell.className = 'wn-minimap-cell' + (c === _pageFocus.c && r === _pageFocus.r ? ' is-focus' : '');
                const name = sheetName(c, r);
                // A named page shows its number in the map and its name in the
                // tooltip: twelve full rubric names would not fit, and the
                // number is what the printed order uses.
                cell.textContent = L.mode === 'pages' ? String(L.pageIndex(c, r) + 1) : name;
                cell.title = name + (L.mode === 'pages' ? ' — ' + t('page_rename_hint', 'double-click to rename') : '');
                cell.onclick = () => scrollToSheet(c, r);
                if (L.mode === 'pages') {
                    cell.ondblclick = (e) => { e.stopPropagation(); renamePage(L.pageIndex(c, r)); };
                }
                map.appendChild(cell);
            }
        }

        const view = document.createElement('div');
        view.className = 'wn-minimap-view';
        view.id = 'wnMiniView';
        view.setAttribute('aria-hidden', 'true');
        map.appendChild(view);

        nav.appendChild(map);
        renderMiniMap();
    }

    /**
     * Rename a page.
     *
     * The name is the whole reason this mode is usable a month later: the duty
     * rota changes, the teacher comes back, finds "График дежурства" in the
     * map, and reprints that one page. A grid id would not survive the gap.
     */

    /**
     * Carry a page's new name onto the heading printed on that page.
     *
     * Only if the heading still says what the starter wrote. Once a teacher
     * has retyped the bar — "6-А, ноябрь" over "Объявления" — it is their
     * text, and a rename in the map must not overwrite it.
     */
    function syncCornerTitle(index, oldName, newName) {
        if (!state.konva || oldName === newName) return;
        for (const n of state.konva.layers.content.children) {
            if (n.getAttr('elCornerTitle') !== index) continue;
            if (!n.text || n.text() !== oldName) continue;
            n.text(newName);
            // The bar was sized for the old words; the new ones may be longer.
            const width = n.width ? n.width() : 0;
            if (width) fitHeading(n, width * 0.9, n.fontSize() * 0.5);
        }
        state.konva.layers.content.batchDraw();
    }

    function renamePage(index) {
        const cv = state.project?.canvas;
        if (!cv || !Array.isArray(cv.page_titles)) return;
        const next = window.prompt(t('page_rename_prompt', 'Name of this page'), cv.page_titles[index] || '');
        if (next === null) return;
        const before = cv.page_titles[index];
        cv.page_titles[index] = next.trim().slice(0, 60);
        syncCornerTitle(index, before, cv.page_titles[index]);
        markDirty();
        pushHistory();
        renderPageNav();
        renderSheetLabels();
        ae('page_rename', {});
    }

    /** Move the "you are here" box. Called on every camera change. */
    function renderMiniMap() {
        const view = $('wnMiniView');
        const cv = state.project?.canvas;
        if (!view || !cv) return;
        const vp = viewportSize();
        const wWorld = vp.w / state.zoom;
        const hWorld = vp.h / state.zoom;
        const pct = (v) => (Math.max(0, Math.min(1, v)) * 100).toFixed(2) + '%';
        view.style.left = pct(state.cam.x / cv.width_px);
        view.style.top = pct(state.cam.y / cv.height_px);
        view.style.width = pct(wWorld / cv.width_px);
        view.style.height = pct(hWorld / cv.height_px);
        // When the whole board is on screen the box would trace the frame and
        // add nothing, so it steps aside instead of drawing a redundant border.
        view.hidden = wWorld >= cv.width_px && hWorld >= cv.height_px;
    }

    function scrollToSheet(c, r) {
        const L = boardLayout();
        const rect = L.tileRectPX(c, r);
        _pageFocus = { c, r };
        // Centre the sheet rather than parking its corner at the origin — on a
        // wide board a corner-anchored jump put most of the chosen sheet
        // off-screen, which read as "the button does nothing".
        centreOn(rect.x + rect.w / 2, rect.y + rect.h / 2);
        renderPageNav();
    }

    // ─── Viewport camera ──────────────────────────────────────────────
    /* Everything the canvas draws is in BOARD coordinates (300 dpi pixels).
       The camera maps them to the screen:  screen = (board - cam) * zoom.
       Konva expresses the same thing as stage.position + stage.scale, which is
       what applyCamera() sets. Nothing else in the file needs to know. */

    /* Breathing room around the board, in screen px. Fixed at 48 it ate a
       third of a phone viewport's width and left the board looking like a
       stamp, so it scales with the viewport and keeps a sane floor. */
    function viewPad() {
        const vp = viewportSize();
        return Math.max(12, Math.min(48, Math.round(Math.min(vp.w, vp.h) * 0.05)));
    }

    function viewportSize() {
        const el = $('wnCanvasScroll');
        const r = el ? el.getBoundingClientRect() : null;
        return {
            w: Math.max(120, Math.round(r?.width  || 800)),
            h: Math.max(120, Math.round(r?.height || 500)),
        };
    }

    function minZoom() {
        // Never let the board shrink below "a third of the screen" — past that
        // it is a stamp floating in grey and there is nothing to aim at.
        const cv = state.project?.canvas;
        const vp = viewportSize();
        if (!cv) return 0.02;
        return Math.min(1, Math.min(vp.w / cv.width_px, vp.h / cv.height_px) / 3);
    }

    function maxZoom() {
        // 4× is enough to nudge a caption by a hair; beyond that the board is
        // bigger than any use for it and the stage bitmap stays viewport-sized
        // anyway, so the ceiling is about usefulness, not memory.
        return 4;
    }

    function clampCamera() {
        const cv = state.project?.canvas;
        if (!cv) return;
        const vp = viewportSize();
        const z = state.zoom;
        const boardW = cv.width_px * z;
        const boardH = cv.height_px * z;
        const PAD = viewPad();
        const padX = PAD / z;
        const padY = PAD / z;

        if (boardW + 2 * PAD <= vp.w) {
            // Board fits horizontally — pin it centred rather than letting it
            // drift, so "fit" and "zoom out" always land somewhere sensible.
            state.cam.x = (cv.width_px - vp.w / z) / 2;
        } else {
            state.cam.x = Math.max(-padX, Math.min(state.cam.x, cv.width_px + padX - vp.w / z));
        }
        if (boardH + 2 * PAD <= vp.h) {
            state.cam.y = (cv.height_px - vp.h / z) / 2;
        } else {
            state.cam.y = Math.max(-padY, Math.min(state.cam.y, cv.height_px + padY - vp.h / z));
        }
    }

    /**
     * Draw the label on each sheet.
     *
     * Separate from buildStage() because renaming a page has to redraw exactly
     * this and nothing else: buildStage() destroys the whole stage, so calling
     * it to refresh a caption would take every element on the board with it.
     */
    function renderSheetLabels() {
        const k = state.konva;
        if (!k) return;
        const Konva = window.Konva;
        const L = boardLayout();
        const showSeams = window.__toolSettings?.load?.(SLUG, 'show_seams', true) !== false
            && state.project.guides?.show_sheet_borders;

        k.layers.overlay.children
            .filter((n) => n.getAttr('isSheetLabel'))
            .forEach((n) => n.destroy());

        for (let r = 0; r < L.rows; r++) {
            for (let c = 0; c < L.cols; c++) {
                if (!L.pageExists(c, r)) continue;
                const rect = L.tileRectPX(c, r);
                const isPages = L.mode === 'pages';
                const lab = new Konva.Text({
                    // A named page gets its name ABOVE the paper, where it is a
                    // caption for the sheet rather than a watermark on the
                    // artwork; a tiled sheet keeps its quiet corner id.
                    x: isPages ? rect.x : rect.x + rect.w * 0.012,
                    y: isPages ? rect.y - rect.h * 0.055 : rect.y + rect.h * 0.012,
                    width: isPages ? rect.w : undefined,
                    text: sheetName(c, r),
                    fontSize: Math.round(rect.h * (isPages ? 0.036 : 0.035)),
                    fontFamily: 'Inter, sans-serif',
                    fontStyle: 'bold',
                    fill: isPages ? 'rgba(15,23,42,.42)' : 'rgba(15,23,42,.16)',
                    listening: false,
                });
                lab.setAttr('isSeam', true);        // same visibility toggle
                lab.setAttr('isSheetLabel', true);  // and its own identity
                lab.visible(showSeams);
                k.layers.overlay.add(lab);
            }
        }
        k.layers.overlay.batchDraw();
    }

    /**
     * Anything meant to be a constant number of SCREEN pixels — the paper's
     * edge and shadow, the selection handles — is specified in board units and
     * therefore has to be divided by the zoom every time the zoom changes.
     * Skipping this made the board's outline a finger thick at 400% and
     * invisible at 7%.
     */
    /** Repaint every sheet — a pages board has one paper node per page. */
    function setPaperFill(color) {
        const k = state.konva;
        if (!k) return;
        for (const r of (k.paperRects || [k.bgRect])) { if (r) r.fill(color); }
        k.layers.bg.batchDraw();
    }

    function refreshPaperChrome() {
        const k = state.konva;
        if (!k) return;
        const z = Math.max(0.01, state.zoom);
        for (const r of (k.paperRects || [])) {
            r.strokeWidth(1 / z);
            r.shadowBlur(24 / z);
            r.shadowOffsetY(8 / z);
        }
        // Seam guides and page safe-areas are hairlines by intent. Left at a
        // fixed 2 board px they were 0.18 px on screen at fit zoom — drawn,
        // invisible, and indistinguishable from "the feature is broken".
        for (const n of k.layers.overlay.children) {
            if (!n.getAttr('isSeam') || !n.strokeWidth) continue;
            if (n.getClassName() === 'Text') continue;
            n.strokeWidth(1.6 / z);
            if (n.dash && n.dash()) n.dash([14 / z, 10 / z]);
        }
        // The transformer is NOT divided by the zoom: Konva already draws its
        // border and anchors at a constant screen size, so compensating again
        // squares the correction. At fit zoom (~5%) that turned a 1.5 px border
        // into a solid blue slab covering half the board — the selection was
        // literally bigger than the artwork it was selecting.
        if (k.tr) {
            const touch = window.matchMedia?.('(hover:none) and (pointer:coarse)')?.matches;
            k.tr.anchorSize(touch ? 20 : 11);
            k.tr.borderStrokeWidth(1.5);
            k.tr.anchorStrokeWidth(1.5);
            k.tr.rotateAnchorOffset(touch ? 34 : 26);
            k.tr.padding(4);
        }
    }

    function applyCamera() {
        const stage = state.konva?.stage;
        if (!stage) return;
        const vp = viewportSize();
        clampCamera();
        stage.size({ width: vp.w, height: vp.h });
        stage.scale({ x: state.zoom, y: state.zoom });
        stage.position({ x: -state.cam.x * state.zoom, y: -state.cam.y * state.zoom });
        refreshPaperChrome();
        stage.batchDraw();
        const el = $('wnStage');
        if (el) { el.style.width = vp.w + 'px'; el.style.height = vp.h + 'px'; }
        const readout = $('wnZoomReadout');
        if (readout) readout.textContent = Math.round(state.zoom * 100) + '%';
        positionElToolbar();
        renderMiniMap();
    }

    /** Board coords → viewport-relative screen px. */
    function boardToScreen(x, y) {
        return { x: (x - state.cam.x) * state.zoom, y: (y - state.cam.y) * state.zoom };
    }

    /**
     * Set zoom, optionally keeping one screen point pinned to the same board
     * point — that is what makes wheel-zoom and pinch feel anchored instead of
     * teleporting to the top-left corner.
     */
    function setZoom(z, anchor) {
        const prev = state.zoom;
        const next = Math.max(minZoom(), Math.min(maxZoom(), z));
        if (!anchor) {
            // Keep the viewport centre fixed.
            const vp = viewportSize();
            const cxWorld = state.cam.x + vp.w / (2 * prev);
            const cyWorld = state.cam.y + vp.h / (2 * prev);
            state.zoom = next;
            state.cam.x = cxWorld - vp.w / (2 * next);
            state.cam.y = cyWorld - vp.h / (2 * next);
        } else {
            const wx = state.cam.x + anchor.x / prev;
            const wy = state.cam.y + anchor.y / prev;
            state.zoom = next;
            state.cam.x = wx - anchor.x / next;
            state.cam.y = wy - anchor.y / next;
        }
        applyCamera();
    }

    function zoomToFit() {
        const cv = state.project?.canvas;
        if (!cv || !state.konva) return;
        const vp = viewportSize();
        const PAD = viewPad();
        const z = Math.min((vp.w - PAD * 2) / cv.width_px, (vp.h - PAD * 2) / cv.height_px);
        state.zoom = Math.max(minZoom(), Math.min(maxZoom(), z));
        state.cam.x = (cv.width_px - vp.w / state.zoom) / 2;
        state.cam.y = (cv.height_px - vp.h / state.zoom) / 2;
        applyCamera();
    }

    /** Fill the viewport width with one sheet — the reading zoom for editing. */
    function zoomToSheet() {
        const L = boardLayout();
        const vp = viewportSize();
        const rect = L.tileRectPX(_pageFocus.c, _pageFocus.r);
        // A named page carries its caption ABOVE the paper, so fitting the
        // printable rect alone cropped the name off the top — the one thing
        // that tells you which page you are on.
        const head = L.mode === 'pages' ? rect.h * 0.10 : 0;
        const fitH = rect.h + head;
        const z = Math.min((vp.w - viewPad()) / rect.w, (vp.h - viewPad()) / fitH);
        state.zoom = Math.max(minZoom(), Math.min(maxZoom(), z));
        state.cam.x = rect.x + rect.w / 2 - vp.w / (2 * state.zoom);
        state.cam.y = (rect.y - head) + fitH / 2 - vp.h / (2 * state.zoom);
        applyCamera();
    }

    function panBy(dxScreen, dyScreen) {
        state.cam.x -= dxScreen / state.zoom;
        state.cam.y -= dyScreen / state.zoom;
        applyCamera();
    }

    function centreOn(bx, by) {
        const vp = viewportSize();
        state.cam.x = bx - vp.w / (2 * state.zoom);
        state.cam.y = by - vp.h / (2 * state.zoom);
        applyCamera();
    }

    /* Pan gestures. Kept out of the Konva handlers so they work no matter which
       tool is active and never fight element dragging. */
    const _pan = { active: false, id: null, sx: 0, sy: 0, cx: 0, cy: 0 };
    const _pinch = { active: false, dist: 0, zoom: 1, mx: 0, my: 0 };

    function isCoarsePointer() {
        return window.matchMedia?.('(hover:none) and (pointer:coarse)')?.matches === true;
    }

    function installViewportGestures() {
        const host = $('wnStage');
        if (!host || host.dataset.wnGestures === '1') return;
        host.dataset.wnGestures = '1';

        // Wheel: pan by default (what a page does), zoom with Ctrl/⌘ — which is
        // also the event a trackpad pinch sends, so pinch-to-zoom works on a
        // laptop for free.
        host.addEventListener('wheel', (e) => {
            if (!state.project) return;
            if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                const r = host.getBoundingClientRect();
                setZoom(state.zoom * (e.deltaY < 0 ? 1.12 : 1 / 1.12),
                    { x: e.clientX - r.left, y: e.clientY - r.top });
            } else {
                e.preventDefault();
                panBy(-e.deltaX, -e.deltaY);
            }
        }, { passive: false });

        host.addEventListener('pointerdown', (e) => {
            if (!state.project) return;
            // Middle button, space-drag and the hand tool always pan. On a
            // touch screen a finger on empty canvas pans too: a rubber-band
            // selection you cannot see the edges of is worth less than being
            // able to move around at all.
            const onEmpty = state.konva && state.konva.stage.getIntersection(
                { x: e.offsetX, y: e.offsetY }) === null;
            const wantPan = e.button === 1 || state.spaceHeld || state.tool === 'hand'
                || (e.pointerType === 'touch' && state.tool === 'select' && onEmpty);
            if (!wantPan) return;
            _pan.active = true;
            _pan.id = e.pointerId;
            _pan.sx = e.clientX; _pan.sy = e.clientY;
            _pan.cx = state.cam.x; _pan.cy = state.cam.y;
            host.setPointerCapture?.(e.pointerId);
            host.classList.add('is-panning');
            e.preventDefault();
        });

        host.addEventListener('pointermove', (e) => {
            if (!_pan.active || e.pointerId !== _pan.id) return;
            state.cam.x = _pan.cx - (e.clientX - _pan.sx) / state.zoom;
            state.cam.y = _pan.cy - (e.clientY - _pan.sy) / state.zoom;
            applyCamera();
        });

        const endPan = (e) => {
            if (!_pan.active || (e && e.pointerId !== _pan.id)) return;
            _pan.active = false; _pan.id = null;
            host.classList.remove('is-panning');
        };
        host.addEventListener('pointerup', endPan);
        host.addEventListener('pointercancel', endPan);

        // Two-finger pinch + drag.
        host.addEventListener('touchstart', (e) => {
            if (e.touches.length !== 2) return;
            const [a, b] = e.touches;
            _pinch.active = true;
            _pinch.dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) || 1;
            _pinch.zoom = state.zoom;
            const r = host.getBoundingClientRect();
            _pinch.mx = (a.clientX + b.clientX) / 2 - r.left;
            _pinch.my = (a.clientY + b.clientY) / 2 - r.top;
            endPan();
        }, { passive: true });

        host.addEventListener('touchmove', (e) => {
            if (!_pinch.active || e.touches.length !== 2) return;
            e.preventDefault();
            const [a, b] = e.touches;
            const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) || 1;
            setZoom(_pinch.zoom * (d / _pinch.dist), { x: _pinch.mx, y: _pinch.my });
        }, { passive: false });

        const endPinch = (e) => { if (!e.touches || e.touches.length < 2) _pinch.active = false; };
        host.addEventListener('touchend', endPinch);
        host.addEventListener('touchcancel', endPinch);

        // The stage must follow the panel/drawer/window changing shape.
        if (window.ResizeObserver) {
            const ro = new ResizeObserver(() => { if (state.konva) applyCamera(); });
            ro.observe($('wnCanvasScroll'));
        } else {
            window.addEventListener('resize', () => { if (state.konva) applyCamera(); });
        }
    }

    // ─── Phone chrome ─────────────────────────────────────────────────
    /**
     * Move the undo/redo and zoom clusters between the top bar and the bottom
     * bar depending on width.
     *
     * CSS alone cannot do this — the two clusters live inside the top bar and
     * flex `order` cannot move a child into a different parent. Leaving them
     * where they were is what made the phone top bar 775 px of content inside
     * a 330 px viewport: everything past the title was simply unreachable.
     */
    const PHONE_MAX = 720;

    function syncMobileChrome() {
        const dock = $('wnBottomDock');
        const topbar = document.querySelector('.wn-topbar');
        const hist = $('wnHistoryGroup');
        const zoom = document.querySelector('.wn-zoom-group');
        if (!dock || !topbar || !hist || !zoom) return;

        const phone = window.innerWidth <= PHONE_MAX;
        const spacer = topbar.querySelector('.wn-topbar-spacer');
        if (phone) {
            if (hist.parentElement !== dock) dock.appendChild(hist);
            if (zoom.parentElement !== dock) dock.appendChild(zoom);
        } else {
            if (hist.parentElement === dock) topbar.insertBefore(hist, spacer);
            if (zoom.parentElement === dock && spacer) topbar.insertBefore(zoom, spacer.nextSibling);
        }
        document.body.classList.toggle('wn-phone', phone);
        if (state.konva) applyCamera();
    }

    // ─── Clipboard + context menu ─────────────────────────────────────
    /* An internal clipboard, not the system one. The system clipboard carries
       images and text but has no way to hold "this photo with this crop, this
       border and this rotation", which is exactly what someone copying a
       framed pupil photo across six sheets wants. */
    let _clipboard = null;

    function copySelection() {
        const nodes = selectedNodes();
        if (!nodes.length) return;
        _clipboard = nodes.map((n) => serializeNode(n));
        toast(t('copied', 'Copied'), 'success');
        ae('copy', { n: nodes.length });
    }

    function pasteClipboard() {
        if (!_clipboard || !_clipboard.length) return;
        const made = [];
        // Offset so the copy is visibly a copy rather than hidden underneath
        // the original — 6 mm of paper, which is a finger's width on screen at
        // any sane zoom.
        const off = Math.round(6 * PX_PER_MM);
        for (const el of _clipboard) {
            const clone = Object.assign({}, el, {
                elId: ulid(), x: (el.x || 0) + off, y: (el.y || 0) + off,
            });
            const before = state.konva.layers.content.children.length;
            deserializeNode(clone);
            const added = state.konva.layers.content.children.slice(before);
            made.push(...added.filter((n) => n.hasName?.('wn-obj')));
        }
        if (!made.length) return;
        state.konva.tr.nodes(made);
        state.konva.layers.content.batchDraw();
        renderProperties();
        renderLayers();
        markDirty();
        pushHistory();
        ae('paste', { n: made.length });
    }

    /**
     * Right-click menu.
     *
     * Every desktop editor has one, and without it the only route to
     * duplicate / bring-to-front / lock was a trip to the right-hand panel —
     * on the far side of the screen from the element being edited.
     */
    function installContextMenu() {
        const host = $('wnStage');
        const menu = $('wnCtxMenu');
        if (!host || !menu || host.dataset.wnCtx === '1') return;
        host.dataset.wnCtx = '1';

        const hide = () => { menu.hidden = true; };
        const show = (clientX, clientY) => {
            const nodes = selectedNodes();
            const has = nodes.length > 0;
            menu.querySelectorAll('[data-need-sel]').forEach((b) => { b.disabled = !has; });
            const pasteBtn = menu.querySelector('[data-cmd="paste"]');
            if (pasteBtn) pasteBtn.disabled = !(_clipboard && _clipboard.length);
            const lockBtn = menu.querySelector('[data-cmd="lock"]');
            if (lockBtn && has) {
                const allLocked = nodes.every((n) => n.getAttr('elLocked') === true);
                lockBtn.textContent = allLocked ? t('prop_unlock', 'Unlock') : t('prop_lock', 'Lock in place');
            }
            menu.hidden = false;
            // Measure after showing, then keep it on screen.
            const r = menu.getBoundingClientRect();
            const x = Math.min(clientX, window.innerWidth - r.width - 8);
            const y = Math.min(clientY, window.innerHeight - r.height - 8);
            menu.style.left = Math.max(8, x) + 'px';
            menu.style.top = Math.max(8, y) + 'px';
        };

        host.addEventListener('contextmenu', (e) => {
            if (!state.project) return;
            e.preventDefault();
            // Right-clicking an unselected element should act on THAT element,
            // which is what every other editor does and what the user means.
            const hit = state.konva?.stage.getIntersection({ x: e.offsetX, y: e.offsetY });
            if (hit) {
                let n = hit;
                while (n && n.parent && !n.hasName?.('wn-obj')) n = n.parent;
                if (n?.hasName?.('wn-obj') && !selectedNodes().includes(n)) {
                    state.konva.tr.nodes([n]);
                    state.konva.layers.content.batchDraw();
                    renderProperties();
                    renderLayers();
                }
            }
            show(e.clientX, e.clientY);
        });

        menu.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-cmd]');
            if (!btn || btn.disabled) return;
            const nodes = selectedNodes();
            switch (btn.dataset.cmd) {
                case 'copy':      copySelection(); break;
                case 'paste':     pasteClipboard(); break;
                case 'duplicate': if (nodes.length) duplicateNodes(nodes); break;
                case 'front':     nodes.forEach((n) => n.moveToTop()); break;
                case 'back':      nodes.forEach((n) => n.moveToBottom()); break;
                case 'lock': {
                    const allLocked = nodes.length && nodes.every((n) => n.getAttr('elLocked') === true);
                    nodes.forEach((n) => setNodeLocked(n, !allLocked));
                    renderLayers();
                    renderProperties();
                    break;
                }
                case 'delete':    if (nodes.length) deleteNodes(nodes); break;
                default: break;
            }
            if (['front', 'back'].includes(btn.dataset.cmd)) {
                state.konva.layers.content.batchDraw();
                renderLayers();
                markDirty();
                pushHistory();
            }
            hide();
        });

        document.addEventListener('click', hide);
        document.addEventListener('scroll', hide, true);
        window.addEventListener('resize', hide);
        window.addEventListener('keydown', (e) => { if (e.key === 'Escape') hide(); });
    }

    // ─── Zoom menu / focus mode ───────────────────────────────────────
    function wireZoomUI() {
        const readout = $('wnZoomReadout');
        const menu = $('wnZoomMenu');
        if (!readout || !menu) return;

        const close = () => { menu.hidden = true; readout.setAttribute('aria-expanded', 'false'); };
        readout.onclick = (e) => {
            e.stopPropagation();
            const open = menu.hidden;
            menu.hidden = !open;
            readout.setAttribute('aria-expanded', open ? 'true' : 'false');
        };
        document.addEventListener('click', close);
        menu.querySelectorAll('[data-zoom]').forEach((b) => {
            b.onclick = (e) => {
                e.stopPropagation();
                const v = b.dataset.zoom;
                if (v === 'fit') zoomToFit();
                else if (v === 'sheet') zoomToSheet();
                else setZoom(parseFloat(v));
                close();
            };
        });

        const focusBtn = $('wnFocusMode');
        if (focusBtn) {
            focusBtn.onclick = () => toggleFocusMode();
            document.addEventListener('fullscreenchange', () => {
                const on = !!document.fullscreenElement;
                focusBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
                document.body.classList.toggle('wn-focus-mode', on);
                // The viewport changed size by a lot; re-fit rather than leave
                // the camera pointing at a corner of a much larger screen.
                requestAnimationFrame(() => requestAnimationFrame(() => {
                    if (state.konva) { applyCamera(); zoomToFit(); }
                }));
            });
        }
    }

    async function toggleFocusMode() {
        const el = $('wnWorkspace');
        if (!el) return;
        try {
            if (document.fullscreenElement) await document.exitFullscreen();
            else if (el.requestFullscreen) await el.requestFullscreen({ navigationUI: 'hide' });
            else {
                // iOS Safari has no Element.requestFullscreen. Fall back to a
                // CSS takeover so the phone still gets a full-screen canvas —
                // this is the platform where the strip is worst.
                document.body.classList.toggle('wn-focus-fallback');
                $('wnFocusMode')?.setAttribute('aria-pressed',
                    document.body.classList.contains('wn-focus-fallback') ? 'true' : 'false');
                requestAnimationFrame(() => requestAnimationFrame(() => {
                    if (state.konva) { applyCamera(); zoomToFit(); }
                }));
            }
            ae('focus_mode', {});
        } catch (e) { /* user or browser refused — nothing to recover */ }
    }

    /** Space held = temporary hand tool, the convention in every canvas app. */
    function installSpacePan() {
        window.addEventListener('keydown', (e) => {
            if (e.code !== 'Space' || state.spaceHeld) return;
            const el = document.activeElement;
            if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
            if (!state.project || $('wnWorkspace').hidden) return;
            state.spaceHeld = true;
            document.body.classList.add('wn-space-pan');
            e.preventDefault();
        });
        window.addEventListener('keyup', (e) => {
            if (e.code !== 'Space') return;
            state.spaceHeld = false;
            document.body.classList.remove('wn-space-pan');
        });
        window.addEventListener('blur', () => {
            state.spaceHeld = false;
            document.body.classList.remove('wn-space-pan');
        });
    }

    // ─── Tool selection ───────────────────────────────────────────────
    function selectTool(name) {
        // Cancel any in-progress drawing operation
        cancelDrawing();
        cancelColorPick();
        // Hide inline text editor if open
        finishTextEdit(true);

        state.tool = name;
        $$('.wn-tool').forEach((b) => {
            const active = b.dataset.tool === name;
            b.classList.toggle('wn-tool-active', active);
            b.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        ae('tool_select', { tool: name });

        // Set cursor hint on canvas area
        const area = $('wnCanvasArea');
        area.style.cursor =
            name === 'select'    ? 'default' :
            name === 'text'      ? 'text' :
            name === 'image'     ? 'copy' :
            name === 'draw'      ? 'crosshair' :
            name === 'shape'     ? 'crosshair' :
            name === 'sticker'   ? 'pointer' :
            name === 'hand'      ? 'grab' :
            name === 'background'? 'pointer' : 'default';

        // Tool-specific UI
        if (name === 'shape') openShapePicker();
        else if (name === 'blocks') openBlockPicker();
        else if (name === 'sticker') openStickerPicker();
        else if (name === 'background') openBackgroundPicker();
        else if (name === 'image') $('wnImageInput').click();
        else closeSubPanel();

        // The transformer must not keep grabbing pointers while the hand tool
        // is out, or "pan" would drag whatever happens to be selected.
        if (state.konva?.tr) state.konva.tr.listening(name !== 'hand');

        // Clear selection when switching to non-select tools
        if (name !== 'select') clearSelection();
    }

    // ─── Sub-panel (shape picker / background / sticker) ──────────────
    const SHAPES = [
        { key: 'rect',    svg: '<rect x="3" y="3" width="14" height="14" rx="2"/>' },
        { key: 'ellipse', svg: '<ellipse cx="10" cy="10" rx="7" ry="7"/>' },
        { key: 'star',    svg: '<polygon points="10,2 12,7 17,7 13,11 14,17 10,14 6,17 7,11 3,7 8,7"/>' },
        { key: 'heart',   svg: '<path d="M10 17s-6-3.5-6-8a3.5 3.5 0 016-2 3.5 3.5 0 016 2c0 4.5-6 8-6 8z"/>' },
        { key: 'arrow',   svg: '<path d="M3 10h12M11 6l4 4-4 4" stroke-linecap="round" stroke-linejoin="round" fill="none"/>' },
        { key: 'line',    svg: '<line x1="3" y1="10" x2="17" y2="10" stroke-linecap="round"/>' },
    ];

    function closeSubPanel() {
        const panel = $('wnSubPanel');
        panel.hidden = true;
        panel.innerHTML = '';
    }

    function openShapePicker() {
        const panel = $('wnSubPanel');
        panel.hidden = false;
        panel.innerHTML = `
            <div class="wn-sub-header">
                <span>${t('shape_picker_title', 'Shape')}</span>
                <button type="button" class="wn-sub-close" aria-label="${t('btn_close', 'Close')}">×</button>
            </div>
            <div class="wn-shape-grid">
                ${SHAPES.map((s) =>
                    `<button type="button" class="wn-shape-btn" data-shape="${s.key}" title="${t('shape_' + s.key, s.key)}">
                        <svg viewBox="0 0 20 20" width="28" height="28" fill="currentColor" stroke="currentColor" stroke-width="1.6">${s.svg}</svg>
                    </button>`
                ).join('')}
            </div>
            <div class="wn-bg-section" style="margin-top:14px">
                <div class="wn-bg-label">${t('prop_fill', 'Fill')}</div>
                <div class="wn-color-swatches" id="wnShapeColors"></div>
            </div>
        `;
        panel.querySelector('.wn-sub-close').onclick = () => { selectTool('select'); };
        $$('[data-shape]', panel).forEach((b) => {
            b.onclick = () => {
                state.drawing = { kind: 'shape-pending', shape: b.dataset.shape };
                $$('[data-shape].is-active', panel).forEach((x) => x.classList.remove('is-active'));
                b.classList.add('is-active');
            };
        });
        // Default selection: rect
        panel.querySelector('[data-shape="rect"]').classList.add('is-active');
        state.drawing = { kind: 'shape-pending', shape: 'rect' };
        renderColorSwatches('wnShapeColors', state.lastFill, (c) => { state.lastFill = c; });
    }

    // ─── Ready-made blocks ────────────────────────────────────────────
    /* A wall newspaper is not a free canvas in practice — it is a set of
       recurring rubrics: a masthead, a framed article, the class list, the
       lesson timetable, the duty rota, "поздравляем именинников". Building any
       of those out of loose rectangles and text boxes is twenty minutes of
       fiddly alignment, every time, for something that is the same shape every
       time. These drop in already assembled and already aligned.
     *
     * Each block is plain elements, not a special node type: the moment it
     * lands it is text and rectangles the user can edit, recolour or pull
     * apart. Nothing about it is locked to a template format. */

    const BLOCK_KEYS = ['masthead', 'article', 'photo_caption', 'roster', 'list', 'timetable', 'birthdays'];

    /* How many pupils the roster block lays out. Real classes are 25–30, and a
       fixed six-slot grid is the single most useless thing a class-list
       template can be — so the count is the block's own control. */
    let _rosterCount = 24;

    /* Cascade counter for consecutive inserts; reset whenever the board
       changes so a fresh project starts at the top-left again. */
    let _blockCascade = 0;

    function blockPalette() {
        return {
            ink: '#1f2a44',
            soft: '#5b6784',
            line: '#c9d4ea',
            panel: '#f4f7fd',
            accent: state.lastFill || '#3b82f6',
        };
    }

    /**
     * Shrink a heading until it fits on one line.
     *
     * A heading is set to a size that suits the layout, not to a size that
     * suits the words — and the words are translated. "Расписание уроков" in a
     * bar sized for "Class list" wraps, and the second line lands BELOW the
     * coloured bar in white-on-white: the page silently loses half its title.
     * The same wrap inside an article block pushes the heading down over the
     * body text.
     *
     * Shrinking has a floor, because a heading small enough to fit anything is
     * no longer a heading; past the floor it is allowed to wrap, which at
     * least keeps the words.
     */
    function fitHeading(node, maxW, minSize) {
        if (!node || !node.measureSize) return;
        const start = node.fontSize();
        const floor = Math.max(6, minSize || start * 0.5);
        let size = start;
        for (let i = 0; i < 40 && size > floor; i++) {
            if (node.measureSize(node.text()).width <= maxW) break;
            size = Math.max(floor, size * 0.93);
            node.fontSize(size);
        }
    }

    function mkText(o) {
        const node = new window.Konva.Text(Object.assign({
            fontFamily: 'Inter', fill: blockPalette().ink, padding: 4, lineHeight: 1.25,
        }, o));
        tagNode(node, { type: 'text' });
        node.on('dblclick dbltap', () => startTextEdit(node));
        attachNodeInteractions(node);
        state.konva.layers.content.add(node);
        return node;
    }

    function mkRect(o) {
        const node = makeShape('rect', { x: o.x, y: o.y, w: o.w, h: o.h, fill: o.fill || '#ffffff' });
        node.cornerRadius(o.radius != null ? o.radius : Math.round(Math.min(o.w, o.h) * 0.04));
        if (o.stroke) { node.stroke(o.stroke); node.strokeWidth(o.strokeWidth || 6); }
        else node.strokeWidth(0);
        tagNode(node, { type: 'shape', shape: 'rect' });
        attachNodeInteractions(node);
        state.konva.layers.content.add(node);
        return node;
    }

    /**
     * Insert a block, sized against the board and dropped where there is room.
     *
     * Blocks are proportional to the sheet, not to the whole board: a masthead
     * on a 4×3 wall and on a single A4 should both read as a masthead, and a
     * fixed pixel size would make one of them absurd.
     */
    function insertBlock(key) {
        /* One undo step for the whole block.
         *
         * The parts are built with the same helpers a user's own actions use,
         * and each of those pushes history — so a class roster of 38 pupils
         * left 39 snapshots on the stack, each a full serialisation of the
         * board. Undo then needed 39 presses to take back one click, and the
         * 60-entry history cap threw away everything that came before. */
        const wasBusy = state.history.busy;
        state.history.busy = true;
        try {
            insertBlockInner(key);
        } finally {
            state.history.busy = wasBusy;
        }
        markDirty();
        pushHistory();
    }

    /**
     * @param {string} key
     * @param {{at?:{c:number,r:number}, top?:number, noTitle?:boolean, silent?:boolean}} [opts]
     *        `at` builds on a named sheet instead of the one in view, `top`
     *        starts the block that many sheet-heights down (the corner starter
     *        puts a rubric bar above it), `noTitle` drops the block's own
     *        heading when something above it already names the page, `silent`
     *        skips selection and the per-block telemetry — twelve pages at
     *        once are one action.
     * @returns {import('konva').Node[]} the nodes that were created
     */
    function insertBlockInner(key, opts) {
        const o = opts || {};
        const L = boardLayout();
        const cv = state.project.canvas;
        const sheetW = L.usable_w_mm * L.px_per_mm;
        const sheetH = L.usable_h_mm * L.px_per_mm;
        const U = sheetH / 100;                       // one "unit" ≈ 1% of a sheet's height
        const p = blockPalette();
        const made = [];

        // Place near the top-left of the sheet currently in view, so a block
        // lands where the user is looking rather than always in the middle —
        // and cascade successive blocks, because dropping three in a row on the
        // same coordinates buries the first two under the third.
        // A seeded corner page already carries the rubric name on its bar, so
        // the block's own title would be a second — and, for "Расписание
        // звонков", a WRONG — heading right under it.
        const showTitle = o.noTitle !== true;
        // A block dropped onto a shared sheet is a card among others; the same
        // block alone on a corner page is the page. Scaling a card UP after
        // the fact only magnifies its proportions — a two-word column set at
        // 40% of the sheet still breaks "рождения" across two lines, just
        // bigger. Built wide from the start, it wraps where it should.
        const wide = o.wide === true;
        const place = o.at || _pageFocus;
        const rect = L.tileRectPX(place.c, place.r);
        const step = Math.round(10 * PX_PER_MM);
        const n = o.at ? 0 : (_blockCascade++ % 6);
        const ox = rect.x + sheetW * 0.08 + n * step;
        const oy = rect.y + (o.top != null ? o.top : sheetH * 0.10) + n * step;

        if (key === 'masthead') {
            const w = sheetW * 0.84, h = U * 26;
            made.push(mkRect({ x: ox, y: oy, w, h, fill: p.accent, radius: U * 2 }));
            const mast = mkText({
                x: ox, y: oy + h * 0.16, width: w, align: 'center',
                text: t('block_masthead_title', 'CLASS NEWS'),
                fontSize: U * 12, fontStyle: 'bold', fill: '#ffffff',
            });
            // White on a coloured bar: a wrapped second line falls off the bar
            // onto white paper and disappears.
            fitHeading(mast, w * 0.94, U * 6);
            made.push(mast);
            made.push(mkText({
                x: ox, y: oy + h * 0.62, width: w, align: 'center',
                text: t('block_masthead_sub', 'issue of  ·  our class'),
                fontSize: U * 5, fill: 'rgba(255,255,255,.85)',
            }));
        } else if (key === 'article') {
            const w = sheetW * (wide ? 0.84 : 0.44), h = sheetH * (wide ? 0.6 : 0.42);
            made.push(mkRect({ x: ox, y: oy, w, h, fill: p.panel, stroke: p.line, strokeWidth: U * 0.6 }));
            const bodyTop = showTitle ? U * 14 : U * 4;
            if (showTitle) {
                const head = mkText({
                    x: ox + U * 3, y: oy + U * 3, width: w - U * 6,
                    text: t('block_article_title', 'Heading'),
                    fontSize: U * 8, fontStyle: 'bold', fill: p.ink,
                });
                fitHeading(head, w - U * 6, U * 4.6);
                made.push(head);
            }
            made.push(mkText({
                x: ox + U * 3, y: oy + bodyTop, width: w - U * 6, height: h - bodyTop - U * 4,
                text: t('block_article_body', 'Write what happened here.'),
                fontSize: U * 4.4, fill: p.soft,
            }));
        } else if (key === 'photo_caption') {
            const w = sheetW * 0.36, h = w * 0.72;
            made.push(addFrameElement(ox, oy, w, h));
            made.push(mkText({
                x: ox, y: oy + h + U * 1.5, width: w, align: 'center',
                text: t('block_caption', 'Who is in the photo'),
                fontSize: U * 4.2, fill: p.soft,
            }));
        } else if (key === 'roster') {
            /* A grid of photo slots with a name under each — the "фото +
               фамилия" wall every class list actually is.
             *
             * The column count is SOLVED, not chosen: a class of 28 laid out
             * on a fixed five-wide grid runs off the bottom of the sheet, and
             * a block that does not fit the page it was dropped on is worse
             * than no block. For every candidate width the cell is limited by
             * both the available width and the remaining height, and the
             * arrangement that yields the largest cell wins — so the same
             * block works for six pupils and for thirty-eight. */
            const n = Math.max(1, Math.min(40, _rosterCount));
            const gap = U * 2.2;
            const titleH = showTitle ? U * 11 : 0;
            const nameH = U * 5.2;
            const PHOTO_RATIO = 1.18;                 // portrait head-and-shoulders
            const availW = sheetW * (wide ? 0.9 : 0.88);
            // The solver needs the room it ACTUALLY has. Told the whole sheet
            // and then shrunk to fit underneath the rubric bar, it picked a
            // five-wide grid and left a hand's width of paper unused down the
            // right-hand side — the cells are limited by height, so guessing
            // the height wrong wastes the width.
            const availH = (wide ? (rect.h - (o.top || 0)) * 0.94 : sheetH * 0.86) - titleH;

            let best = null;
            for (let cols = 1; cols <= Math.min(8, n); cols++) {
                const rowsN = Math.ceil(n / cols);
                const byW = (availW - gap * (cols - 1)) / cols;
                const cellH = (availH - gap * (rowsN - 1)) / rowsN;
                const byH = (cellH - nameH) / PHOTO_RATIO;
                const cw = Math.min(byW, byH);
                if (cw <= 0) continue;
                if (!best || cw > best.cw) best = { cols, rowsN, cw };
            }
            if (best) {
                const { cols, cw } = best;
                const photoH = cw * PHOTO_RATIO;
                const ch = photoH + nameH;
                const gridW = cols * cw + gap * (cols - 1);
                const left = ox + (availW - gridW) / 2;

                if (showTitle) {
                    made.push(mkText({
                        x: ox, y: oy, width: availW, align: 'center',
                        text: t('block_roster_title', 'Our class'),
                        fontSize: Math.min(U * 7.5, titleH * 0.62), fontStyle: 'bold', fill: p.ink,
                    }));
                }
                for (let i = 0; i < n; i++) {
                    const cx = left + (i % cols) * (cw + gap);
                    const cy = oy + titleH + Math.floor(i / cols) * (ch + gap);
                    made.push(addFrameElement(cx, cy, cw, photoH));
                    made.push(mkText({
                        x: cx, y: cy + photoH + U * 0.6, width: cw, align: 'center',
                        text: t('block_roster_name', 'Name'),
                        fontSize: Math.min(U * 3.4, nameH * 0.62), fill: p.soft,
                    }));
                }
            }
        } else if (key === 'list') {
            const w = sheetW * (wide ? 0.6 : 0.34), rows = wide ? 14 : 10;
            if (showTitle) {
                const head = mkText({
                    x: ox, y: oy, width: w,
                    text: t('block_list_title', 'Our class'),
                    fontSize: U * 7.5, fontStyle: 'bold', fill: p.ink,
                });
                fitHeading(head, w, U * 4.4);
                made.push(head);
            }
            let lines = '';
            for (let i = 1; i <= rows; i++) lines += i + '. \n';
            made.push(mkText({
                x: ox, y: oy + (showTitle ? U * 11 : 0), width: w,
                text: lines.trimEnd(),
                fontSize: U * 4.6, fill: p.soft, lineHeight: 1.65,
            }));
        } else if (key === 'timetable') {
            // A real timetable, drawn as a grid: five weekdays, seven periods.
            const cols = 6, rowsN = 8;
            const w = sheetW * (wide ? 0.88 : 0.8), h = sheetH * (wide ? 0.62 : 0.5);
            const cw = w / cols, ch = h / rowsN;
            made.push(mkRect({ x: ox, y: oy, w, h, fill: '#ffffff', stroke: p.line, strokeWidth: U * 0.6 }));
            for (let c = 1; c < cols; c++) {
                const line = new window.Konva.Line({
                    points: [ox + c * cw, oy, ox + c * cw, oy + h],
                    stroke: p.line, strokeWidth: U * 0.4,
                });
                tagNode(line, { type: 'drawing' });
                attachNodeInteractions(line);
                state.konva.layers.content.add(line);
                made.push(line);
            }
            for (let r = 1; r < rowsN; r++) {
                const line = new window.Konva.Line({
                    points: [ox, oy + r * ch, ox + w, oy + r * ch],
                    stroke: p.line, strokeWidth: U * 0.4,
                });
                tagNode(line, { type: 'drawing' });
                attachNodeInteractions(line);
                state.konva.layers.content.add(line);
                made.push(line);
            }
            const days = (t('block_days', 'Mon,Tue,Wed,Thu,Fri') || '').split(',');
            for (let c = 1; c < cols; c++) {
                made.push(mkText({
                    x: ox + c * cw, y: oy + ch * 0.28, width: cw, align: 'center',
                    text: (days[c - 1] || '').trim(),
                    fontSize: U * 4, fontStyle: 'bold', fill: p.ink,
                }));
            }
            for (let r = 1; r < rowsN; r++) {
                made.push(mkText({
                    x: ox, y: oy + r * ch + ch * 0.28, width: cw, align: 'center',
                    text: String(r), fontSize: U * 4, fill: p.soft,
                }));
            }
        } else if (key === 'birthdays') {
            const w = sheetW * (wide ? 0.84 : 0.38), h = sheetH * (wide ? 0.5 : 0.3);
            made.push(mkRect({ x: ox, y: oy, w, h, fill: '#fff6e5', stroke: '#f5c04a', strokeWidth: U * 0.7 }));
            if (showTitle) {
                const head = mkText({
                    x: ox, y: oy + U * 3, width: w, align: 'center',
                    text: t('block_birthdays_title', 'Happy birthday!'),
                    fontSize: U * 7, fontStyle: 'bold', fill: '#a15c00',
                });
                fitHeading(head, w - U * 4, U * 4.2);
                made.push(head);
            }
            made.push(mkText({
                x: ox + U * 4, y: oy + (showTitle ? U * 14 : U * 5), width: w - U * 8,
                text: t('block_birthdays_body', 'Names and dates'),
                fontSize: U * 4.4, fill: '#7a5417', lineHeight: 1.6,
            }));
        }

        const nodes = made.filter(Boolean);
        if (!nodes.length) return nodes;
        fitBlockToSheet(nodes, place, o.top);
        if (o.silent) return nodes;
        state.konva.tr.nodes(nodes);
        state.konva.layers.content.batchDraw();
        renderProperties();
        renderLayers();
        ae('block_add', { key });
        // No toast. The block arrives selected, the layers panel grows and the
        // properties panel switches to it — adding six in a row used to stack
        // six banners over the canvas the user was trying to look at.
        return nodes;
    }


    /**
     * Keep a freshly-dropped block inside the sheet it was dropped on.
     *
     * Two things push a block out: the cascade that stops consecutive blocks
     * burying each other, and a block whose own content is bigger than the
     * page (a class of 38 needs more room than a class of 6). Both end the
     * same way — part of the block is off the paper and simply not printed —
     * so the block is moved back in, and only scaled down if it genuinely
     * cannot fit.
     */
    function fitBlockToSheet(nodes, at, top) {
        if (!nodes.length || !state.konva) return;
        const L = boardLayout();
        const place = at || _pageFocus;
        const target = L.tileRectPX(place.c, place.r);
        // A rubric bar already occupies the top of a corner page. Without this
        // the block is pushed back up under it and the two overlap — "fits on
        // the sheet" is not the same as "fits in the room that is left".
        if (top != null) { target.y += top; target.h -= top; }
        const layer = state.konva.layers.content;

        const bbox = () => {
            let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
            for (const n of nodes) {
                const r = n.getClientRect({ relativeTo: layer, skipShadow: true });
                x1 = Math.min(x1, r.x); y1 = Math.min(y1, r.y);
                x2 = Math.max(x2, r.x + r.width); y2 = Math.max(y2, r.y + r.height);
            }
            return { x1, y1, x2, y2, w: x2 - x1, h: y2 - y1 };
        };

        let b = bbox();
        if (!Number.isFinite(b.x1)) return;

        const k = Math.min(1, target.w / Math.max(1, b.w), target.h / Math.max(1, b.h));
        if (k < 1) {
            const cx = (b.x1 + b.x2) / 2, cy = (b.y1 + b.y2) / 2;
            for (const n of nodes) {
                n.x(cx + (n.x() - cx) * k);
                n.y(cy + (n.y() - cy) * k);
                n.scaleX(n.scaleX() * k);
                n.scaleY(n.scaleY() * k);
            }
            b = bbox();
        }

        let dx = 0, dy = 0;
        if (b.x1 < target.x) dx = target.x - b.x1;
        else if (b.x2 > target.x + target.w) dx = (target.x + target.w) - b.x2;
        if (b.y1 < target.y) dy = target.y - b.y1;
        else if (b.y2 > target.y + target.h) dy = (target.y + target.h) - b.y2;
        if (dx || dy) for (const n of nodes) { n.x(n.x() + dx); n.y(n.y() + dy); }
    }


    // ─── Classroom-corner starter ─────────────────────────────────────
    /**
     * What goes on each page of a классный уголок, by position.
     *
     * The page NAMES are translated, so the mapping is by index, not by
     * string — position 2 is the timetable in every locale. The order matches
     * `corner_default_pages`, and the two must be edited together.
     */
    const CORNER_PAGE_BLOCKS = [
        'roster',      // Список класса
        'timetable',   // Расписание уроков
        'list',        // Расписание звонков
        'timetable',   // График дежурства
        'birthdays',   // Поздравляем!
        'article',     // Наши достижения
        'list',        // Актив класса
        'article',     // Объявления
        'quote',       // Наш девиз
        'list',        // Кружки и секции
        'article',     // Для вас, родители
        'article',     // Это интересно
    ];

    /**
     * A colour per page, so a pocket is findable across the room.
     *
     * Muted on purpose: these bars are printed on a home printer and then
     * looked at every day for a term, and saturated ink both costs more and
     * reads as a warning sign rather than a heading.
     */
    const CORNER_PAGE_COLORS = ['#3f6fb5', '#3f9d7a', '#d8912f', '#c05a5a', '#7a63b8', '#2f8fa8'];

    /**
     * Scale a freshly-built block UP to use the page it now owns.
     *
     * `fitBlockToSheet` only ever shrinks — it exists to rescue a block that
     * overflows. Here the opposite happens: a card that shares a sheet with
     * five other blocks looks abandoned when it is the only thing on an A4
     * page. The block is already BUILT wide for a corner page (see `wide`), so
     * this is the last nudge rather than the rescue, and the cap is low: past
     * about a fifth, magnifying a layout stops making it more readable and
     * starts making it look like a mistake.
     */
    function growToPage(nodes, target) {
        if (!nodes.length) return;
        const layer = state.konva.layers.content;
        const bbox = () => {
            let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
            for (const n of nodes) {
                const r = n.getClientRect({ relativeTo: layer, skipShadow: true });
                x1 = Math.min(x1, r.x); y1 = Math.min(y1, r.y);
                x2 = Math.max(x2, r.x + r.width); y2 = Math.max(y2, r.y + r.height);
            }
            return { x1, y1, x2, y2, w: x2 - x1, h: y2 - y1 };
        };
        let b = bbox();
        if (!Number.isFinite(b.x1) || b.w <= 0 || b.h <= 0) return;

        const k = Math.min(1.2, target.w / b.w, target.h / b.h);
        if (k > 1.02) {
            const cx = b.x1, cy = b.y1;
            for (const n of nodes) {
                n.x(cx + (n.x() - cx) * k);
                n.y(cy + (n.y() - cy) * k);
                n.scaleX(n.scaleX() * k);
                n.scaleY(n.scaleY() * k);
            }
            b = bbox();
        }
        // Centre what is left over horizontally; leave it at the top vertically,
        // because a heading that floats in the middle of a page reads as a
        // mistake while free space at the bottom reads as room to write.
        const dx = target.x + (target.w - b.w) / 2 - b.x1;
        if (dx) for (const n of nodes) n.x(n.x() + dx);
    }

    /** Build one corner page: its rubric bar, then the block that belongs under it. */
    function seedCornerPage(c, r, index) {
        const L = boardLayout();
        const rect = L.tileRectPX(c, r);
        const sheetW = L.usable_w_mm * L.px_per_mm;
        const sheetH = L.usable_h_mm * L.px_per_mm;
        const U = sheetH / 100;
        const color = CORNER_PAGE_COLORS[index % CORNER_PAGE_COLORS.length];
        const barTop = sheetH * 0.05;
        const barH = U * 12;
        const barX = rect.x + sheetW * 0.08;
        const barW = sheetW * 0.84;

        // The rubric bar carries the page's own name — the same name that is
        // in the page map and on the printed sheet, so the printout and the
        // navigation cannot disagree.
        mkRect({ x: barX, y: rect.y + barTop, w: barW, h: barH, fill: color, radius: U * 1.8 });
        const head = mkText({
            x: barX, y: rect.y + barTop, width: barW, align: 'center',
            text: pageTitle(index),
            fontSize: U * 6.4, fontStyle: 'bold', fill: '#ffffff',
        });
        fitHeading(head, barW * 0.9, U * 3.6);
        head.y(rect.y + barTop + Math.max(0, (barH - head.height()) / 2));
        // Remember which page this heading speaks for. Renaming the page in
        // the map then renames the bar too — otherwise the printed sheet says
        // "График дежурства" while the map says "Дежурство ноябрь", and the
        // whole point of naming pages is that the two agree.
        head.setAttr('elCornerTitle', index);

        const bodyTop = barTop + barH + U * 5;
        const kind = CORNER_PAGE_BLOCKS[index] || 'article';
        const target = { x: rect.x, y: rect.y + bodyTop, w: rect.w, h: rect.h - bodyTop };

        if (kind === 'quote') {
            // A motto is one sentence set large — it has no block of its own
            // because it is only ever wanted here.
            const q = mkText({
                x: rect.x + sheetW * 0.1, y: rect.y + bodyTop + sheetH * 0.12,
                width: sheetW * 0.8, align: 'center',
                text: t('block_motto_body', 'Write your class motto here'),
                fontSize: U * 9, fontStyle: 'italic', fill: blockPalette().ink, lineHeight: 1.4,
            });
            return [q];
        }

        const nodes = insertBlockInner(kind, {
            at: { c, r }, top: bodyTop, silent: true, noTitle: true, wide: true,
        }) || [];
        growToPage(nodes, target);
        return nodes;
    }

    /**
     * Lay a rubric out on every page of a corner board.
     *
     * Without this the mode delivered six correctly-sized blank sheets with
     * names — which is exactly the state a teacher is trying to get OUT of.
     * Every part stays a normal element: the bar can be recoloured, the title
     * retyped, the whole block deleted. The starter is a starting point, not a
     * fixed template.
     */
    function seedCornerPages() {
        if (!isPagesMode() || !state.konva) return 0;
        const L = boardLayout();
        let pages = 0;
        // One undo step for the whole starter — the same reason a single block
        // is one step. Twelve pages built with the ordinary helpers would
        // otherwise leave well over a hundred snapshots on the stack.
        const wasBusy = state.history.busy;
        state.history.busy = true;
        try {
            for (let r = 0; r < L.rows; r++) {
                for (let c = 0; c < L.cols; c++) {
                    if (!L.pageExists(c, r)) continue;
                    seedCornerPage(c, r, L.pageIndex(c, r));
                    pages++;
                }
            }
        } finally {
            state.history.busy = wasBusy;
        }
        state.konva.tr.nodes([]);
        state.konva.layers.content.batchDraw();
        renderLayers();
        renderProperties();
        ae('corner_seed', { pages });
        return pages;
    }

    function openBlockPicker() {
        const panel = $('wnSubPanel');
        panel.hidden = false;
        panel.innerHTML = `
            <div class="wn-sub-header">
                <span>${escapeHtml(t('tool_blocks', 'Blocks'))}</span>
                <button type="button" class="wn-sub-close" id="wnSubClose" aria-label="${escapeHtml(t('btn_close', 'Close'))}">×</button>
            </div>
            <div class="wn-prop-hint">${escapeHtml(t('blocks_hint', 'Ready-made pieces of a wall newspaper. Drop one in and edit the words.'))}</div>
            <div class="wn-block-list">
                ${BLOCK_KEYS.map((k) => `
                    <button type="button" class="wn-block-card" data-block="${k}">
                        <span class="wn-block-art wn-block-art-${k}" aria-hidden="true"></span>
                        <span class="wn-block-name">${escapeHtml(t('block_' + k, k))}</span>
                    </button>`).join('')}
            </div>
            <div class="wn-block-opt">
                <label for="wnRosterCount">${escapeHtml(t('block_roster_count', 'Pupils in the class'))}</label>
                <input type="number" id="wnRosterCount" min="1" max="40" value="${_rosterCount}">
            </div>
        `;
        $('wnSubClose').onclick = () => { closeSubPanel(); selectTool('select'); };
        const countIn = $('wnRosterCount');
        if (countIn) countIn.onchange = () => {
            const v = parseInt(countIn.value, 10);
            if (Number.isFinite(v)) _rosterCount = Math.max(1, Math.min(40, v));
            countIn.value = String(_rosterCount);
        };
        $$('[data-block]', panel).forEach((b) => {
            b.onclick = () => insertBlock(b.dataset.block);
        });
    }

    // ─── Sticker picker (uses window.WNStickers) ──────────────────────
    let _stickerCat = 'shapes';
    let _stickerColor = '#3b82f6';

    function openStickerPicker() {
        const panel = $('wnSubPanel');
        panel.hidden = false;
        if (!window.WNStickers) {
            panel.innerHTML = `
                <div class="wn-sub-header">
                    <span>${t('sticker_picker_title', 'Stickers')}</span>
                    <button type="button" class="wn-sub-close" aria-label="${t('btn_close', 'Close')}">×</button>
                </div>
                <div style="font-size:12px;color:var(--lg-text-dim);text-align:center;padding:30px 10px">
                    ${t('stickers_coming', 'Sticker library will be available soon.')}
                </div>
            `;
            panel.querySelector('.wn-sub-close').onclick = () => { selectTool('select'); };
            return;
        }

        const cats = window.WNStickers.getCategories();
        const tabs = cats.map((c) =>
            `<button type="button" class="wn-sticker-tab ${c.key === _stickerCat ? 'is-active' : ''}" data-cat="${c.key}">${escapeHtml(t(c.label_key, c.key))}</button>`
        ).join('');
        panel.innerHTML = `
            <div class="wn-sub-header">
                <span>${t('sticker_picker_title', 'Stickers')}</span>
                <button type="button" class="wn-sub-close" aria-label="${t('btn_close', 'Close')}">×</button>
            </div>
            <div class="wn-sticker-tabs">${tabs}</div>
            <div class="wn-bg-section">
                <div class="wn-bg-label">${t('sticker_color', 'Color (for outline stickers)')}</div>
                <div class="wn-color-swatches" id="wnStickerColors"></div>
            </div>
            <div class="wn-sticker-grid" id="wnStickerGrid"></div>
        `;
        panel.querySelector('.wn-sub-close').onclick = () => { selectTool('select'); };

        $$('.wn-sticker-tab', panel).forEach((b) => {
            b.onclick = () => {
                _stickerCat = b.dataset.cat;
                $$('.wn-sticker-tab.is-active', panel).forEach((x) => x.classList.remove('is-active'));
                b.classList.add('is-active');
                renderStickerGrid();
            };
        });

        renderColorSwatches('wnStickerColors', _stickerColor, (c) => { _stickerColor = c; renderStickerGrid(); });
        renderStickerGrid();
    }

    function renderStickerGrid() {
        const grid = $('wnStickerGrid');
        if (!grid) return;
        const items = window.WNStickers.getCategoryStickers(_stickerCat);
        grid.innerHTML = items.map((def) => {
            const colour = def.monochrome ? _stickerColor : null;
            return `<button type="button" class="wn-sticker-cell" data-key="${def.key}" title="${escapeHtml(def.key)}">${
                window.WNStickers.thumbnailSVG(def, colour)
            }</button>`;
        }).join('');
        $$('.wn-sticker-cell', grid).forEach((b) => {
            b.onclick = () => addStickerByKey(b.dataset.key);
        });
    }

    function addStickerByKey(key) {
        const def = window.WNStickers.getStickerByKey(key);
        if (!def) return;
        const cv = state.project.canvas;
        // Default size: ~6% of canvas width — visible without dominating
        const size = Math.round(cv.width_px * 0.06);
        const colour = def.monochrome ? _stickerColor : null;
        const group = window.WNStickers.buildKonvaGroup(def, {
            x: (cv.width_px - size) / 2,
            y: (cv.height_px - size) / 2,
            size,
            color: colour,
        });
        tagNode(group, { type: 'sticker' });
        group.setAttr('elStickerKey', key);
        group.setAttr('elStickerColor', colour || def.defaultColor || null);
        attachNodeInteractions(group);
        state.konva.layers.content.add(group);
        state.konva.tr.nodes([group]);
        state.konva.layers.content.batchDraw();
        renderProperties();
        renderLayers();
        markDirty();
        pushHistory();
        ae('sticker_add', { key });
    }

    function openBackgroundPicker() {
        const panel = $('wnSubPanel');
        panel.hidden = false;
        panel.innerHTML = `
            <div class="wn-sub-header">
                <span>${t('bg_picker_title', 'Background')}</span>
                <button type="button" class="wn-sub-close" aria-label="${t('btn_close', 'Close')}">×</button>
            </div>
            <div class="wn-bg-section">
                <div class="wn-bg-label">${t('bg_color', 'Color')}</div>
                <div class="wn-color-swatches" id="wnBgColors"></div>
            </div>
            <div class="wn-prop-row" style="margin-top:8px">
                <label>${t('bg_custom', 'Custom')}</label>
                <input type="color" id="wnBgCustom" value="${state.project.canvas.background?.color || '#ffffff'}">
            </div>
        `;
        panel.querySelector('.wn-sub-close').onclick = () => { selectTool('select'); };
        renderColorSwatches('wnBgColors', state.project.canvas.background?.color || '#ffffff', applyBackgroundColor);
        $('wnBgCustom').oninput = (e) => { applyBackgroundColor(e.target.value); };
    }

    function renderColorSwatches(containerId, current, onPick) {
        const cont = $(containerId);
        if (!cont) return;
        cont.innerHTML = '';
        PALETTE.forEach((c) => {
            const sw = document.createElement('button');
            sw.type = 'button';
            sw.className = 'wn-color-swatch';
            sw.style.background = c;
            sw.title = c;
            if (c.toLowerCase() === (current || '').toLowerCase()) sw.classList.add('is-active');
            sw.onclick = () => {
                $$('.wn-color-swatch.is-active', cont).forEach((x) => x.classList.remove('is-active'));
                sw.classList.add('is-active');
                onPick(c);
            };
            cont.appendChild(sw);
        });
    }

    function applyBackgroundColor(color) {
        if (!state.konva) return;
        state.project.canvas.background = { type: 'color', color };
        setPaperFill(color);
        state.konva.layers.bg.batchDraw();
        markDirty();
        pushHistory();
    }

    // ─── Stage event dispatcher ───────────────────────────────────────
    function onStagePointerDown(e) {
        const stage = state.konva.stage;
        const isStage = e.target === stage;
        const tool = state.tool;

        if (tool === 'select') {
            if (isStage) {
                // Start rubber-band selection
                const pos = stagePointerPos();
                state.drawing = { kind: 'rubber-band', x1: pos.x, y1: pos.y, x2: pos.x, y2: pos.y };
                const sel = state.konva.selectionRect;
                sel.setAttrs({ x: pos.x, y: pos.y, width: 0, height: 0, visible: true });
                state.konva.layers.overlay.batchDraw();
                clearSelection();
            }
            return;
        }

        if (tool === 'text') {
            if (isStage) {
                const pos = stagePointerPos();
                addTextElement(pos.x, pos.y);
            }
            return;
        }

        if (tool === 'shape' && state.drawing?.kind === 'shape-pending') {
            const pos = stagePointerPos();
            state.drawing = {
                kind: 'shape-drawing',
                shape: state.drawing.shape,
                x1: pos.x, y1: pos.y, x2: pos.x, y2: pos.y,
                node: null,
            };
            return;
        }

        if (tool === 'draw') {
            const pos = stagePointerPos();
            state.drawing = { kind: 'pen-drawing', points: [pos.x, pos.y], node: null };
            const node = new window.Konva.Line({
                points: [pos.x, pos.y, pos.x + 0.1, pos.y + 0.1],
                stroke: state.lastColor,
                strokeWidth: Math.max(2, Math.round(4 / state.zoom)),
                lineCap: 'round',
                lineJoin: 'round',
                tension: 0.3,
            });
            tagNode(node, { type: 'drawing' });
            state.konva.layers.content.add(node);
            state.drawing.node = node;
        }
    }

    function onStagePointerMove(e) {
        if (!state.drawing) return;
        const pos = stagePointerPos();

        if (state.drawing.kind === 'rubber-band') {
            state.drawing.x2 = pos.x;
            state.drawing.y2 = pos.y;
            const x = Math.min(state.drawing.x1, state.drawing.x2);
            const y = Math.min(state.drawing.y1, state.drawing.y2);
            const w = Math.abs(state.drawing.x2 - state.drawing.x1);
            const h = Math.abs(state.drawing.y2 - state.drawing.y1);
            state.konva.selectionRect.setAttrs({ x, y, width: w, height: h });
            state.konva.layers.overlay.batchDraw();
            return;
        }

        if (state.drawing.kind === 'shape-drawing') {
            state.drawing.x2 = pos.x;
            state.drawing.y2 = pos.y;
            updateInProgressShape();
            return;
        }

        if (state.drawing.kind === 'pen-drawing') {
            state.drawing.points.push(pos.x, pos.y);
            state.drawing.node.points(state.drawing.points);
            state.konva.layers.content.batchDraw();
        }
    }

    function onStagePointerUp(e) {
        if (!state.drawing) return;

        if (state.drawing.kind === 'rubber-band') {
            state.konva.selectionRect.visible(false);
            state.konva.layers.overlay.batchDraw();
            const r = state.drawing;
            const box = {
                x: Math.min(r.x1, r.x2), y: Math.min(r.y1, r.y2),
                width: Math.abs(r.x2 - r.x1), height: Math.abs(r.y2 - r.y1),
            };
            if (box.width > 4 && box.height > 4) {
                // `box` is in board coordinates (stagePointerPos returns those),
                // so the node rects have to be too — relative to the content
                // layer, which carries no transform. Comparing against
                // stage-relative rects would test board coords against screen
                // coords and select the wrong things at any zoom but 100%.
                const hits = state.konva.layers.content.children.filter((n) =>
                    n.hasName('wn-obj') && Konva.Util.haveIntersection(box, n.getClientRect({ skipShadow: true, relativeTo: state.konva.layers.content })));
                state.konva.tr.nodes(hits);
                state.konva.layers.content.batchDraw();
                renderProperties();
                renderLayers();
            }
            state.drawing = null;
            return;
        }

        if (state.drawing.kind === 'shape-drawing') {
            // Commit: convert preview node into a "real" element with id and listeners
            const node = state.drawing.node;
            if (!node) { state.drawing = { kind: 'shape-pending', shape: state.drawing.shape }; return; }
            attachNodeInteractions(node);
            // Select the new node so user can reposition immediately
            state.konva.tr.nodes([node]);
            state.konva.layers.content.batchDraw();
            renderProperties();
            renderLayers();
            // Stay in shape tool with same shape selected for chaining; require user to click sub-panel close to leave
            state.drawing = { kind: 'shape-pending', shape: state.drawing.shape };
            markDirty();
            pushHistory();
            return;
        }

        if (state.drawing.kind === 'pen-drawing') {
            const node = state.drawing.node;
            if (state.drawing.points.length < 6) {
                // Too short — drop it
                node.destroy();
            } else {
                attachNodeInteractions(node);
                markDirty();
                pushHistory();
            }
            state.konva.layers.content.batchDraw();
            state.drawing = null;
            return;
        }
    }

    function onStageClick(e) {
        if (state.tool !== 'select') return;
        if (e.target === state.konva.stage) {
            clearSelection();
            return;
        }
        // Click on an object — select it
        let node = e.target;
        while (node && node.parent && !node.hasName?.('wn-obj')) {
            // Walk up to a root pm-obj. (Konva.Image children can be groups in templates.)
            if (node.getParent && node.getParent() === state.konva.layers.content) break;
            node = node.parent;
        }
        // Find a tagged ancestor
        let target = e.target;
        while (target && !target.hasName?.('wn-obj')) target = target.getParent?.();
        if (!target || !target.hasName?.('wn-obj')) { clearSelection(); return; }

        const shift = e.evt?.shiftKey;
        const cur = state.konva.tr.nodes();
        if (shift) {
            const idx = cur.indexOf(target);
            const next = idx >= 0 ? cur.filter((n) => n !== target) : cur.concat([target]);
            state.konva.tr.nodes(next);
        } else {
            state.konva.tr.nodes([target]);
        }
        state.konva.layers.content.batchDraw();
        renderProperties();
    }

    function onStageWheel() {
        // Wheel is handled on the container in installViewportGestures(), where
        // it can pan as well as zoom. Kept as a no-op so the stage binding in
        // buildStage() stays symmetrical with the other pointer events.
    }

    function cancelDrawing() {
        if (!state.drawing) return;
        if ((state.drawing.kind === 'shape-drawing' || state.drawing.kind === 'pen-drawing') && state.drawing.node) {
            state.drawing.node.destroy();
            state.konva.layers.content.batchDraw();
        }
        if (state.drawing.kind === 'rubber-band') {
            state.konva.selectionRect.visible(false);
            state.konva.layers.overlay.batchDraw();
        }
        state.drawing = null;
    }

    // ─── Update preview while dragging a shape ────────────────────────
    function updateInProgressShape() {
        const Konva = window.Konva;
        const d = state.drawing;
        const x = Math.min(d.x1, d.x2);
        const y = Math.min(d.y1, d.y2);
        const w = Math.max(2, Math.abs(d.x2 - d.x1));
        const h = Math.max(2, Math.abs(d.y2 - d.y1));

        // Lazy-create the preview node on first drag-move
        if (!d.node) {
            d.node = makeShape(d.shape, { x, y, w, h, fill: state.lastFill });
            tagNode(d.node, { type: 'shape', shape: d.shape });
            state.konva.layers.content.add(d.node);
        }

        // Update geometry
        if (d.shape === 'rect') {
            d.node.setAttrs({ x, y, width: w, height: h });
        } else if (d.shape === 'ellipse') {
            d.node.setAttrs({ x: x + w / 2, y: y + h / 2, radiusX: w / 2, radiusY: h / 2 });
        } else if (d.shape === 'star') {
            const r = Math.min(w, h) / 2;
            d.node.setAttrs({ x: x + w / 2, y: y + h / 2, innerRadius: r * 0.5, outerRadius: r });
        } else if (d.shape === 'heart') {
            d.node.setAttrs({ x, y, _w: w, _h: h });
            d.node.scale({ x: w / 100, y: h / 100 });
        } else if (d.shape === 'arrow' || d.shape === 'line') {
            d.node.setAttrs({ points: [d.x1, d.y1, d.x2, d.y2] });
        }
        state.konva.layers.content.batchDraw();
    }

    function makeShape(shape, opts) {
        const Konva = window.Konva;
        if (shape === 'rect') {
            return new Konva.Rect({
                x: opts.x, y: opts.y, width: opts.w, height: opts.h,
                fill: opts.fill, cornerRadius: 4, draggable: true,
            });
        }
        if (shape === 'ellipse') {
            return new Konva.Ellipse({
                x: opts.x + opts.w / 2, y: opts.y + opts.h / 2,
                radiusX: opts.w / 2, radiusY: opts.h / 2,
                fill: opts.fill, draggable: true,
            });
        }
        if (shape === 'star') {
            const r = Math.min(opts.w, opts.h) / 2;
            return new Konva.Star({
                x: opts.x + opts.w / 2, y: opts.y + opts.h / 2,
                numPoints: 5, innerRadius: r * 0.5, outerRadius: r,
                fill: opts.fill, draggable: true,
            });
        }
        if (shape === 'heart') {
            // Heart as a path using a 100×100 viewbox; scaled via .scale() in drag.
            return new Konva.Path({
                x: opts.x, y: opts.y,
                data: 'M50,90 C50,90 5,55 5,30 A25,25 0 0 1 50,15 A25,25 0 0 1 95,30 C95,55 50,90 50,90 Z',
                fill: opts.fill, draggable: true,
                scaleX: opts.w / 100, scaleY: opts.h / 100,
            });
        }
        if (shape === 'arrow') {
            return new Konva.Arrow({
                points: [opts.x, opts.y + opts.h / 2, opts.x + opts.w, opts.y + opts.h / 2],
                pointerLength: 16, pointerWidth: 14,
                stroke: opts.fill, fill: opts.fill, strokeWidth: 6,
                draggable: true, lineCap: 'round', lineJoin: 'round',
            });
        }
        if (shape === 'line') {
            return new Konva.Line({
                points: [opts.x, opts.y, opts.x + opts.w, opts.y + opts.h],
                stroke: opts.fill, strokeWidth: 6,
                draggable: true, lineCap: 'round',
            });
        }
        return null;
    }

    // ─── Element tagging ──────────────────────────────────────────────
    function tagNode(node, attrs) {
        node.name('wn-obj');
        node.setAttrs({
            elId: attrs.id || ulid(),
            elType: attrs.type,
            elShape: attrs.shape || null,
            elAssetId: attrs.assetId || null,
        });
    }

    function attachNodeInteractions(node) {
        node.draggable(true);
        node.on('dragmove', () => { applySnapDuringDrag(node); });
        node.on('dragend',  () => {
            clearSnapGuides();
            checkCrossSheet(node);
            markDirty();
            pushHistory();
        });
        node.on('mouseenter', () => { document.body.style.cursor = state.tool === 'select' ? 'move' : ''; });
        node.on('mouseleave', () => { document.body.style.cursor = ''; });
    }

    // ─── Cross-sheet warning ──────────────────────────────────────────
    // After a drag, if the element bbox crosses a sheet seam, warn the user
    // once per element. Important for text — getting a word cut by the seam
    // is a print-day disaster, easy to miss on screen.
    const _crossSheetWarned = new Set();

    function checkCrossSheet(node) {
        if (!node || !state.project || !state.konva) return;
        const id = node.getAttr('elId');
        if (!id || _crossSheetWarned.has(id)) return;

        const cv = state.project.canvas;
        const sx = Math.abs(node.scaleX ? node.scaleX() : 1);
        const sy = Math.abs(node.scaleY ? node.scaleY() : 1);
        const w = (node.width  ? node.width()  : 0) * sx;
        const h = (node.height ? node.height() : 0) * sy;
        if (w === 0 && h === 0) return; // lines / paths without a real bbox
        const x1 = node.x();
        const y1 = node.y();
        const x2 = x1 + w;
        const y2 = y1 + h;
        const L0 = boardLayout();
        if (L0.mode === 'pages') {
            // On separate pages this is not a cosmetic warning: anything
            // outside a page's printable area is simply not printed, and
            // anything spanning two pages is torn between two pockets. It
            // applies to EVERY element type, not just text.
            let inside = false;
            let straddles = false;
            for (let r = 0; r < L0.rows; r++) {
                for (let c = 0; c < L0.cols; c++) {
                    if (!L0.pageExists(c, r)) continue;
                    const t0 = L0.tileRectPX(c, r);
                    const ox = Math.min(x2, t0.x + t0.w) - Math.max(x1, t0.x);
                    const oy = Math.min(y2, t0.y + t0.h) - Math.max(y1, t0.y);
                    if (ox <= 0 || oy <= 0) continue;
                    const fully = x1 >= t0.x - 1 && y1 >= t0.y - 1
                        && x2 <= t0.x + t0.w + 1 && y2 <= t0.y + t0.h + 1;
                    if (fully) { inside = true; }
                    else { straddles = true; }
                }
            }
            if (!inside && straddles) {
                toast(t('cross_page_warn', 'This element sits across two pages — each page prints separately.'), 'warning');
                _crossSheetWarned.add(id);
                ae('cross_page_warn', { type: node.getAttr('elType') });
            } else if (!inside && !straddles) {
                toast(t('off_page_warn', 'This element is outside every page and will not print.'), 'warning');
                _crossSheetWarned.add(id);
                ae('off_page_warn', { type: node.getAttr('elType') });
            }
            return;
        }

        // Only warn for text — stickers and decorative shapes spanning seams is
        // intentional and looks fine assembled. Text cut mid-letter is the
        // actual usability problem.
        const isText = node.getAttr('elType') === 'text';
        if (!isText) return;

        // The hazard is the GLUE BAND, not an infinitely thin line: anything
        // that lands inside it gets a paper edge laid across it. Measuring
        // against the band (rather than its centre) is why this now fires for
        // a heading that merely touches the seam.
        const L = boardLayout();
        const SAFE = 6; // canvas-space px slack before triggering
        let crosses = false;
        for (const b of L.overlapBandsPX()) {
            if (b.axis === 'v') {
                if (x1 < b.x + b.w - SAFE && x2 > b.x + SAFE) { crosses = true; break; }
            } else if (y1 < b.y + b.h - SAFE && y2 > b.y + SAFE) { crosses = true; break; }
        }
        if (!crosses && L.overlap_mm <= 0) {
            const strideW = cv.width_px / cv.sheets.cols;
            const strideH = cv.height_px / cv.sheets.rows;
            for (let c = 1; c < cv.sheets.cols; c++) {
                const seamX = c * strideW;
                if (x1 < seamX - SAFE && x2 > seamX + SAFE) { crosses = true; break; }
            }
            if (!crosses) {
                for (let r = 1; r < cv.sheets.rows; r++) {
                    const seamY = r * strideH;
                    if (y1 < seamY - SAFE && y2 > seamY + SAFE) { crosses = true; break; }
                }
            }
        }
        if (crosses) {
            toast(t('cross_sheet_warn', 'This element crosses a sheet seam — it will be cut when printed.'), 'warning');
            _crossSheetWarned.add(id);
            ae('cross_sheet_warn', { type: node.getAttr('elType') });
        }
    }

    // ─── Smart guides + snap ─────────────────────────────────────────
    // Snap candidates: canvas borders, canvas centre, A4 sheet seams.
    // The snap tolerance is a fixed 8 px in canvas-space — at default zoom
    // that translates to ~0.7 mm of slack which feels good without fighting
    // the user.
    let _snapGuideLines = [];

    function clearSnapGuides() {
        for (const ln of _snapGuideLines) ln.destroy();
        _snapGuideLines = [];
        if (state.konva) state.konva.layers.overlay.batchDraw();
    }

    function applySnapDuringDrag(node) {
        if (!state.project || !state.konva) return;
        // Snap can be turned off in Settings (still draws guides though — they
        // just don't pull the position).
        const snapEnabled = window.__toolSettings?.load?.(SLUG, 'snap_enabled', true) !== false;
        const cv = state.project.canvas;
        if (!cv) return;
        // Tolerance in canvas-space — scale-aware so it stays ~consistent visually
        const TOL = Math.max(4, 12 / Math.max(0.4, state.zoom));

        // Bounding box of the node in canvas-space
        const sx = node.scaleX ? Math.abs(node.scaleX()) : 1;
        const sy = node.scaleY ? Math.abs(node.scaleY()) : 1;
        const w  = (node.width  ? node.width()  : 0) * sx;
        const h  = (node.height ? node.height() : 0) * sy;
        const x  = node.x();
        const y  = node.y();
        const cx = x + w / 2;
        const cy = y + h / 2;

        // Seam candidates come from the layout, not from an even division of
        // the canvas: with a glue overlap the seams are NOT evenly spaced, and
        // snapping to the wrong place would park an element half inside the
        // band it was trying to avoid.
        const L = boardLayout();
        const strideW = L.stride_w_mm * L.px_per_mm;
        const strideH = L.stride_h_mm * L.px_per_mm;
        const bandW = L.overlap_mm * L.px_per_mm;

        // Vertical-line candidates (snap on the X axis): canvas edges, centre, seams
        const xLines = [
            { v: 0,             label: 'edge'    },
            { v: cv.width_px,   label: 'edge'    },
            { v: cv.width_px/2, label: 'center'  },
        ];
        if (L.mode === 'pages') {
            // Snap to the printable edges of each page — that is where content
            // has to stop, so it is where the guides belong.
            for (let c = 0; c < L.cols; c++) {
                const t0 = L.tileRectPX(c, 0);
                xLines.push({ v: t0.x, label: 'seam' });
                xLines.push({ v: t0.x + t0.w, label: 'seam' });
                xLines.push({ v: t0.x + t0.w / 2, label: 'center' });
            }
        } else for (let c = 1; c < cv.sheets.cols; c++) {
            xLines.push({ v: c * strideW, label: 'seam' });
            if (bandW > 0) xLines.push({ v: c * strideW + bandW, label: 'seam' });
        }

        // Horizontal-line candidates
        const yLines = [
            { v: 0,              label: 'edge'    },
            { v: cv.height_px,   label: 'edge'    },
            { v: cv.height_px/2, label: 'center'  },
        ];
        if (L.mode === 'pages') {
            for (let r = 0; r < L.rows; r++) {
                const t0 = L.tileRectPX(0, r);
                yLines.push({ v: t0.y, label: 'seam' });
                yLines.push({ v: t0.y + t0.h, label: 'seam' });
                yLines.push({ v: t0.y + t0.h / 2, label: 'center' });
            }
        } else for (let r = 1; r < cv.sheets.rows; r++) {
            yLines.push({ v: r * strideH, label: 'seam' });
            if (bandW > 0) yLines.push({ v: r * strideH + bandW, label: 'seam' });
        }

        // Add snap candidates from OTHER objects on the canvas — left, centre,
        // right edges (and equivalents for the y axis). This is the "nudge into
        // alignment with that photo over there" behaviour Figma/Canva users
        // expect. Skip the dragged node itself and any selected sibling.
        const draggedSet = new Set(state.konva.tr.nodes());
        for (const other of state.konva.layers.content.children) {
            if (!other.hasName?.('wn-obj')) continue;
            if (other === node || draggedSet.has(other)) continue;
            const osx = other.scaleX ? Math.abs(other.scaleX()) : 1;
            const osy = other.scaleY ? Math.abs(other.scaleY()) : 1;
            const ow = (other.width  ? other.width()  : 0) * osx;
            const oh = (other.height ? other.height() : 0) * osy;
            const ox = other.x();
            const oy = other.y();
            xLines.push({ v: ox,        label: 'obj' });
            xLines.push({ v: ox + ow,   label: 'obj' });
            xLines.push({ v: ox + ow/2, label: 'obj' });
            yLines.push({ v: oy,        label: 'obj' });
            yLines.push({ v: oy + oh,   label: 'obj' });
            yLines.push({ v: oy + oh/2, label: 'obj' });
        }

        // For each axis we evaluate three anchors: left/center/right (or top/middle/bottom).
        let bestDx = null, bestDxAt = null, bestXLine = null;
        for (const line of xLines) {
            for (const [anchor, val] of [['left', x], ['center', cx], ['right', x + w]]) {
                const d = line.v - val;
                if (Math.abs(d) <= TOL && (bestDx == null || Math.abs(d) < Math.abs(bestDx))) {
                    bestDx = d; bestDxAt = anchor; bestXLine = line;
                }
            }
        }
        let bestDy = null, bestDyAt = null, bestYLine = null;
        for (const line of yLines) {
            for (const [anchor, val] of [['top', y], ['middle', cy], ['bottom', y + h]]) {
                const d = line.v - val;
                if (Math.abs(d) <= TOL && (bestDy == null || Math.abs(d) < Math.abs(bestDy))) {
                    bestDy = d; bestDyAt = anchor; bestYLine = line;
                }
            }
        }

        // Only pull the position when snap is enabled. Visual guides stay so
        // the user can see alignment opportunities even with snap off.
        if (snapEnabled && bestDx != null) node.x(x + bestDx);
        if (snapEnabled && bestDy != null) node.y(y + bestDy);

        // Render visible guide lines on the overlay layer
        clearSnapGuides();
        const Konva = window.Konva;
        if (bestXLine) {
            const ln = new Konva.Line({
                points: [bestXLine.v, 0, bestXLine.v, cv.height_px],
                stroke: '#ff3b8a', strokeWidth: Math.max(1, 1 / state.zoom),
                listening: false, opacity: 0.85,
            });
            state.konva.layers.overlay.add(ln);
            _snapGuideLines.push(ln);
        }
        if (bestYLine) {
            const ln = new Konva.Line({
                points: [0, bestYLine.v, cv.width_px, bestYLine.v],
                stroke: '#ff3b8a', strokeWidth: Math.max(1, 1 / state.zoom),
                listening: false, opacity: 0.85,
            });
            state.konva.layers.overlay.add(ln);
            _snapGuideLines.push(ln);
        }
        state.konva.layers.overlay.batchDraw();
    }

    // ─── Text element ─────────────────────────────────────────────────
    function addTextElement(x, y) {
        const Konva = window.Konva;
        // Size the default relative to the SHEET, not in absolute pixels.
        // A flat 56 px is 4.7 mm of paper — below the size a person reads from
        // a metre away, which is the only distance this artefact is ever read
        // from — and it was the same 4.7 mm whether the board was one A4 or a
        // 1.1-metre wall.
        const L = boardLayout();
        const defaultSize = Math.round(L.usable_h_mm * 0.045 * L.px_per_mm);
        const txt = new Konva.Text({
            x, y,
            text: t('text_placeholder', 'Type your text'),
            fontSize: defaultSize,
            fontFamily: 'Inter, system-ui, sans-serif',
            fill: state.lastColor,
            align: 'center',
            padding: 4,
        });
        tagNode(txt, { type: 'text' });
        attachNodeInteractions(txt);
        // Double-click toggles inline edit
        txt.on('dblclick dbltap', () => startTextEdit(txt));
        state.konva.layers.content.add(txt);
        state.konva.tr.nodes([txt]);
        state.konva.layers.content.batchDraw();
        startTextEdit(txt);
        renderProperties();
        renderLayers();
        markDirty();
        pushHistory();
        // Stay in 'text' tool — user can keep adding text, finishing each
        // with Enter/Esc/blur. Switching to 'select' here closed the editor
        // we had just opened.
    }

    // ─── WordArt presets ─────────────────────────────────────────────
    function applyWordart(node, preset) {
        if (!node) return;
        node.setAttr('elWordart', preset || 'none');
        const base = node.getAttr('elBaseFill') || node.fill() || '#111';
        // Reset everything WordArt may have touched first
        node.fillLinearGradientColorStops([]);
        node.shadowColor(null);
        node.shadowBlur(0);
        node.shadowOffsetX(0);
        node.shadowOffsetY(0);
        node.shadowOpacity(0);
        node.stroke(null);
        node.strokeWidth(0);

        if (preset === 'rainbow') {
            // Horizontal gradient using fillLinearGradient*. Width inferred from
            // the text node's measured width (Konva populates it after layout).
            const w = Math.max(80, node.width() || 400);
            node.fill(null);
            node.fillLinearGradientStartPoint({ x: 0, y: 0 });
            node.fillLinearGradientEndPoint({ x: w, y: 0 });
            node.fillLinearGradientColorStops([
                0,    '#e63946',
                0.25, '#ff9900',
                0.50, '#ffd700',
                0.75, '#5cb3ff',
                1,    '#a78bfa',
            ]);
        } else if (preset === 'shadow3d') {
            node.fill(base);
            node.shadowColor('#222');
            node.shadowBlur(0);
            node.shadowOffsetX(8);
            node.shadowOffsetY(8);
            node.shadowOpacity(0.6);
        } else if (preset === 'outline') {
            node.fill(base);
            node.stroke('#111');
            node.strokeWidth(Math.max(2, node.fontSize() * 0.04));
        } else if (preset === 'glow') {
            node.fill(base);
            node.shadowColor('#fff8a0');
            node.shadowBlur(24);
            node.shadowOpacity(0.95);
        } else { // 'none'
            node.fill(base);
        }
    }

    /* A curved heading is a Konva.TextPath, not a Konva.Text: it draws text
       along an arc and has no lineHeight() (Sentry TIMBRICA-FRONTEND-JY — the
       inline editor asked the TextPath directly on double-click and died).
       Every reader of a text element's spacing and box goes through here.
       Line height belongs to the HEADING, not to the node class: the curved
       form carries the straight form's value in `elLineHeight`, so taking the
       bend out gives the user's spacing back instead of a default 1.2. */
    function textLineHeight(node) {
        if (node && typeof node.lineHeight === 'function') return node.lineHeight();
        const kept = node && node.getAttr ? node.getAttr('elLineHeight') : null;
        return typeof kept === 'number' && kept > 0 ? kept : 1.2;
    }
    function textBoxWidth(node) {
        return node.getAttr('elTextWidth') || (typeof node.width === 'function' ? node.width() : 0) || 0;
    }

    let _editingTextNode = null;
    function startTextEdit(node) {
        finishTextEdit(true);
        _editingTextNode = node;
        const ta = $('wnTextEditor');
        // The overlay lives in the viewport, so the node's board position goes
        // through the camera exactly like everything else drawn on top.
        const p = boardToScreen(node.x(), node.y());
        const x = p.x;
        const y = p.y;
        ta.value = node.text();
        ta.style.fontSize = (node.fontSize() * state.zoom) + 'px';
        ta.style.fontFamily = node.fontFamily();
        ta.style.color = node.fill();
        ta.style.lineHeight = textLineHeight(node);
        ta.style.textAlign = node.align();
        ta.style.fontWeight = (node.fontStyle?.() === 'bold' ? 700 : (node.fontStyle?.()?.includes?.('bold') ? 700 : 400));
        ta.style.left = x + 'px';
        ta.style.top = y + 'px';
        ta.style.width = Math.max(120, textBoxWidth(node) * state.zoom) + 'px';
        ta.style.minHeight = Math.max(40, node.fontSize() * state.zoom * 1.4) + 'px';
        ta.hidden = false;
        node.hide();
        state.konva.layers.content.batchDraw();
        ta.focus();
        ta.select();
    }

    function finishTextEdit(silent) {
        if (!_editingTextNode) return;
        const ta = $('wnTextEditor');
        const newText = ta.value;
        const node = _editingTextNode;
        _editingTextNode = null;
        ta.hidden = true;
        if (!newText.trim()) {
            // Empty text → remove element
            node.destroy();
        } else {
            node.text(newText);
            node.show();
        }
        state.konva.layers.content.batchDraw();
        if (!silent) { markDirty(); pushHistory(); renderProperties(); renderLayers(); }
    }

    // ─── Photo frames ─────────────────────────────────────────────────
    /* A frame is an empty photo slot: a marked-out rectangle that turns into a
       cropped photo when one is dropped on it.
     *
     * This is the interaction the whole genre is built on — a wall newspaper is
     * a wall of class photos — and it is what lets a template ship a LAYOUT
     * rather than only decoration. Every benchmark editor has it; without it a
     * template can suggest where a photo goes only by leaving a gap, and the
     * user has to size and position each photo by hand.
     */

    function makeFrameNode(x, y, w, h) {
        const Konva = window.Konva;
        const g = new Konva.Group({ x, y, draggable: true });
        const rect = new Konva.Rect({
            x: 0, y: 0, width: w, height: h,
            fill: 'rgba(120,150,200,.10)',
            stroke: '#7aa7ff', strokeWidth: Math.max(6, Math.round(Math.min(w, h) * 0.012)),
            dash: [Math.round(Math.min(w, h) * 0.05), Math.round(Math.min(w, h) * 0.035)],
            cornerRadius: Math.round(Math.min(w, h) * 0.03),
            name: 'wn-frame-rect',
        });
        const glyph = new Konva.Text({
            x: 0, y: h / 2 - Math.min(w, h) * 0.18,
            width: w, align: 'center',
            text: '🖼️',
            fontSize: Math.round(Math.min(w, h) * 0.26),
            listening: false, name: 'wn-frame-glyph',
        });
        const label = new Konva.Text({
            x: 0, y: h / 2 + Math.min(w, h) * 0.12,
            width: w, align: 'center',
            text: t('frame_hint', 'Add a photo'),
            fontSize: Math.max(24, Math.round(Math.min(w, h) * 0.075)),
            fontFamily: 'Inter, sans-serif',
            fill: '#5b7fc7', listening: false, name: 'wn-frame-label',
        });
        g.add(rect); g.add(glyph); g.add(label);
        // Konva groups have no intrinsic size; the rest of the app (cm readout,
        // crop maths, alignment) asks nodes for width()/height().
        g.width(w); g.height(h);
        return g;
    }

    function addFrameElement(x, y, w, h) {
        const cv = state.project.canvas;
        const fw = w || Math.round(cv.width_px * 0.18);
        const fh = h || Math.round(fw * 0.75);
        const node = makeFrameNode(
            x != null ? x : (cv.width_px - fw) / 2,
            y != null ? y : (cv.height_px - fh) / 2,
            fw, fh,
        );
        tagNode(node, { type: 'frame' });
        attachNodeInteractions(node);
        node.on('dblclick dbltap', () => pickPhotoForFrame(node));
        state.konva.layers.content.add(node);
        state.konva.tr.nodes([node]);
        state.konva.layers.content.batchDraw();
        renderProperties();
        renderLayers();
        markDirty();
        pushHistory();
        ae('frame_add', {});
        return node;
    }

    let _frameAwaitingPhoto = null;

    function pickPhotoForFrame(node) {
        _frameAwaitingPhoto = node;
        $('wnImageInput').click();
    }

    /**
     * Replace a frame with a photo that fills exactly the frame's box.
     *
     * The frame's geometry is the contract: the photo inherits its position,
     * size and z-order, and gets a crop window so it fills without distortion.
     * That is the difference between "a template suggests a photo here" and
     * "the photo is already laid out".
     */
    async function fillFrameWithBlob(frame, blob) {
        const box = {
            x: frame.x(), y: frame.y(),
            w: frame.width() * Math.abs(frame.scaleX() || 1),
            h: frame.height() * Math.abs(frame.scaleY() || 1),
            rotation: frame.rotation(),
            z: frame.zIndex(),
        };
        const node = await addImageFromBlob(blob, box.x, box.y, { silent: true });
        if (!node) return null;
        node.width(box.w);
        node.height(box.h);
        node.rotation(box.rotation);
        applyPhotoCrop(node);
        node.zIndex(Math.min(box.z, state.konva.layers.content.children.length - 1));
        frame.destroy();
        state.konva.tr.nodes([node]);
        state.konva.layers.content.batchDraw();
        renderProperties();
        renderLayers();
        markDirty();
        pushHistory();
        ae('frame_filled', {});
        return node;
    }

    /** The frame under a board-space point, if any. Used by drag-and-drop. */
    function frameAtPoint(x, y) {
        const kids = state.konva?.layers.content.children || [];
        for (let i = kids.length - 1; i >= 0; i--) {
            const n = kids[i];
            if (n.getAttr?.('elType') !== 'frame') continue;
            const r = n.getClientRect({ relativeTo: state.konva.layers.content });
            if (x >= r.x && x <= r.x + r.width && y >= r.y && y <= r.y + r.height) return n;
        }
        return null;
    }

    // ─── Image element ────────────────────────────────────────────────
    async function addImageFromBlob(blob, x, y, opts) {
        try {
            // iPhone HEIC/HEIF: decode to JPEG via heic-to before validation/decode.
            if (window.HeicDecode) { try { blob = await window.HeicDecode.normalize(blob, window.__ae || null); } catch (_e) {} }
            // Validation via Timbrica global guard
            if (window.__validateFileSize) {
                window.__validateFileSize(blob, 'image', { source: SLUG });
            }
        } catch (e) {
            toast(e.message || t('image_too_large', 'Image too large'), 'error');
            return;
        }

        // Smart compress: photos > 4 MP eat ~24 MB of canvas memory each. With
        // 10 photos that's 240 MB — enough to OOM iOS Safari. Compressing to
        // 2400 px max edge preserves print quality at 300 DPI for any photo
        // smaller than ~1/3 of the canvas (i.e. realistic editorial use).
        // Toggle off via Settings → "image_compress" __toolSettings key.
        const compressEnabled = window.__toolSettings?.load?.(SLUG, 'image_compress', true) !== false;
        let workingBlob = blob;
        const probeUrl = URL.createObjectURL(blob);
        let probe;
        try { probe = await loadImage(probeUrl); }
        catch (e) { URL.revokeObjectURL(probeUrl); toast(t('load_error', 'Could not open file'), 'error'); return; }
        const naturalMP = (probe.naturalWidth * probe.naturalHeight) / 1_000_000;

        if (compressEnabled && naturalMP > 4) {
            try {
                const compressed = await compressImageToMaxEdge(probe, 2400, 0.88);
                if (compressed) {
                    workingBlob = compressed;
                    ae('image_compressed', {
                        fromKb: Math.round(blob.size / 1024),
                        toKb:   Math.round(compressed.size / 1024),
                        fromMP: Math.round(naturalMP),
                    });
                }
            } catch (e) { /* fall back to original blob silently */ }
        }
        URL.revokeObjectURL(probeUrl);

        const url = URL.createObjectURL(workingBlob);
        const img = workingBlob === blob ? probe : await loadImage(url);
        const assetId = ulid();
        state.assets.set(assetId, { blob: workingBlob, url, source: 'upload' });

        // Scale to fit ~1/3 of the canvas width by default
        const cv = state.project.canvas;
        const maxW = cv.width_px * 0.4;
        const ratio = img.naturalWidth / img.naturalHeight;
        const w = Math.min(img.naturalWidth, maxW);
        const h = w / ratio;

        const Konva = window.Konva;
        const node = new Konva.Image({
            x: x != null ? x : (cv.width_px - w) / 2,
            y: y != null ? y : (cv.height_px - h) / 2,
            image: img,
            width: w, height: h,
            draggable: true,
        });
        node.strokeScaleEnabled(false);
        applyPhotoCrop(node);
        tagNode(node, { type: 'image', assetId });
        attachNodeInteractions(node);
        state.konva.layers.content.add(node);
        if (opts?.silent) {
            // The frame path finishes the placement itself and pushes one
            // history entry for the whole operation.
            return node;
        }
        state.konva.tr.nodes([node]);
        state.konva.layers.content.batchDraw();
        renderProperties();
        renderLayers();
        markDirty();
        pushHistory();
        ae('image_add', { source: 'upload', sizeKb: Math.round(blob.size / 1024) });
        return node;
    }

    /* A stored photo whose bytes no longer decode. The board keeps its place;
       the person hears it once per opening, not once per photo. */
    let _unreadableSaid = 0;
    function photoUnreadable(assetId) {
        aeCritical('photo_unreadable', { asset: String(assetId || '').slice(0, 32) });
        const now = Date.now();
        if (now - _unreadableSaid < 5000) return;
        _unreadableSaid = now;
        toast(t('photo_unreadable', 'A photo on this wall newspaper could not be opened — its place is kept empty.'), 'warning');
    }

    function loadImage(url) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error('image load failed'));
            img.src = url;
        });
    }

    async function compressImageToMaxEdge(img, maxEdge, quality) {
        const ratio = img.naturalWidth / img.naturalHeight;
        let w, h;
        if (img.naturalWidth >= img.naturalHeight) {
            w = Math.min(maxEdge, img.naturalWidth);
            h = Math.round(w / ratio);
        } else {
            h = Math.min(maxEdge, img.naturalHeight);
            w = Math.round(h * ratio);
        }
        const c = document.createElement('canvas');
        c.width = w;
        c.height = h;
        c.getContext('2d').drawImage(img, 0, 0, w, h);
        return new Promise((resolve) => c.toBlob(resolve, 'image/jpeg', quality || 0.88));
    }

    function installCanvasDragDrop() {
        const area = $('wnCanvasArea');
        ['dragenter', 'dragover'].forEach((ev) => {
            area.addEventListener(ev, (e) => {
                if (e.dataTransfer?.types?.includes('Files')) {
                    e.preventDefault();
                    area.classList.add('wn-drag-over');
                }
            });
        });
        area.addEventListener('dragleave', () => area.classList.remove('wn-drag-over'));
        area.addEventListener('drop', async (e) => {
            e.preventDefault();
            area.classList.remove('wn-drag-over');
            const files = Array.from(e.dataTransfer?.files || []);
            const images = files.filter((f) => f.type.startsWith('image/'));
            if (!images.length) return;

            // Where on the board did it land? A photo dropped onto a frame
            // fills that frame; anywhere else it is placed at the cursor rather
            // than always in the middle of the board, which is what made
            // dropping ten photos produce one stack in the centre.
            const host = $('wnStage');
            const r = host.getBoundingClientRect();
            const bx = (e.clientX - r.left) / state.zoom + state.cam.x;
            const by = (e.clientY - r.top) / state.zoom + state.cam.y;

            const frame = frameAtPoint(bx, by);
            if (frame) {
                await fillFrameWithBlob(frame, images.shift());
            }
            let i = 0;
            for (const f of images) {
                // Fan the rest out so they do not land exactly on top of
                // each other.
                const off = (i++) * Math.round(8 * PX_PER_MM);
                await addImageFromBlob(f, bx + off, by + off);
            }
        });
    }

    function installPasteHandler() {
        document.addEventListener('paste', async (e) => {
            if (state.project == null) return;
            // Don't intercept while editing text
            if (_editingTextNode) return;
            const items = Array.from(e.clipboardData?.items || []);
            for (const it of items) {
                if (it.type.startsWith('image/')) {
                    const blob = it.getAsFile();
                    if (blob) {
                        e.preventDefault();
                        await addImageFromBlob(blob);
                        ae('image_add', { source: 'paste' });
                        return;
                    }
                }
            }
        });
    }

    // ─── Selection helpers ────────────────────────────────────────────
    function clearSelection() {
        state.konva.tr.nodes([]);
        state.konva.layers.content.batchDraw();
        renderProperties();
    }

    function selectedNodes() {
        return state.konva ? state.konva.tr.nodes() : [];
    }

    /** After any resize the photo's source window has to be recomputed. */
    function refitPhotosAfterTransform(nodes) {
        for (const n of nodes) {
            if (n.getAttr?.('elType') !== 'image') continue;
            // Bake the transformer's scale into width/height so the crop maths
            // and the cm readout both see the real box.
            const sx = n.scaleX() || 1, sy = n.scaleY() || 1;
            if (sx !== 1 || sy !== 1) {
                n.width(Math.max(1, n.width() * Math.abs(sx)));
                n.height(Math.max(1, n.height() * Math.abs(sy)));
                n.scaleX(sx < 0 ? -1 : 1);
                n.scaleY(sy < 0 ? -1 : 1);
            }
            const pct = n.getAttr('elRadiusPct') || 0;
            if (pct) n.cornerRadius(Math.min(n.width(), n.height()) * (pct / 100));
            applyPhotoCrop(n);
            if (n.isCached()) refreshPhotoRender(n);
        }
    }

    function commitTransform() {
        // Photos are the one type whose geometry cannot stay in `scale`: the
        // crop window is derived from width/height, so a scaled frame would
        // keep cropping for its old size and stretch the picture.
        refitPhotosAfterTransform(selectedNodes());
        state.konva?.layers.content.batchDraw();
        renderProperties();
    }

    // ─── Properties panel ─────────────────────────────────────────────
    function renderProperties() {
        const root = $('wnProperties');
        const nodes = selectedNodes();
        // Floating element toolbar tracks the same selection — keep it in sync.
        positionElToolbar();
        if (nodes.length === 0) {
            root.innerHTML = `<div class="wn-rp-empty">${t('properties_empty', 'Select an element to see its properties.')}</div>`;
            return;
        }
        if (nodes.length > 1) {
            root.innerHTML = `
                <div class="wn-rp-empty">${t('properties_multi', ':n elements selected.').replace(':n', nodes.length)}</div>
                ${alignmentHTML(nodes.length)}
                ${commonActionsHTML()}
            `;
            wireAlignment(nodes);
            wireCommonActions(nodes);
            return;
        }
        const node = nodes[0];
        const type = node.getAttr('elType');

        let typeFields = '';
        if (type === 'frame') typeFields = `
            <div class="wn-prop-section">
                <div class="wn-prop-section-label">${escapeHtml(t('prop_frame', 'Frame'))}</div>
                <button type="button" class="wn-btn wn-btn-wide wn-btn-primary" id="wnFramePick">${escapeHtml(t('frame_pick', 'Choose a photo'))}</button>
                <div class="wn-prop-hint">${escapeHtml(t('frame_drop_hint', 'Or drag a photo straight onto the frame.'))}</div>
            </div>`;
        else if (type === 'text') typeFields = textFieldsHTML(node);
        else if (type === 'image') typeFields = photoFieldsHTML(node);
        else if (type === 'shape' || type === 'drawing') typeFields = colorFieldsHTML(node);

        const locked = node.getAttr('elLocked') === true;
        const cmU = escapeHtml(t('cm_label', 'cm'));
        root.innerHTML = `
            <div class="wn-prop-section">
                <div class="wn-prop-section-label">${escapeHtml(t('prop_position', 'Position and size'))}</div>
                <div class="wn-prop-grid-2">
                    <div class="wn-prop-row"><label>X</label><span class="wn-num-input"><input type="number" step="0.1" id="wnPropX" value="${cmField(node.x())}"><span class="wn-num-unit">${cmU}</span></span></div>
                    <div class="wn-prop-row"><label>Y</label><span class="wn-num-input"><input type="number" step="0.1" id="wnPropY" value="${cmField(node.y())}"><span class="wn-num-unit">${cmU}</span></span></div>
                    <div class="wn-prop-row"><label>${escapeHtml(t('prop_width', 'W'))}</label><span class="wn-num-input"><input type="number" step="0.1" min="0.1" id="wnPropW" value="${cmField(nodeBoxW(node))}"><span class="wn-num-unit">${cmU}</span></span></div>
                    <div class="wn-prop-row"><label>${escapeHtml(t('prop_height', 'H'))}</label><span class="wn-num-input"><input type="number" step="0.1" min="0.1" id="wnPropH" value="${cmField(nodeBoxH(node))}" ${isCurvedText(node) ? 'disabled title="' + escapeHtml(t('prop_height_curved', 'Set by the arc and the font size')) + '"' : ''}><span class="wn-num-unit">${cmU}</span></span></div>
                </div>
                <div class="wn-prop-row">
                    <label>${escapeHtml(t('prop_rotation', 'Rotate'))}</label>
                    <input type="range" id="wnPropRot" min="-180" max="180" value="${Math.round(node.rotation())}">
                    <output class="wn-prop-out" id="wnPropRotOut">${Math.round(node.rotation())}°</output>
                </div>
                <div class="wn-prop-row">
                    <label>${escapeHtml(t('prop_opacity', 'Opacity'))}</label>
                    <input type="range" id="wnPropOp" min="0" max="100" value="${Math.round(node.opacity() * 100)}">
                    <output class="wn-prop-out" id="wnPropOpOut">${Math.round(node.opacity() * 100)}%</output>
                </div>
            </div>
            ${typeFields}
            ${alignmentHTML(1)}
            <div class="wn-prop-row wn-prop-lock">
                <label class="wn-check">
                    <input type="checkbox" id="wnPropLock" ${locked ? 'checked' : ''}>
                    <span>${escapeHtml(t('prop_lock', 'Lock in place'))}</span>
                </label>
            </div>
            ${commonActionsHTML()}
        `;
        wireCommonProps(node);
        wireCommonActions([node]);
        wireAlignment([node]);
        if (type === 'frame' && $('wnFramePick')) $('wnFramePick').onclick = () => pickPhotoForFrame(node);
        if (type === 'text') wireTextProps(node);
        if (type === 'image') wirePhotoProps(node);
        if (type === 'shape' || type === 'drawing') wireColorProps(node);
    }

    function textFieldsHTML(node) {
        const curFont = node.fontFamily?.() || 'Inter';
        const fontOpts = FONT_FAMILIES.map((f) =>
            `<option value="${escapeHtml(f.id)}" ${curFont.includes(f.id) ? 'selected' : ''} style="font-family:'${escapeHtml(f.id)}',sans-serif">${escapeHtml(f.label)}</option>`
        ).join('');
        const curWordart = node.getAttr?.('elWordart') || 'none';
        // Visual WordArt buttons — each button renders the effect inline so the
        // user sees what they'll get before clicking. Matches the design handoff
        // grid (rainbow gradient text, 3D shadow, outline stroke, glow).
        const WORDART_LABEL_CLS = {
            none: '', rainbow: 'wa-rainbow', shadow3d: 'wa-shadow3d',
            outline: 'wa-outline', glow: 'wa-glow',
        };
        const wordartBtnLabel = (p) => p === 'none' ? t('wordart_none', 'None')
            : p === 'rainbow' ? 'RAINBOW' : p === 'shadow3d' ? '3D'
            : p === 'outline' ? 'OUTLINE' : p === 'glow' ? 'GLOW'
            : escapeHtml(t('wordart_' + p, p));
        const wordartGrid = WORDART_PRESETS.map((p) =>
            `<button type="button" class="wn-wordart-btn ${WORDART_LABEL_CLS[p] || ''}${p === curWordart ? ' is-active' : ''}" data-wa="${p}" title="${escapeHtml(t('wordart_' + p, p))}">${wordartBtnLabel(p)}</button>`
        ).join('');
        // Display the current fill via a colour swatch even if a gradient
        // (rainbow) is active — fall back to the saved base colour.
        const baseColour = node.getAttr?.('elBaseFill') || node.fill?.() || '#111111';
        return `
            ${colorRowHTML('prop_color', 'Color', 'wnPropColor', baseColour)}
            <div class="wn-prop-row"><label>${t('prop_font_size', 'Size')}</label><input type="number" id="wnPropFontSize" min="8" max="400" value="${Math.round(node.fontSize())}"></div>
            <div class="wn-prop-row"><label>${t('prop_font', 'Font')}</label><select id="wnPropFont">${fontOpts}</select></div>
            <div class="wn-prop-section">
                <div class="wn-prop-section-label">${t('prop_wordart', 'Effect')}</div>
                <div class="wn-wordart-grid" id="wnPropWordartGrid">${wordartGrid}</div>
            </div>
            <div class="wn-prop-row">
                <label>${t('prop_align', 'Align')}</label>
                <select id="wnPropAlign">
                    <option value="left"   ${node.align() === 'left'   ? 'selected' : ''}>${t('align_left', 'Left')}</option>
                    <option value="center" ${node.align() === 'center' ? 'selected' : ''}>${t('align_center', 'Center')}</option>
                    <option value="right"  ${node.align() === 'right'  ? 'selected' : ''}>${t('align_right', 'Right')}</option>
                </select>
            </div>
            <div class="wn-prop-row" style="gap:4px">
                <button type="button" class="wn-btn wn-btn-sm" id="wnPropBold" aria-pressed="${(node.fontStyle?.()||'').includes('bold') ? 'true' : 'false'}"><b>B</b></button>
                <button type="button" class="wn-btn wn-btn-sm" id="wnPropItalic" aria-pressed="${(node.fontStyle?.()||'').includes('italic') ? 'true' : 'false'}"><i>I</i></button>
                <button type="button" class="wn-btn wn-btn-sm" id="wnPropUpper" aria-pressed="${node.getAttr?.('elUpper') ? 'true' : 'false'}" title="${escapeHtml(t('prop_uppercase', 'UPPERCASE'))}">AA</button>
            </div>
            <div class="wn-prop-section">
                <div class="wn-prop-section-label">${escapeHtml(t('prop_spacing', 'Spacing'))}</div>
                ${isCurvedText(node) ? '' : `<div class="wn-prop-row">
                    <label>${escapeHtml(t('prop_line_height', 'Lines'))}</label>
                    <input type="range" id="wnPropLineH" min="80" max="220" step="5" value="${Math.round(textLineHeight(node) * 100)}">
                    <output class="wn-prop-out" id="wnPropLineHOut">${textLineHeight(node).toFixed(2)}</output>
                </div>`}
                <div class="wn-prop-row">
                    <label>${escapeHtml(t('prop_letter_spacing', 'Letters'))}</label>
                    <input type="range" id="wnPropLetter" min="-10" max="60" value="${Math.round(node.letterSpacing?.() || 0)}">
                    <output class="wn-prop-out" id="wnPropLetterOut">${Math.round(node.letterSpacing?.() || 0)}</output>
                </div>
                <div class="wn-prop-row">
                    <label>${escapeHtml(t('prop_curve', 'Arc'))}</label>
                    <input type="range" id="wnPropCurve" min="-100" max="100" step="5" value="${Math.round(textCurveAmount(node))}">
                    <output class="wn-prop-out" id="wnPropCurveOut">${Math.round(textCurveAmount(node))}</output>
                </div>
                <button type="button" class="wn-btn wn-btn-sm wn-btn-wide" id="wnPropAutofit">${escapeHtml(t('prop_autofit', 'Fit text to the box'))}</button>
            </div>
        `;
    }

    function imageFieldsHTML(node) {
        return `
            <div class="wn-prop-actions">
                <button type="button" class="wn-btn" id="wnPropFlipH">↔</button>
                <button type="button" class="wn-btn" id="wnPropFlipV">↕</button>
                <button type="button" class="wn-btn" id="wnPropReplace">${t('prop_replace_image', 'Replace')}</button>
            </div>
        `;
    }

    function colorFieldsHTML(node) {
        const isDrawing = node.getAttr('elType') === 'drawing';
        const fill = (node.fill && node.fill()) || '#5cb3ff';
        const hasStroke = (node.strokeWidth?.() || 0) > 0;
        const strokeCol = node.stroke?.() || '#111111';
        const canRound = node.getAttr('elShape') === 'rect';
        if (isDrawing) {
            return `
                ${colorRowHTML('prop_color', 'Color', 'wnPropStrokeColor', strokeCol)}
                <div class="wn-prop-row">
                    <label>${escapeHtml(t('prop_thickness', 'Thickness'))}</label>
                    <input type="range" id="wnPropStrokeW" min="1" max="80" value="${Math.round(node.strokeWidth() || 6)}">
                    <output class="wn-prop-out" id="wnPropStrokeWOut">${Math.round(node.strokeWidth() || 6)}</output>
                </div>
            `;
        }
        return `
            ${colorRowHTML('prop_fill', 'Fill', 'wnPropFill', fill)}
            <div class="wn-prop-row">
                <label class="wn-check">
                    <input type="checkbox" id="wnPropStrokeOn" ${hasStroke ? 'checked' : ''}>
                    <span>${escapeHtml(t('prop_outline', 'Outline'))}</span>
                </label>
            </div>
            <div class="wn-prop-row" id="wnPropStrokeRow" ${hasStroke ? '' : 'hidden'}>
                <input type="color" id="wnPropStrokeColor" value="${strokeCol}">
                <input type="range" id="wnPropStrokeW" min="1" max="80" value="${Math.max(1, Math.round(node.strokeWidth() || 8))}">
                <output class="wn-prop-out" id="wnPropStrokeWOut">${Math.max(1, Math.round(node.strokeWidth() || 8))}</output>
            </div>
            ${canRound ? `
            <div class="wn-prop-row">
                <label>${escapeHtml(t('prop_corners', 'Corners'))}</label>
                <input type="range" id="wnPropCorner" min="0" max="50" value="${Math.round(node.getAttr('elRadiusPct') || 0)}">
                <output class="wn-prop-out" id="wnPropCornerOut">${Math.round(node.getAttr('elRadiusPct') || 0)}%</output>
            </div>` : ''}
        `;
    }

    // ─── Photo framing, filters and borders ───────────────────────────
    /* A wall newspaper is mostly photographs, and the panel offered exactly
       three things for one: flip, flip, replace. Everything below is the set a
       teacher actually reaches for — put the class photo in a circle, crop the
       corridor out of it, make the old scan black-and-white, give it a border. */

    const PHOTO_ASPECTS = [
        { key: 'free',  r: null },
        { key: '1_1',   r: 1 },
        { key: '4_3',   r: 4 / 3 },
        { key: '3_4',   r: 3 / 4 },
        { key: '16_9',  r: 16 / 9 },
    ];

    /**
     * Recompute the source rectangle so the photo FILLS its frame without
     * distortion, honouring a pan offset the user can drag.
     *
     * Konva scales `image` to `width`/`height` whatever their aspect, so
     * changing a frame to a square used to squash faces. `crop` picks the
     * largest correctly-proportioned window of the source instead.
     *
     * `zoom` ≥ 1 magnifies inside the frame; `ox`/`oy` in −1..1 slide the
     * window, which is the "reposition inside the frame" gesture.
     */
    function applyPhotoCrop(node) {
        const img = node.image();
        if (!img || !img.naturalWidth) return;
        const srcW = img.naturalWidth;
        const srcH = img.naturalHeight;
        const boxW = Math.max(1, node.width());
        const boxH = Math.max(1, node.height());
        const zoom = Math.max(1, node.getAttr('elPhotoZoom') || 1);
        const ox = Math.max(-1, Math.min(1, node.getAttr('elPhotoOX') ?? 0));
        const oy = Math.max(-1, Math.min(1, node.getAttr('elPhotoOY') ?? 0));

        const boxRatio = boxW / boxH;
        let cw = srcW, ch = srcW / boxRatio;
        if (ch > srcH) { ch = srcH; cw = srcH * boxRatio; }
        cw /= zoom; ch /= zoom;

        const freeX = srcW - cw;
        const freeY = srcH - ch;
        const cx = freeX * (0.5 + ox / 2);
        const cy = freeY * (0.5 + oy / 2);

        node.crop({
            x: Math.max(0, Math.min(freeX, cx)),
            y: Math.max(0, Math.min(freeY, cy)),
            width: cw,
            height: ch,
        });
    }

    /** Resize a photo's frame to an aspect ratio, keeping its area and centre. */
    function setPhotoAspect(node, ratio) {
        node.setAttr('elPhotoAspect', ratio);
        if (ratio) {
            const cx = node.x() + node.width() / 2;
            const cy = node.y() + node.height() / 2;
            const area = node.width() * node.height();
            const w = Math.sqrt(area * ratio);
            const h = w / ratio;
            node.width(w); node.height(h);
            node.x(cx - w / 2); node.y(cy - h / 2);
        }
        applyPhotoCrop(node);
        refreshPhotoRender(node);
    }

    /**
     * Konva filters only run on a cached node, and the cache is a bitmap: on a
     * 2400 px photo an un-capped cache is ~23 MB per image. It is capped here
     * and dropped entirely when no filter is active, so an unfiltered board
     * costs nothing.
     */
    function refreshPhotoRender(node) {
        const Konva = window.Konva;
        const bw = node.getAttr('elBW') === true;
        const sepia = node.getAttr('elSepia') === true;
        const bright = node.getAttr('elBright') || 0;      // -1..1
        const contrast = node.getAttr('elContrast') || 0;  // -100..100
        const sat = node.getAttr('elSat');                 // -2..10, default 0

        const wanted = [];
        if (bw) wanted.push(Konva.Filters.Grayscale);
        if (sepia) wanted.push(Konva.Filters.Sepia);
        if (bright) wanted.push(Konva.Filters.Brighten);
        if (contrast) wanted.push(Konva.Filters.Contrast);
        if (sat) wanted.push(Konva.Filters.HSL);

        node.filters(wanted);
        node.brightness(bright);
        node.contrast(contrast);
        node.saturation(sat || 0);

        if (wanted.length) {
            const w = Math.max(1, node.width());
            const h = Math.max(1, node.height());
            // Cap the cache at ~2 MP: past that the filter cost is invisible on
            // a printed wall poster and the memory is not.
            const ratio = Math.min(1, Math.sqrt(2_000_000 / Math.max(1, w * h)));
            node.cache({ pixelRatio: ratio });
        } else if (node.isCached()) {
            node.clearCache();
        }
        state.konva?.layers.content.batchDraw();
    }

    function photoFieldsHTML(node) {
        const radiusPct = Math.round((node.getAttr('elRadiusPct') || 0));
        const zoom = Math.round(((node.getAttr('elPhotoZoom') || 1) - 1) * 100);
        const curAspect = node.getAttr('elPhotoAspect') || null;
        const aspectBtns = PHOTO_ASPECTS.map((a) => {
            const on = (a.r === null && !curAspect) || (a.r && curAspect && Math.abs(a.r - curAspect) < 0.001);
            return `<button type="button" class="wn-chip${on ? ' is-active' : ''}" data-aspect="${a.r === null ? '' : a.r}">${escapeHtml(t('aspect_' + a.key, a.key))}</button>`;
        }).join('');
        const border = node.stroke() && node.strokeWidth() > 0;
        return `
            <div class="wn-prop-section">
                <div class="wn-prop-section-label">${escapeHtml(t('prop_frame', 'Frame'))}</div>
                <div class="wn-chip-row" id="wnPhotoAspects">${aspectBtns}</div>
                <div class="wn-prop-row">
                    <label>${escapeHtml(t('prop_corners', 'Corners'))}</label>
                    <input type="range" id="wnPhotoRadius" min="0" max="50" value="${radiusPct}">
                    <output class="wn-prop-out" id="wnPhotoRadiusOut">${radiusPct === 50 ? '●' : radiusPct + '%'}</output>
                </div>
                <div class="wn-prop-row">
                    <label>${escapeHtml(t('prop_photo_zoom', 'Zoom in'))}</label>
                    <input type="range" id="wnPhotoZoom" min="0" max="200" value="${zoom}">
                    <output class="wn-prop-out" id="wnPhotoZoomOut">${100 + zoom}%</output>
                </div>
                <div class="wn-prop-hint">${escapeHtml(t('prop_photo_pan_hint', 'Drag inside the frame with Alt to reposition.'))}</div>
            </div>
            <div class="wn-prop-section">
                <div class="wn-prop-section-label">${escapeHtml(t('prop_look', 'Look'))}</div>
                <div class="wn-chip-row">
                    <button type="button" class="wn-chip${node.getAttr('elBW') ? ' is-active' : ''}" id="wnPhotoBW">${escapeHtml(t('filter_bw', 'B&W'))}</button>
                    <button type="button" class="wn-chip${node.getAttr('elSepia') ? ' is-active' : ''}" id="wnPhotoSepia">${escapeHtml(t('filter_sepia', 'Sepia'))}</button>
                    <button type="button" class="wn-chip${border ? ' is-active' : ''}" id="wnPhotoBorder">${escapeHtml(t('prop_border', 'Border'))}</button>
                </div>
                <div class="wn-prop-row">
                    <label>${escapeHtml(t('prop_brightness', 'Brightness'))}</label>
                    <input type="range" id="wnPhotoBright" min="-50" max="50" value="${Math.round((node.getAttr('elBright') || 0) * 100)}">
                    <output class="wn-prop-out" id="wnPhotoBrightOut">${Math.round((node.getAttr('elBright') || 0) * 100)}</output>
                </div>
                <div class="wn-prop-row">
                    <label>${escapeHtml(t('prop_contrast', 'Contrast'))}</label>
                    <input type="range" id="wnPhotoContrast" min="-50" max="50" value="${Math.round(node.getAttr('elContrast') || 0)}">
                    <output class="wn-prop-out" id="wnPhotoContrastOut">${Math.round(node.getAttr('elContrast') || 0)}</output>
                </div>
                <div class="wn-prop-row" id="wnPhotoBorderRow" ${border ? '' : 'hidden'}>
                    <label>${escapeHtml(t('prop_border_color', 'Border'))}</label>
                    <input type="color" id="wnPhotoBorderColor" value="${node.stroke() || '#ffffff'}">
                    <input type="range" id="wnPhotoBorderW" min="1" max="60" value="${Math.max(1, Math.round(node.strokeWidth() || 12))}">
                </div>
            </div>
            <div class="wn-prop-actions">
                <button type="button" class="wn-btn" id="wnPropFlipH" title="${escapeHtml(t('prop_flip_h', 'Flip horizontally'))}">↔</button>
                <button type="button" class="wn-btn" id="wnPropFlipV" title="${escapeHtml(t('prop_flip_v', 'Flip vertically'))}">↕</button>
                <button type="button" class="wn-btn" id="wnPropReplace">${escapeHtml(t('prop_replace_image', 'Replace'))}</button>
            </div>
        `;
    }

    function wirePhotoProps(node) {
        const redraw = (commit) => {
            applyPhotoCrop(node);
            refreshPhotoRender(node);
            if (commit) { markDirty(); pushHistory(); }
        };

        $$('#wnPhotoAspects [data-aspect]').forEach((b) => {
            b.onclick = () => {
                const v = b.dataset.aspect ? parseFloat(b.dataset.aspect) : null;
                setPhotoAspect(node, v);
                markDirty(); pushHistory();
                renderProperties();
            };
        });

        const radius = $('wnPhotoRadius');
        if (radius) {
            const out = $('wnPhotoRadiusOut');
            const applyRadius = () => {
                const pct = parseFloat(radius.value);
                node.setAttr('elRadiusPct', pct);
                node.cornerRadius(Math.min(node.width(), node.height()) * (pct / 100));
                if (out) out.textContent = pct === 50 ? '●' : pct + '%';
                state.konva.layers.content.batchDraw();
            };
            radius.oninput = applyRadius;
            radius.onchange = () => { applyRadius(); markDirty(); pushHistory(); };
        }

        const zoomIn = $('wnPhotoZoom');
        if (zoomIn) {
            const out = $('wnPhotoZoomOut');
            zoomIn.oninput = () => {
                node.setAttr('elPhotoZoom', 1 + parseFloat(zoomIn.value) / 100);
                if (out) out.textContent = (100 + parseFloat(zoomIn.value)) + '%';
                redraw(false);
            };
            zoomIn.onchange = () => redraw(true);
        }

        const bw = $('wnPhotoBW');
        if (bw) bw.onclick = () => {
            node.setAttr('elBW', !node.getAttr('elBW'));
            if (node.getAttr('elBW')) node.setAttr('elSepia', false);
            redraw(true); renderProperties();
        };
        const sep = $('wnPhotoSepia');
        if (sep) sep.onclick = () => {
            node.setAttr('elSepia', !node.getAttr('elSepia'));
            if (node.getAttr('elSepia')) node.setAttr('elBW', false);
            redraw(true); renderProperties();
        };

        const bright = $('wnPhotoBright');
        if (bright) {
            const out = $('wnPhotoBrightOut');
            bright.oninput = () => {
                node.setAttr('elBright', parseFloat(bright.value) / 100);
                if (out) out.textContent = bright.value;
                refreshPhotoRender(node);
            };
            bright.onchange = () => { markDirty(); pushHistory(); };
        }
        const contrast = $('wnPhotoContrast');
        if (contrast) {
            const out = $('wnPhotoContrastOut');
            contrast.oninput = () => {
                node.setAttr('elContrast', parseFloat(contrast.value));
                if (out) out.textContent = contrast.value;
                refreshPhotoRender(node);
            };
            contrast.onchange = () => { markDirty(); pushHistory(); };
        }

        const borderBtn = $('wnPhotoBorder');
        if (borderBtn) borderBtn.onclick = () => {
            const on = node.strokeWidth() > 0 && node.stroke();
            if (on) { node.strokeWidth(0); }
            else {
                node.stroke(node.stroke() || '#ffffff');
                node.strokeWidth(Math.max(8, Math.round(Math.min(node.width(), node.height()) * 0.03)));
                // A white photo border only reads against the board when it has
                // an edge of its own, the way a printed photo corner does.
                node.strokeScaleEnabled(false);
            }
            markDirty(); pushHistory(); renderProperties();
            state.konva.layers.content.batchDraw();
        };
        const bc = $('wnPhotoBorderColor');
        if (bc) bc.oninput = () => { node.stroke(bc.value); state.konva.layers.content.batchDraw(); };
        if (bc) bc.onchange = () => { markDirty(); pushHistory(); };
        const bwid = $('wnPhotoBorderW');
        if (bwid) bwid.oninput = () => { node.strokeWidth(parseFloat(bwid.value)); state.konva.layers.content.batchDraw(); };
        if (bwid) bwid.onchange = () => { markDirty(); pushHistory(); };

        wireImageProps(node);   // flip + replace, unchanged
    }


    /** Is this a heading that has been bent into an arc? */
    function isCurvedText(node) {
        return node?.getClassName?.() === 'TextPath';
    }

    /**
     * The width and height the panel should SHOW.
     *
     * A Konva.TextPath has no width or height of its own — the path carries
     * both — so asking it reported 0.0 cm for a heading that plainly occupies
     * space. For a curved heading the meaningful width is the span of its arc
     * (which the user can change), and the meaningful height is whatever the
     * glyphs ended up covering (which they cannot).
     */
    function nodeBoxW(node) {
        if (isCurvedText(node)) return node.getAttr('elTextWidth') || 0;
        return node.width() * (node.scaleX() || 1);
    }

    function nodeBoxH(node) {
        if (isCurvedText(node)) {
            const r = node.getClientRect({ relativeTo: state.konva.layers.content, skipShadow: true });
            return r.height;
        }
        return node.height() * (node.scaleY() || 1);
    }

    // ─── Curved text ──────────────────────────────────────────────────
    /**
     * Bend a heading into an arc.
     *
     * An arched masthead is the single most recognisable thing about a hand-made
     * wall newspaper, and it is the one text effect the benchmark editors treat
     * as table stakes (a simple curve — not text following an arbitrary path,
     * which nobody ships).
     *
     * Konva draws curved text with a DIFFERENT node class, Konva.TextPath, so
     * turning the curve on and off swaps the node. Everything that identifies
     * the element — its id, its place in the stack, its selection — is carried
     * across, because from the user's point of view nothing was replaced: they
     * moved a slider.
     */
    function arcPath(width, curve) {
        // `curve` is -100..100: how far the middle of the line rises above (or
        // sinks below) its ends, as a percentage of half the width.
        const w = Math.max(20, width);
        const sag = (curve / 100) * (w / 2);
        if (Math.abs(sag) < 0.5) return `M0,0 L${w},0`;
        // A quadratic whose apex is `sag` away puts the control point at 2×sag.
        return `M0,0 Q${w / 2},${-2 * sag} ${w},0`;
    }

    function textCurveAmount(node) {
        return node?.getAttr('elCurve') || 0;
    }

    /**
     * Copy the attributes that make a text element itself between the two node
     * classes. TextPath has no width/align of its own — the path carries both —
     * so the width is remembered as an attribute for when the curve is removed.
     */
    function textCommonAttrs(node) {
        return {
            elId: node.getAttr('elId'),
            elType: 'text',
            elWordart: node.getAttr('elWordart') || 'none',
            elBaseFill: node.getAttr('elBaseFill') || node.fill() || '#111111',
            elUpper: node.getAttr('elUpper') === true,
            elTextOriginal: node.getAttr('elTextOriginal') || null,
            elLocked: node.getAttr('elLocked') === true,
            elTextWidth: node.getAttr('elTextWidth') || (node.width ? node.width() : 0) || 0,
            text: node.text(),
            fontSize: node.fontSize(),
            fontFamily: node.fontFamily(),
            fontStyle: node.fontStyle(),
            fill: node.fill(),
            stroke: node.stroke ? node.stroke() : undefined,
            strokeWidth: node.strokeWidth ? node.strokeWidth() : 0,
            letterSpacing: node.letterSpacing ? node.letterSpacing() : 0,
            x: node.x(), y: node.y(),
            rotation: node.rotation(), opacity: node.opacity(),
            scaleX: node.scaleX(), scaleY: node.scaleY(),
            align: node.align ? node.align() : 'left',
            lineHeight: textLineHeight(node),
        };
    }

    function setTextCurve(node, curve) {
        const Konva = window.Konva;
        const layer = state.konva.layers.content;
        const a = textCommonAttrs(node);
        const wasSelected = selectedNodes().includes(node);
        const z = node.zIndex();
        const curved = Math.abs(curve) >= 1;
        const isPath = node.getClassName() === 'TextPath';
        if (curved === isPath) {
            // Same class — just reshape the existing path.
            if (isPath) { node.data(arcPath(a.elTextWidth, curve)); node.setAttr('elCurve', curve); layer.batchDraw(); }
            return node;
        }

        let next;
        if (curved) {
            next = new Konva.TextPath({
                x: a.x, y: a.y, data: arcPath(a.elTextWidth, curve),
                text: a.text, fontSize: a.fontSize, fontFamily: a.fontFamily,
                fontStyle: a.fontStyle, fill: a.fill,
                letterSpacing: a.letterSpacing, align: a.align,
                draggable: true,
            });
        } else {
            next = new Konva.Text({
                x: a.x, y: a.y, text: a.text, fontSize: a.fontSize,
                fontFamily: a.fontFamily, fontStyle: a.fontStyle, fill: a.fill,
                align: a.align, lineHeight: a.lineHeight, padding: 4,
                letterSpacing: a.letterSpacing,
                width: a.elTextWidth || undefined,
                draggable: true,
            });
        }
        if (a.stroke) next.stroke(a.stroke);
        if (a.strokeWidth) next.strokeWidth(a.strokeWidth);
        next.rotation(a.rotation);
        next.opacity(a.opacity);
        next.scaleX(a.scaleX);
        next.scaleY(a.scaleY);

        tagNode(next, { id: a.elId, type: 'text' });
        next.setAttr('elWordart', a.elWordart);
        next.setAttr('elBaseFill', a.elBaseFill);
        next.setAttr('elUpper', a.elUpper);
        next.setAttr('elTextOriginal', a.elTextOriginal);
        next.setAttr('elTextWidth', a.elTextWidth);
        next.setAttr('elCurve', curved ? curve : 0);
        if (curved) next.setAttr('elLineHeight', a.lineHeight);   // see textLineHeight
        attachNodeInteractions(next);
        next.on('dblclick dbltap', () => startTextEdit(next));

        layer.add(next);
        next.zIndex(Math.min(z, layer.children.length - 1));
        node.destroy();
        if (a.elLocked) setNodeLocked(next, true);
        if (wasSelected) state.konva.tr.nodes([next]);
        layer.batchDraw();
        return next;
    }

    // ─── Eyedropper ───────────────────────────────────────────────────
    /**
     * Pick a colour off the board.
     *
     * The obvious use is the one this genre is made of: match the heading to
     * the blue in the class photo, or the frame to the ribbon in the picture.
     *
     * Two implementations, and the second is the point. The native
     * `EyeDropper` API exists only in Chromium — Canva's eyedropper is
     * Chrome/Edge-only for exactly this reason — so Safari and Firefox get a
     * canvas sampler instead: the stage is already a canvas, and reading one
     * pixel out of it needs no API at all. The only thing the native version
     * adds is the ability to sample outside the page, which is not what anyone
     * is doing here.
     */
    let _pickingColor = null;

    function eyedropperAvailable() { return true; }

    function startColorPick(onPick) {
        if (_pickingColor) cancelColorPick();

        if (window.EyeDropper) {
            const ed = new window.EyeDropper();
            ed.open()
                .then((r) => { if (r && r.sRGBHex) onPick(r.sRGBHex); })
                .catch(() => { /* the user pressed Escape */ });
            ae('eyedropper', { impl: 'native' });
            return;
        }

        const host = $('wnStage');
        if (!host) return;
        document.body.classList.add('wn-picking');
        toast(t('pick_color_hint', 'Click anywhere on the board to take its colour'), 'info');

        const finish = (hex) => {
            cancelColorPick();
            if (hex) onPick(hex);
        };
        const onClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            const stage = state.konva?.stage;
            if (!stage) return finish(null);
            // Sample the composited stage rather than any one layer, so the
            // colour picked is the colour seen — background, photo and every
            // shape stacked on top of it.
            const r = host.getBoundingClientRect();
            const x = Math.round(e.clientX - r.left);
            const y = Math.round(e.clientY - r.top);
            let hex = null;
            try {
                const c = stage.toCanvas({ x, y, width: 1, height: 1, pixelRatio: 1 });
                const d = c.getContext('2d').getImageData(0, 0, 1, 1).data;
                hex = '#' + [d[0], d[1], d[2]].map((v) => v.toString(16).padStart(2, '0')).join('');
            } catch (err) { /* tainted or out of bounds — treated as a cancel */ }
            finish(hex);
        };
        const onKey = (e) => { if (e.key === 'Escape') finish(null); };

        _pickingColor = { host, onClick, onKey };
        host.addEventListener('click', onClick, true);
        window.addEventListener('keydown', onKey, true);
        ae('eyedropper', { impl: 'canvas' });
    }

    function cancelColorPick() {
        if (!_pickingColor) return;
        const { host, onClick, onKey } = _pickingColor;
        host.removeEventListener('click', onClick, true);
        window.removeEventListener('keydown', onKey, true);
        document.body.classList.remove('wn-picking');
        _pickingColor = null;
    }

    /** Attach a pick button that writes into a colour <input> and fires it. */
    function wireEyedropper(buttonId, inputId, after) {
        const btn = $(buttonId);
        const input = $(inputId);
        if (!btn || !input) return;
        btn.onclick = (e) => {
            e.preventDefault();
            startColorPick((hex) => {
                input.value = hex;
                input.dispatchEvent(new Event('input', { bubbles: true }));
                input.dispatchEvent(new Event('change', { bubbles: true }));
                if (after) after(hex);
            });
        };
    }

    /** Markup for a colour row with a pick button next to it. */
    function colorRowHTML(labelKey, labelFallback, inputId, value) {
        return `<div class="wn-prop-row">
            <label>${escapeHtml(t(labelKey, labelFallback))}</label>
            <span class="wn-color-pair">
                <input type="color" id="${inputId}" value="${value}">
                <button type="button" class="wn-pick-btn" id="${inputId}Pick" title="${escapeHtml(t('pick_color', 'Take a colour from the board'))}" aria-label="${escapeHtml(t('pick_color', 'Take a colour from the board'))}">
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M11 2.5l2.5 2.5M12 1.5l2.5 2.5-7 7-3.5 1 1-3.5z"/><path d="M3 13.5h4"/></svg>
                </button>
            </span>
        </div>`;
    }

    // ─── Arrange ──────────────────────────────────────────────────────
    /* Alignment used to need two or more elements, because it could only align
       things to EACH OTHER. The commonest request in a page layout is the one
       that needs exactly one: put this heading in the middle of the sheet. So
       there are two targets — the sheet you are working on, and the selection
       itself — and the first works with a single element. */

    const ALIGN_ICONS = {
        left:   '<svg viewBox="0 0 16 16"><path d="M2 1v14M4 4h9v3H4zM4 9h6v3H4z"/></svg>',
        'center-h': '<svg viewBox="0 0 16 16"><path d="M8 1v14M3.5 4h9v3h-9zM5 9h6v3H5z"/></svg>',
        right:  '<svg viewBox="0 0 16 16"><path d="M14 1v14M3 4h9v3H3zM6 9h6v3H6z"/></svg>',
        top:    '<svg viewBox="0 0 16 16"><path d="M1 2h14M4 4v9h3V4zM9 4v6h3V4z"/></svg>',
        middle: '<svg viewBox="0 0 16 16"><path d="M1 8h14M4 3.5v9h3v-9zM9 5v6h3V5z"/></svg>',
        bottom: '<svg viewBox="0 0 16 16"><path d="M1 14h14M4 3h3v9H4zM9 6h3v6H9z"/></svg>',
    };

    function alignmentHTML(count) {
        const btn = (mode, key) =>
            `<button type="button" class="wn-align-btn" data-align="${mode}" title="${escapeHtml(t(key, mode))}" aria-label="${escapeHtml(t(key, mode))}">${ALIGN_ICONS[mode]}</button>`;
        const many = count > 1;
        return `
            <div class="wn-prop-section">
                <div class="wn-prop-section-label">${escapeHtml(t('prop_arrange', 'Arrange'))}</div>
                ${many ? `
                <div class="wn-seg wn-align-target" role="radiogroup" aria-label="${escapeHtml(t('align_target', 'Align to'))}">
                    <button type="button" class="wn-seg-btn is-active" data-target="sheet">${escapeHtml(t('align_to_sheet', 'The sheet'))}</button>
                    <button type="button" class="wn-seg-btn" data-target="selection">${escapeHtml(t('align_to_selection', 'Each other'))}</button>
                </div>` : `<div class="wn-prop-hint">${escapeHtml(t('align_to_sheet_hint', 'Aligns to the sheet you are working on.'))}</div>`}
                <div class="wn-align-row">
                    ${btn('left', 'align_left')}${btn('center-h', 'align_center')}${btn('right', 'align_right')}
                    ${btn('top', 'align_top')}${btn('middle', 'align_middle')}${btn('bottom', 'align_bottom')}
                </div>
                ${count > 2 ? `
                <div class="wn-prop-actions">
                    <button type="button" class="wn-btn wn-btn-sm" data-distribute="h">${escapeHtml(t('distribute_h', 'Even spacing across'))}</button>
                    <button type="button" class="wn-btn wn-btn-sm" data-distribute="v">${escapeHtml(t('distribute_v', 'Even spacing down'))}</button>
                </div>` : ''}
            </div>
        `;
    }

    let _alignTarget = 'sheet';

    function wireAlignment(nodes) {
        $$('.wn-align-target [data-target]').forEach((b) => {
            b.onclick = () => {
                _alignTarget = b.dataset.target;
                $$('.wn-align-target [data-target]').forEach((x) => {
                    const on = x === b;
                    x.classList.toggle('is-active', on);
                    x.setAttribute('aria-pressed', on ? 'true' : 'false');
                });
            };
        });
        $$('[data-align]').forEach((btn) => {
            btn.onclick = () => alignNodes(nodes, btn.dataset.align);
        });
        $$('[data-distribute]').forEach((btn) => {
            btn.onclick = () => distributeNodes(nodes, btn.dataset.distribute);
        });
    }

    /** Bounding boxes in board space — what alignment actually moves. */
    function alignBoxes(nodes) {
        const layer = state.konva.layers.content;
        return nodes.map((n) => {
            const r = n.getClientRect({ relativeTo: layer, skipShadow: true });
            return { node: n, x: r.x, y: r.y, w: r.width, h: r.height };
        });
    }

    /** The rectangle alignment is measured against. */
    function alignFrame(nodes) {
        const single = nodes.length < 2;
        if (single || _alignTarget === 'sheet') {
            const L = boardLayout();
            // The sheet the SELECTION is on, not whichever one the page nav
            // last pointed at — otherwise "centre this" sends the element to a
            // different sheet than the one it is sitting on.
            const b = alignBoxes(nodes);
            const cx = b.reduce((a, x) => a + x.x + x.w / 2, 0) / b.length;
            const cy = b.reduce((a, x) => a + x.y + x.h / 2, 0) / b.length;
            let best = null;
            for (let r = 0; r < L.rows; r++) {
                for (let c = 0; c < L.cols; c++) {
                    if (!L.pageExists(c, r)) continue;
                    const t0 = L.tileRectPX(c, r);
                    const d = Math.hypot(cx - (t0.x + t0.w / 2), cy - (t0.y + t0.h / 2));
                    if (!best || d < best.d) best = { d, rect: t0 };
                }
            }
            if (best) return { x: best.rect.x, y: best.rect.y, w: best.rect.w, h: best.rect.h };
        }
        const b = alignBoxes(nodes);
        const x1 = Math.min(...b.map((v) => v.x));
        const y1 = Math.min(...b.map((v) => v.y));
        const x2 = Math.max(...b.map((v) => v.x + v.w));
        const y2 = Math.max(...b.map((v) => v.y + v.h));
        return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
    }

    function alignNodes(nodes, mode) {
        if (!nodes || !nodes.length) return;
        const f = alignFrame(nodes);
        for (const b of alignBoxes(nodes)) {
            // Move by the DELTA of the bounding box, not by setting x/y: a
            // rotated element's box does not start at its own x/y, and a Konva
            // group's origin is somewhere else entirely.
            let dx = 0, dy = 0;
            if      (mode === 'left')     dx = f.x - b.x;
            else if (mode === 'right')    dx = (f.x + f.w) - (b.x + b.w);
            else if (mode === 'center-h') dx = (f.x + f.w / 2) - (b.x + b.w / 2);
            else if (mode === 'top')      dy = f.y - b.y;
            else if (mode === 'bottom')   dy = (f.y + f.h) - (b.y + b.h);
            else if (mode === 'middle')   dy = (f.y + f.h / 2) - (b.y + b.h / 2);
            if (dx) b.node.x(b.node.x() + dx);
            if (dy) b.node.y(b.node.y() + dy);
        }
        state.konva.layers.content.batchDraw();
        renderProperties();
        renderLayers();
        markDirty();
        pushHistory();
        ae('align', { mode, n: nodes.length, target: nodes.length < 2 ? 'sheet' : _alignTarget });
    }

    /**
     * Equal GAPS, not equal centres.
     *
     * Spacing three photos of different widths by their centres leaves visibly
     * uneven white between them, which is the thing the user was trying to fix.
     */
    function distributeNodes(nodes, axis) {
        if (!nodes || nodes.length < 3) return;
        const b = alignBoxes(nodes).sort((p1, p2) => (axis === 'h' ? p1.x - p2.x : p1.y - p2.y));
        const first = b[0], last = b[b.length - 1];
        const span = axis === 'h'
            ? (last.x + last.w) - first.x
            : (last.y + last.h) - first.y;
        const used = b.reduce((a, v) => a + (axis === 'h' ? v.w : v.h), 0);
        const gap = (span - used) / (b.length - 1);
        let cursor = axis === 'h' ? first.x : first.y;
        for (const v of b) {
            const delta = cursor - (axis === 'h' ? v.x : v.y);
            if (axis === 'h') v.node.x(v.node.x() + delta);
            else v.node.y(v.node.y() + delta);
            cursor += (axis === 'h' ? v.w : v.h) + gap;
        }
        state.konva.layers.content.batchDraw();
        renderProperties();
        renderLayers();
        markDirty();
        pushHistory();
        ae('distribute', { axis, n: nodes.length });
    }

    function commonActionsHTML() {
        return `
            <div class="wn-prop-actions">
                <button type="button" class="wn-btn" id="wnPropDup">${t('prop_duplicate', 'Duplicate')}</button>
                <button type="button" class="wn-btn" id="wnPropToFront" title="${t('prop_to_front', 'Bring to front')}">▲</button>
                <button type="button" class="wn-btn" id="wnPropToBack" title="${t('prop_to_back', 'Send to back')}">▼</button>
                <button type="button" class="wn-btn" id="wnPropDel" title="${t('btn_delete', 'Delete')}">🗑️</button>
            </div>
        `;
    }

    function wireCommonProps(node) {
        const setNum = (id, fn) => {
            const el = $(id);
            if (!el) return;
            el.addEventListener('change', () => {
                const v = parseFloat(el.value);
                if (Number.isFinite(v)) {
                    fn(v);
                    state.konva.layers.content.batchDraw();
                    markDirty();
                    pushHistory();
                }
            });
        };
        // The four boxes are centimetres of paper; the node speaks 300-dpi px.
        setNum('wnPropX',   (v) => node.x(cmToPx(v)));
        setNum('wnPropY',   (v) => node.y(cmToPx(v)));
        // Resizing a node that carries a scale has to go through the scale, not
        // through width(): a Konva.Text scaled to 2× and then given width(500)
        // ends up 1000 wide, i.e. the number you typed is not the number you get.
        setNum('wnPropW',   (v) => {
            const target = cmToPx(v);
            if (isCurvedText(node)) {
                // Widening a curved heading means widening its arc.
                node.setAttr('elTextWidth', target);
                node.data(arcPath(target, node.getAttr('elCurve') || 0));
                return;
            }
            const base = node.width() || 1;
            if (node.getAttr('elType') === 'text') { node.scaleX(1); node.width(target); }
            else node.scaleX(target / base);
        });
        setNum('wnPropH',   (v) => {
            if (isCurvedText(node)) return;   // the arc and the font set it
            const target = cmToPx(v);
            const base = node.height() || 1;
            if (node.getAttr('elType') === 'text') { node.scaleY(1); node.height(target); }
            else node.scaleY(target / base);
        });
        setNum('wnPropRot', (v) => node.rotation(v));

        const rot = $('wnPropRot');
        const rotOut = $('wnPropRotOut');
        if (rot && rotOut) rot.addEventListener('input', () => {
            rotOut.textContent = Math.round(parseFloat(rot.value)) + '°';
            node.rotation(parseFloat(rot.value));
            state.konva.layers.content.batchDraw();
            positionElToolbar();
        });

        const op = $('wnPropOp');
        const opOut = $('wnPropOpOut');
        if (op) op.addEventListener('input', () => {
            node.opacity(parseFloat(op.value) / 100);
            if (opOut) opOut.textContent = Math.round(parseFloat(op.value)) + '%';
            state.konva.layers.content.batchDraw();
            // pushHistory only on change (not input) — too noisy on slider drag
        });
        if (op) op.addEventListener('change', () => { markDirty(); pushHistory(); });

        const lock = $('wnPropLock');
        if (lock) lock.addEventListener('change', () => {
            setNodeLocked(node, lock.checked);
            markDirty();
            pushHistory();
            renderLayers();
        });
    }

    /**
     * Lock an element in place.
     *
     * A wall newspaper is mostly a background and a heading that must not move
     * while photos are dragged around them; without a lock every stray click on
     * the background picks it up instead. The transformer has to be cleared too
     * — leaving a locked node selected would show resize handles that refuse to
     * do anything.
     */
    function setNodeLocked(node, locked) {
        node.setAttr('elLocked', !!locked);
        node.draggable(!locked);
        node.listening(!locked);
        if (locked && state.konva?.tr?.nodes().includes(node)) {
            state.konva.tr.nodes(state.konva.tr.nodes().filter((n) => n !== node));
            renderProperties();
        }
        state.konva?.layers.content.batchDraw();
    }

    function wireCommonActions(nodes) {
        const dup = $('wnPropDup'); if (dup) dup.onclick = () => duplicateNodes(nodes);
        const front = $('wnPropToFront'); if (front) front.onclick = () => { nodes.forEach((n) => n.moveToTop()); state.konva.tr.moveToTop(); state.konva.layers.content.batchDraw(); markDirty(); pushHistory(); renderLayers(); };
        const back = $('wnPropToBack'); if (back) back.onclick = () => { nodes.forEach((n) => n.moveToBottom()); state.konva.layers.content.batchDraw(); markDirty(); pushHistory(); renderLayers(); };
        const del = $('wnPropDel'); if (del) del.onclick = () => deleteNodes(nodes);
    }

    function wireTextProps(node) {
        $('wnPropColor').oninput = (e) => {
            const v = e.target.value;
            node.setAttr('elBaseFill', v);
            // If a wordart effect uses gradient (rainbow), keep gradient and
            // store base for re-application; otherwise apply directly.
            if ((node.getAttr('elWordart') || 'none') === 'rainbow') {
                // Re-apply to refresh stops
                applyWordart(node, 'rainbow');
            } else {
                node.fill(v);
                node.fillLinearGradientColorStops([]);
            }
            state.lastColor = v;
            state.konva.layers.content.batchDraw();
        };
        $('wnPropColor').onchange = () => { markDirty(); pushHistory(); };
        wireEyedropper('wnPropColorPick', 'wnPropColor');
        $('wnPropFontSize').onchange = (e) => { node.fontSize(parseFloat(e.target.value) || 56); state.konva.layers.content.batchDraw(); markDirty(); pushHistory(); };
        $('wnPropFont').onchange = async (e) => {
            const fam = e.target.value;
            // Make sure the woff2 face is loaded before re-rendering, else
            // Konva will rasterise the fallback font and snap to the new font
            // only on the next redraw — visible glitch.
            if (document.fonts && document.fonts.load) {
                try { await document.fonts.load(`${node.fontStyle?.() || 'normal'} ${node.fontSize()}px "${fam}"`); }
                catch (e) { /* noop */ }
            }
            node.fontFamily(fam);
            state.konva.layers.content.batchDraw();
            markDirty(); pushHistory();
        };
        // WordArt: visual button grid. Click activates the preset, only one
        // button highlighted at a time.
        const waGrid = $('wnPropWordartGrid');
        if (waGrid) {
            waGrid.addEventListener('click', (e) => {
                const btn = e.target.closest('[data-wa]');
                if (!btn) return;
                const v = btn.dataset.wa;
                waGrid.querySelectorAll('.wn-wordart-btn').forEach((b) => b.classList.toggle('is-active', b === btn));
                applyWordart(node, v);
                state.konva.layers.content.batchDraw();
                markDirty(); pushHistory();
            });
        }
        $('wnPropAlign').onchange = (e) => { node.align(e.target.value); state.konva.layers.content.batchDraw(); markDirty(); pushHistory(); };
        const bold = $('wnPropBold');
        const italic = $('wnPropItalic');
        bold.onclick = () => {
            const cur = node.fontStyle() || 'normal';
            const has = cur.includes('bold');
            const next = (has ? cur.replace('bold', '') : (cur + ' bold')).replace('normal', '').trim() || 'normal';
            node.fontStyle(next);
            bold.setAttribute('aria-pressed', has ? 'false' : 'true');
            state.konva.layers.content.batchDraw();
            markDirty(); pushHistory();
        };
        italic.onclick = () => {
            const cur = node.fontStyle() || 'normal';
            const has = cur.includes('italic');
            const next = (has ? cur.replace('italic', '') : (cur + ' italic')).replace('normal', '').trim() || 'normal';
            node.fontStyle(next);
            italic.setAttribute('aria-pressed', has ? 'false' : 'true');
            state.konva.layers.content.batchDraw();
            markDirty(); pushHistory();
        };

        const upper = $('wnPropUpper');
        if (upper) upper.onclick = () => {
            const on = !node.getAttr('elUpper');
            node.setAttr('elUpper', on);
            // Keep the original casing so the switch is reversible — uppercasing
            // in place would destroy it and "back" would come out as Title Case
            // or not at all.
            if (on) {
                node.setAttr('elTextOriginal', node.text());
                node.text(node.text().toLocaleUpperCase(window.__LOCALE || undefined));
            } else {
                node.text(node.getAttr('elTextOriginal') || node.text());
            }
            upper.setAttribute('aria-pressed', on ? 'true' : 'false');
            state.konva.layers.content.batchDraw();
            markDirty(); pushHistory();
        };

        const lineH = $('wnPropLineH');
        if (lineH) {
            const out = $('wnPropLineHOut');
            lineH.oninput = () => {
                const v = parseFloat(lineH.value) / 100;
                node.lineHeight(v);
                if (out) out.textContent = v.toFixed(2);
                state.konva.layers.content.batchDraw();
            };
            lineH.onchange = () => { markDirty(); pushHistory(); };
        }
        const letter = $('wnPropLetter');
        if (letter) {
            const out = $('wnPropLetterOut');
            letter.oninput = () => {
                node.letterSpacing(parseFloat(letter.value));
                if (out) out.textContent = letter.value;
                state.konva.layers.content.batchDraw();
            };
            letter.onchange = () => { markDirty(); pushHistory(); };
        }

        const curve = $('wnPropCurve');
        if (curve) {
            const out = $('wnPropCurveOut');
            // The swap happens on `change`, not on `input`: replacing the node
            // on every slider tick would destroy the very node the slider is
            // bound to, mid-drag.
            curve.oninput = () => { if (out) out.textContent = curve.value; };
            curve.onchange = () => {
                const v = parseFloat(curve.value);
                // Remember the box width before the first bend — TextPath has
                // none, and without it the arc would collapse to a default.
                if (!node.getAttr('elTextWidth') && node.width) node.setAttr('elTextWidth', node.width());
                const next = setTextCurve(node, v);
                markDirty();
                pushHistory();
                renderProperties();
                renderLayers();
                if (next) applyWordart(next, next.getAttr('elWordart') || 'none');
            };
        }

        const autofit = $('wnPropAutofit');
        if (autofit) autofit.onclick = () => { autoFitText(node); markDirty(); pushHistory(); renderProperties(); };
    }

    /**
     * Grow or shrink the font until the text fills its box.
     *
     * Canva documents this as absent — dragging a text box's side handles
     * "won't affect the text font size" — and it is exactly what goes wrong
     * when a teacher types 28 pupils' names into a box sized for six. Binary
     * search on font size, measured through Konva's own layout so wrapping is
     * accounted for.
     */
    function autoFitText(node) {
        const boxW = node.width();
        const boxH = node.height() || (node.fontSize() * 1.6);
        if (!boxW) return;
        let lo = 6, hi = 900, best = node.fontSize();
        for (let i = 0; i < 22 && hi - lo > 0.5; i++) {
            const mid = (lo + hi) / 2;
            node.fontSize(mid);
            // A Konva.Text with a fixed width reflows on fontSize change and
            // reports the laid-out height from height(), so wrapping is already
            // accounted for and no manual line counting is needed.
            if (node.height() <= boxH + 0.5) { best = mid; lo = mid; } else { hi = mid; }
        }
        node.fontSize(Math.max(6, best));
        state.konva.layers.content.batchDraw();
        ae('text_autofit', {});
    }

    function wireImageProps(node) {
        $('wnPropFlipH').onclick = () => {
            const sx = node.scaleX();
            node.scaleX(-sx);
            // Compensate position so the visual top-left stays put
            node.x(node.x() + (sx > 0 ? node.width() : -node.width()));
            state.konva.layers.content.batchDraw();
            markDirty(); pushHistory();
        };
        $('wnPropFlipV').onclick = () => {
            const sy = node.scaleY();
            node.scaleY(-sy);
            node.y(node.y() + (sy > 0 ? node.height() : -node.height()));
            state.konva.layers.content.batchDraw();
            markDirty(); pushHistory();
        };
        $('wnPropReplace').onclick = () => $('wnImageInput').click();
    }

    function wireColorProps(node) {
        const draw = () => state.konva.layers.content.batchDraw();

        const strokeOn = $('wnPropStrokeOn');
        if (strokeOn) strokeOn.onchange = () => {
            if (strokeOn.checked) {
                node.stroke(node.stroke() || '#111111');
                node.strokeWidth(Math.max(4, Math.round(Math.min(node.width?.() || 100, node.height?.() || 100) * 0.04)));
            } else node.strokeWidth(0);
            draw(); markDirty(); pushHistory(); renderProperties();
        };
        const sc = $('wnPropStrokeColor');
        if (sc) {
            sc.oninput = () => { node.stroke(sc.value); draw(); };
            sc.onchange = () => { markDirty(); pushHistory(); };
            wireEyedropper('wnPropStrokeColorPick', 'wnPropStrokeColor');
        }
        const sw = $('wnPropStrokeW');
        if (sw) {
            const out = $('wnPropStrokeWOut');
            sw.oninput = () => { node.strokeWidth(parseFloat(sw.value)); if (out) out.textContent = sw.value; draw(); };
            sw.onchange = () => { markDirty(); pushHistory(); };
        }
        const corner = $('wnPropCorner');
        if (corner) {
            const out = $('wnPropCornerOut');
            const apply = () => {
                const pct = parseFloat(corner.value);
                node.setAttr('elRadiusPct', pct);
                node.cornerRadius(Math.min(node.width(), node.height()) * (pct / 100));
                if (out) out.textContent = pct + '%';
                draw();
            };
            corner.oninput = apply;
            corner.onchange = () => { apply(); markDirty(); pushHistory(); };
        }

        if (!$('wnPropFill')) return;
        $('wnPropFill').oninput = (e) => {
            const v = e.target.value;
            // Fill only. It used to set the stroke to the same colour, which was
            // harmless while there was no outline control and destroys the
            // outline now that there is one.
            if (node.fill) node.fill(v);
            state.lastFill = v;
            state.konva.layers.content.batchDraw();
        };
        $('wnPropFill').onchange = () => { markDirty(); pushHistory(); };
        wireEyedropper('wnPropFillPick', 'wnPropFill');
    }

    // ─── Layers panel ─────────────────────────────────────────────────
    function renderLayers() {
        const root = $('wnLayers');
        if (!root) return;
        const items = state.konva.layers.content.children
            .filter((n) => n.hasName?.('wn-obj'))
            .slice() // make a copy
            .reverse(); // top-most first in UI
        if (items.length === 0) {
            root.innerHTML = `<div class="wn-rp-empty">${t('layers_empty', 'No elements yet.')}</div>`;
            return;
        }
        const selectedSet = new Set(selectedNodes());
        root.innerHTML = items.map((n, i) => {
            const type = n.getAttr('elType');
            const labelFn = TYPE_LABELS[type];
            const baseLabel = labelFn ? labelFn() : type;
            const text = (type === 'text' && n.text) ? n.text().slice(0, 24) : baseLabel;
            const isSel = selectedSet.has(n);
            const visible = n.visible() !== false;
            const locked = n.getAttr('elLocked') === true;
            return `
                <div class="wn-layer-item ${isSel ? 'is-selected' : ''} ${locked ? 'is-locked' : ''}" data-idx="${i}" role="button" tabindex="0" draggable="true">
                    <span class="wn-layer-icon">${layerIcon(type)}</span>
                    <span class="wn-layer-name">${escapeHtml(text)}</span>
                    <span class="wn-layer-actions">
                        <button class="wn-layer-act" data-act="lock" aria-pressed="${locked ? 'true' : 'false'}" title="${escapeHtml(t('prop_lock', 'Lock in place'))}">${locked ? '🔒' : '🔓'}</button>
                        <button class="wn-layer-act" data-act="vis" aria-pressed="${visible ? 'true' : 'false'}" title="${escapeHtml(t('layer_visibility', 'Show/hide'))}">${visible ? '👁' : '◌'}</button>
                        <button class="wn-layer-act" data-act="del" title="${escapeHtml(t('btn_delete', 'Delete'))}">🗑️</button>
                    </span>
                </div>
            `;
        }).join('');
        $$('.wn-layer-item', root).forEach((row) => {
            const idx = parseInt(row.dataset.idx, 10);
            const node = items[idx];
            row.onclick = (e) => {
                if (e.target.closest('.wn-layer-act')) return;
                state.konva.tr.nodes([node]);
                state.konva.layers.content.batchDraw();
                renderProperties();
                renderLayers();
            };
            const visBtn = row.querySelector('[data-act="vis"]');
            visBtn.onclick = (e) => {
                e.stopPropagation();
                node.visible(!node.visible());
                state.konva.layers.content.batchDraw();
                markDirty(); pushHistory();
                renderLayers();
            };
            const lockBtn = row.querySelector('[data-act="lock"]');
            lockBtn.onclick = (e) => {
                e.stopPropagation();
                setNodeLocked(node, node.getAttr('elLocked') !== true);
                markDirty(); pushHistory();
                renderLayers();
                renderProperties();
            };
            const delBtn = row.querySelector('[data-act="del"]');
            delBtn.onclick = (e) => {
                e.stopPropagation();
                deleteNodes([node]);
            };

            /* Drag to restack. The panel already showed the stacking order and
               offered no way to change it except "to front" / "to back", which
               cannot express "just below that photo". */
            row.addEventListener('dragstart', (ev) => {
                ev.dataTransfer.effectAllowed = 'move';
                ev.dataTransfer.setData('text/plain', String(idx));
                row.classList.add('is-dragging');
            });
            row.addEventListener('dragend', () => row.classList.remove('is-dragging'));
            row.addEventListener('dragover', (ev) => {
                ev.preventDefault();
                ev.dataTransfer.dropEffect = 'move';
                row.classList.add('is-drop-target');
            });
            row.addEventListener('dragleave', () => row.classList.remove('is-drop-target'));
            row.addEventListener('drop', (ev) => {
                ev.preventDefault();
                row.classList.remove('is-drop-target');
                const from = parseInt(ev.dataTransfer.getData('text/plain'), 10);
                if (!Number.isFinite(from) || from === idx) return;
                // `items` is top-most first, the Konva layer is bottom-most
                // first, so the target z-index is mirrored.
                const moving = items[from];
                const targetZ = items.length - 1 - idx;
                moving.zIndex(Math.max(0, Math.min(items.length - 1, targetZ)));
                state.konva.layers.content.batchDraw();
                markDirty(); pushHistory();
                renderLayers();
            });
        });
    }

    function layerIcon(type) {
        if (type === 'frame')   return '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-dasharray="2.4 1.8"><rect x="2" y="3" width="12" height="10" rx="1.5"/></svg>';
        if (type === 'text')    return '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><path d="M3 4h10M8 4v9M5 13h6"/></svg>';
        if (type === 'image')   return '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="2" y="2" width="12" height="12" rx="1.5"/><circle cx="6" cy="6" r="1.2"/><path d="M14 11l-3.5-3.5L4 14"/></svg>';
        if (type === 'shape')   return '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="2" y="2" width="8" height="8" rx="1"/><circle cx="11" cy="11" r="3"/></svg>';
        if (type === 'drawing') return '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><path d="M2 14l3-1 7-7-2-2-7 7z"/></svg>';
        if (type === 'sticker') return '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6"><polygon points="8 2 10 6 14 6 11 9 12 13 8 11 4 13 5 9 2 6 6 6"/></svg>';
        return '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="3" y="3" width="10" height="10"/></svg>';
    }

    function escapeHtml(s) {
        return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    }

    function deleteNodes(nodes) {
        if (!nodes || !nodes.length) return;
        nodes.forEach((n) => n.destroy());
        state.konva.tr.nodes([]);
        state.konva.layers.content.batchDraw();
        renderProperties();
        renderLayers();
        markDirty();
        pushHistory();
    }

    // ─── Group / ungroup ──────────────────────────────────────────────
    // Group multiple selected nodes into a single Konva.Group. The group
    // becomes a single draggable/transformable unit, and its children move
    // with it. Serialization preserves the group structure (recursive).
    function groupSelected() {
        const sel = state.konva.tr.nodes();
        const flat = sel.filter((n) => n.hasName?.('wn-obj'));
        if (flat.length < 2) return;

        const Konva = window.Konva;
        // Use the bounding box of the selection as the group origin so child
        // coordinates stay positive — easier to reason about visually.
        const boxes = flat.map((n) => {
            const sx = Math.abs(n.scaleX ? n.scaleX() : 1);
            const sy = Math.abs(n.scaleY ? n.scaleY() : 1);
            return {
                x: n.x(),
                y: n.y(),
                w: (n.width  ? n.width()  : 0) * sx,
                h: (n.height ? n.height() : 0) * sy,
            };
        });
        const minX = Math.min(...boxes.map((b) => b.x));
        const minY = Math.min(...boxes.map((b) => b.y));

        const group = new Konva.Group({ x: minX, y: minY });
        tagNode(group, { type: 'group' });
        // Listen on the group itself for drag/transform
        attachNodeInteractions(group);

        for (const n of flat) {
            const ax = n.x() - minX;
            const ay = n.y() - minY;
            // Detach existing dragmove/dragend listeners since the group will
            // own dragging now. Children inside a Konva.Group still emit drag
            // events themselves if they're draggable — turn that off so the
            // group is the only drag handle.
            n.draggable(false);
            n.x(ax);
            n.y(ay);
            n.moveTo(group);
        }

        state.konva.layers.content.add(group);
        state.konva.tr.nodes([group]);
        state.konva.layers.content.batchDraw();
        renderProperties();
        renderLayers();
        markDirty();
        pushHistory();
        ae('group', { n: flat.length });
    }

    function ungroupSelected() {
        const sel = state.konva.tr.nodes();
        const groups = sel.filter((n) => n.getAttr?.('elType') === 'group');
        if (!groups.length) return;

        const released = [];
        for (const g of groups) {
            const gx = g.x(), gy = g.y();
            const gsx = g.scaleX() || 1, gsy = g.scaleY() || 1;
            const grot = g.rotation() || 0;
            const children = g.children.slice();
            for (const c of children) {
                if (!c.hasName?.('wn-obj')) continue;
                // Compute child's absolute position by applying group transform.
                // For axis-aligned groups (no rotation) this is straightforward;
                // with rotation we use Konva's matrix helper.
                if (Math.abs(grot) < 0.01 && Math.abs(gsx - 1) < 0.001 && Math.abs(gsy - 1) < 0.001) {
                    c.x(gx + c.x());
                    c.y(gy + c.y());
                } else {
                    const abs = c.getAbsolutePosition();
                    c.moveTo(state.konva.layers.content);
                    c.setAbsolutePosition(abs);
                    c.rotation(c.rotation() + grot);
                    c.scaleX((c.scaleX() || 1) * gsx);
                    c.scaleY((c.scaleY() || 1) * gsy);
                    released.push(c);
                    continue;
                }
                c.moveTo(state.konva.layers.content);
                released.push(c);
            }
            for (const c of released) {
                c.draggable(true);
                attachNodeInteractions(c);
            }
            g.destroy();
        }
        state.konva.tr.nodes(released);
        state.konva.layers.content.batchDraw();
        renderProperties();
        renderLayers();
        markDirty();
        pushHistory();
        ae('ungroup', { n: groups.length });
    }

    function duplicateNodes(nodes) {
        if (!nodes || !nodes.length) return;
        const clones = nodes.map((n) => {
            const c = n.clone({
                x: n.x() + 30,
                y: n.y() + 30,
                elId: ulid(),
            });
            attachNodeInteractions(c);
            if (n.getAttr('elType') === 'text') {
                c.on('dblclick dbltap', () => startTextEdit(c));
            }
            state.konva.layers.content.add(c);
            return c;
        });
        state.konva.tr.nodes(clones);
        state.konva.layers.content.batchDraw();
        renderProperties();
        renderLayers();
        markDirty();
        pushHistory();
    }

    // ─── History (undo/redo) ──────────────────────────────────────────
    // Strategy: snapshot serialised elements + canvas (NOT assets — those live in
    // state.assets and are immutable). Restore = rebuild content layer.
    function pushHistory() {
        if (state.history.busy) return;
        const snap = takeSnapshot();
        const h = state.history;
        h.stack.length = h.ptr + 1;
        h.stack.push(snap);
        if (h.stack.length > HISTORY_CAP) h.stack.shift();
        else h.ptr++;
        updateHistoryButtons();
        // Trigger autosave 2s after the last mutation. Each pushHistory is a
        // "complete action" boundary (drag end, transform end, color change),
        // so this debounce naturally batches rapid edits.
        scheduleAutoSave();
        // If a collaboration session is running, broadcast the new state to
        // every connected guest. Network failure on individual peers is
        // tolerated inside collab.js.
        if (window.WNCollab?.isHost?.() && window.WNCollab.peerCount() > 0) {
            window.WNCollab.broadcast();
        }
    }

    function undo() {
        const h = state.history;
        if (h.ptr <= 0) return;
        h.ptr--;
        applySnapshot(h.stack[h.ptr]);
        updateHistoryButtons();
        ae('undo', {});
    }

    function redo() {
        const h = state.history;
        if (h.ptr >= h.stack.length - 1) return;
        h.ptr++;
        applySnapshot(h.stack[h.ptr]);
        updateHistoryButtons();
        ae('redo', {});
    }

    function updateHistoryButtons() {
        $('wnUndo').disabled = state.history.ptr <= 0;
        $('wnRedo').disabled = state.history.ptr >= state.history.stack.length - 1;
    }

    function takeSnapshot() {
        // Lightweight serialisation: per-element flat record. Heavy bits (image
        // bitmaps) are referenced by asset_id, not duplicated.
        const elements = state.konva.layers.content.children
            .filter((n) => n.hasName?.('wn-obj'))
            .map((n) => serializeNode(n));
        return JSON.stringify({
            elements,
            canvas_bg: state.project.canvas.background,
            // Page names are an edit like any other — renaming a page and
            // pressing undo has to put the old name back, and the name is
            // drawn on the board, so it cannot live outside history.
            page_titles: Array.isArray(state.project.canvas.page_titles)
                ? state.project.canvas.page_titles.slice() : null,
        });
    }

    function applySnapshot(json) {
        state.history.busy = true;
        try {
            const snap = JSON.parse(json);
            // Reset content layer
            const content = state.konva.layers.content;
            // Remove all wn-obj nodes (keep transformer)
            content.children
                .filter((n) => n.hasName?.('wn-obj'))
                .forEach((n) => n.destroy());
            state.konva.tr.nodes([]);
            // Reapply background
            if (snap.canvas_bg) {
                state.project.canvas.background = snap.canvas_bg;
                setPaperFill(snap.canvas_bg.color || '#ffffff');
                state.konva.layers.bg.batchDraw();
            }
            // Restore page names before anything redraws the board labels.
            const titles = state.project.canvas.page_titles;
            const changed = Array.isArray(snap.page_titles) && Array.isArray(titles)
                && JSON.stringify(snap.page_titles) !== JSON.stringify(titles);
            if (Array.isArray(snap.page_titles)) {
                state.project.canvas.page_titles = snap.page_titles.slice();
            }

            // Rebuild elements
            (snap.elements || []).forEach((el) => deserializeNode(el));
            content.batchDraw();
            renderProperties();
            renderLayers();
            if (changed) { renderSheetLabels(); renderPageNav(); }
        } finally {
            state.history.busy = false;
        }
    }

    function serializeNode(n) {
        const base = {
            elId: n.getAttr('elId'),
            elType: n.getAttr('elType'),
            elShape: n.getAttr('elShape'),
            elAssetId: n.getAttr('elAssetId'),
            x: n.x(), y: n.y(),
            scaleX: n.scaleX(), scaleY: n.scaleY(),
            rotation: n.rotation(),
            opacity: n.opacity(),
            visible: n.visible(),
            locked: n.getAttr('elLocked') === true,
        };
        if (n.width)  base.width = n.width();
        if (n.height) base.height = n.height();
        if (n.fill)   base.fill = n.fill();
        if (n.stroke) base.stroke = n.stroke();
        if (n.strokeWidth) base.strokeWidth = n.strokeWidth();
        if (n.getAttr('elType') === 'text') {
            base.text = n.text();
            base.fontSize = n.fontSize();
            base.fontFamily = n.fontFamily();
            base.fontStyle = n.fontStyle();
            // A curved heading is a Konva.TextPath, not a Konva.Text (see
            // textLineHeight): asking it for a lineHeight threw inside
            // pushHistory and took the whole edit with it.
            base.align = n.align ? n.align() : 'left';
            base.lineHeight = textLineHeight(n);
            base.letterSpacing = n.letterSpacing ? n.letterSpacing() : 0;
            base.curve = n.getAttr('elCurve') || 0;
            const cornerTitle = n.getAttr('elCornerTitle');
            if (cornerTitle != null) base.cornerTitle = cornerTitle;
            base.textWidth = n.getAttr('elTextWidth') || (n.width ? n.width() : 0) || 0;
            base.upper = n.getAttr('elUpper') === true;
            base.textOriginal = n.getAttr('elTextOriginal') || null;
            base.wordart = n.getAttr('elWordart') || 'none';
            base.baseFill = n.getAttr('elBaseFill') || n.fill() || '#111';
            // WordArt-derived attrs (preserve so undo/redo & save round-trip cleanly)
            base.shadowColor   = n.shadowColor();
            base.shadowBlur    = n.shadowBlur();
            base.shadowOffsetX = n.shadowOffsetX();
            base.shadowOffsetY = n.shadowOffsetY();
            base.shadowOpacity = n.shadowOpacity();
            base.strokeWidth   = n.strokeWidth();
            base.gradientStops = n.fillLinearGradientColorStops();
        }
        if (n.getAttr('elType') === 'frame') {
            base.width = n.width();
            base.height = n.height();
        }
        if (n.getAttr('elType') === 'image') {
            // Everything that makes the photo look the way it does on screen.
            // Without these a saved board reopened as an un-cropped, un-framed,
            // full-colour rectangle and the user's framing was gone.
            base.crop = n.crop();
            base.cornerRadius = n.cornerRadius();
            base.radiusPct = n.getAttr('elRadiusPct') || 0;
            base.photoZoom = n.getAttr('elPhotoZoom') || 1;
            base.photoOX = n.getAttr('elPhotoOX') || 0;
            base.photoOY = n.getAttr('elPhotoOY') || 0;
            base.photoAspect = n.getAttr('elPhotoAspect') || null;
            base.bw = n.getAttr('elBW') === true;
            base.sepia = n.getAttr('elSepia') === true;
            base.bright = n.getAttr('elBright') || 0;
            base.contrast = n.getAttr('elContrast') || 0;
        }
        if (n.getAttr('elType') === 'drawing') {
            base.points = n.points();
            base.tension = n.tension();
        }
        if (n.getAttr('elType') === 'shape') {
            if (n.getAttr('elShape') === 'star')    { base.numPoints = n.numPoints(); base.innerRadius = n.innerRadius(); base.outerRadius = n.outerRadius(); }
            if (n.getAttr('elShape') === 'ellipse') { base.radiusX = n.radiusX(); base.radiusY = n.radiusY(); }
            if (n.getAttr('elShape') === 'arrow' || n.getAttr('elShape') === 'line') base.points = n.points();
            if (n.getAttr('elShape') === 'rect')    { base.cornerRadius = n.cornerRadius(); base.radiusPct = n.getAttr('elRadiusPct') || 0; }
            if (n.getAttr('elShape') === 'heart')   base.data = n.data?.();
        }
        if (n.getAttr('elType') === 'sticker') {
            base.elStickerKey = n.getAttr('elStickerKey');
            base.elStickerColor = n.getAttr('elStickerColor');
        }
        if (n.getAttr('elType') === 'group') {
            // Recursively serialise children so groups round-trip through
            // save/load + undo/redo identically.
            base.children = (n.children || [])
                .filter((c) => c.hasName?.('wn-obj'))
                .map((c) => serializeNode(c));
        }
        return base;
    }

    // Like deserializeNode, but returns the node without adding it to the
    // content layer. Used by group reconstruction so the group can own
    // children directly.
    function buildNodeFromElement(el) {
        const before = state.konva.layers.content.children.length;
        deserializeNode(el);
        const after = state.konva.layers.content.children.slice(before);
        const node = after[0];
        if (node) node.remove(); // detach from content; caller will reparent
        return node;
    }

    function deserializeNode(el) {
        const Konva = window.Konva;
        let node = null;
        if (el.elType === 'text' && Math.abs(el.curve || 0) >= 1) {
            // A curved heading comes back as the node class that can draw it.
            node = new Konva.TextPath({
                x: el.x, y: el.y,
                data: arcPath(el.textWidth || 600, el.curve),
                text: el.text || '', fontSize: el.fontSize || 56,
                fontFamily: el.fontFamily || 'Inter', fontStyle: el.fontStyle || 'normal',
                fill: el.fill || el.baseFill || '#111',
                letterSpacing: el.letterSpacing || 0, align: el.align || 'left',
            });
            node.setAttr('elCurve', el.curve);
            node.setAttr('elTextWidth', el.textWidth || 600);
            if (el.baseFill) node.setAttr('elBaseFill', el.baseFill);
            if (el.wordart) node.setAttr('elWordart', el.wordart);
            if (el.upper) node.setAttr('elUpper', true);
            if (el.textOriginal) node.setAttr('elTextOriginal', el.textOriginal);
            if (el.stroke) node.stroke(el.stroke);
            if (el.strokeWidth) node.strokeWidth(el.strokeWidth);
            node.on('dblclick dbltap', () => startTextEdit(node));
        } else if (el.elType === 'text') {
            node = new Konva.Text({
                x: el.x, y: el.y, text: el.text || '', fontSize: el.fontSize || 56,
                fontFamily: el.fontFamily || 'Inter', fontStyle: el.fontStyle || 'normal',
                fill: el.fill || el.baseFill || '#111', align: el.align || 'left',
                lineHeight: el.lineHeight || 1.2, padding: 4,
                letterSpacing: el.letterSpacing || 0,
            });
            if (el.upper) node.setAttr('elUpper', true);
            if (el.textOriginal) node.setAttr('elTextOriginal', el.textOriginal);
            if (el.textWidth) node.setAttr('elTextWidth', el.textWidth);
            if (el.cornerTitle != null) node.setAttr('elCornerTitle', el.cornerTitle);
            // Restore wordart-related visual state
            if (el.baseFill)      node.setAttr('elBaseFill', el.baseFill);
            if (el.wordart)       node.setAttr('elWordart',  el.wordart);
            if (el.shadowColor)   node.shadowColor(el.shadowColor);
            if (el.shadowBlur)    node.shadowBlur(el.shadowBlur);
            if (el.shadowOffsetX) node.shadowOffsetX(el.shadowOffsetX);
            if (el.shadowOffsetY) node.shadowOffsetY(el.shadowOffsetY);
            if (el.shadowOpacity) node.shadowOpacity(el.shadowOpacity);
            if (el.strokeWidth)   node.strokeWidth(el.strokeWidth);
            if (el.stroke)        node.stroke(el.stroke);
            if (Array.isArray(el.gradientStops) && el.gradientStops.length) {
                const w = Math.max(80, node.width() || 400);
                node.fill(null);
                node.fillLinearGradientStartPoint({ x: 0, y: 0 });
                node.fillLinearGradientEndPoint({ x: w, y: 0 });
                node.fillLinearGradientColorStops(el.gradientStops);
            }
            node.on('dblclick dbltap', () => startTextEdit(node));
        } else if (el.elType === 'image') {
            const asset = state.assets.get(el.elAssetId);
            if (!asset) return; // broken image — skip silently for now
            const img = new Image();
            // The node gets its picture only once it is DECODED: Konva draws
            // whatever it holds, and a broken image throws on every frame
            // (Sentry TIMBRICA-FRONTEND-R1). Until then it keeps its place and size.
            node = new Konva.Image({
                x: el.x, y: el.y, width: el.width, height: el.height,
                cornerRadius: el.cornerRadius || 0,
                stroke: el.stroke || undefined,
                strokeWidth: el.strokeWidth || 0,
                strokeScaleEnabled: false,
            });
            node.setAttr('elRadiusPct', el.radiusPct || 0);
            node.setAttr('elPhotoZoom', el.photoZoom || 1);
            node.setAttr('elPhotoOX', el.photoOX || 0);
            node.setAttr('elPhotoOY', el.photoOY || 0);
            node.setAttr('elPhotoAspect', el.photoAspect || null);
            node.setAttr('elBW', el.bw === true);
            node.setAttr('elSepia', el.sepia === true);
            node.setAttr('elBright', el.bright || 0);
            node.setAttr('elContrast', el.contrast || 0);
            if (el.crop && el.crop.width) node.crop(el.crop);
            const photoNode = node;
            // Filters and crop both need the decoded bitmap, so they are
            // applied once the image actually loads — doing it now would cache
            // an empty node and the photo would come back blank.
            img.onload = () => {
                photoNode.image(img);
                if (!el.crop || !el.crop.width) applyPhotoCrop(photoNode);
                refreshPhotoRender(photoNode);
                state.konva.layers.content.batchDraw();
            };
            img.onerror = () => photoUnreadable(el.elAssetId);
            img.src = asset.url;
        } else if (el.elType === 'frame') {
            node = makeFrameNode(el.x, el.y, el.width || 600, el.height || 450);
            node.on('dblclick dbltap', () => pickPhotoForFrame(node));
        } else if (el.elType === 'shape') {
            node = makeShape(el.elShape, { x: el.x, y: el.y, w: el.width || 100, h: el.height || 100, fill: el.fill || '#5cb3ff' });
            if (el.elShape === 'star') { node.numPoints(el.numPoints || 5); node.innerRadius(el.innerRadius); node.outerRadius(el.outerRadius); }
            if (el.elShape === 'ellipse') { node.radiusX(el.radiusX); node.radiusY(el.radiusY); }
            if ((el.elShape === 'arrow' || el.elShape === 'line') && el.points) node.points(el.points);
            if (el.elShape === 'rect') {
                node.cornerRadius(el.cornerRadius != null ? el.cornerRadius : 4);
                node.setAttr('elRadiusPct', el.radiusPct || 0);
            }
            if (el.stroke) node.stroke(el.stroke);
            node.strokeWidth(el.strokeWidth || 0);
        } else if (el.elType === 'drawing') {
            node = new Konva.Line({
                points: el.points, stroke: el.stroke || '#111', strokeWidth: el.strokeWidth || 6,
                lineCap: 'round', lineJoin: 'round', tension: el.tension || 0.3,
            });
        } else if (el.elType === 'sticker') {
            if (!window.WNStickers) return;
            const def = window.WNStickers.getStickerByKey(el.elStickerKey);
            if (!def) return;
            node = window.WNStickers.buildKonvaGroup(def, { x: el.x, y: el.y, size: 100, color: el.elStickerColor });
            node.setAttr('elStickerKey', el.elStickerKey);
            node.setAttr('elStickerColor', el.elStickerColor);
        } else if (el.elType === 'group') {
            node = new Konva.Group({ x: el.x, y: el.y });
            // Recursively reconstruct children — using a small helper that
            // builds the child node WITHOUT auto-adding it to the content
            // layer (groups must take ownership directly).
            for (const childEl of (el.children || [])) {
                const child = buildNodeFromElement(childEl);
                if (!child) continue;
                child.draggable(false);
                node.add(child);
            }
        }
        if (!node) return;
        node.scaleX(el.scaleX || 1);
        node.scaleY(el.scaleY || 1);
        node.rotation(el.rotation || 0);
        node.opacity(el.opacity != null ? el.opacity : 1);
        node.visible(el.visible !== false);
        tagNode(node, { id: el.elId, type: el.elType, shape: el.elShape, assetId: el.elAssetId });
        attachNodeInteractions(node);
        // Restore lock AFTER interactions are attached — attachNodeInteractions
        // makes the node draggable and listening, so doing it earlier would be
        // silently undone and a locked background would come back movable.
        if (el.locked) setNodeLocked(node, true);
        state.konva.layers.content.add(node);
    }

    // ─── Dirty / save indicator ───────────────────────────────────────
    function markDirty() {
        state.dirty = true;
        // setSaveState is defined below — guard for very early init.
        if (typeof setSaveState === 'function') setSaveState('dirty');
        else $('wnSaveIndicator').dataset.state = 'dirty';
    }

    // ─── Keyboard shortcuts ───────────────────────────────────────────
    function installKeyboardShortcuts() {
        document.addEventListener('keydown', (e) => {
            if (state.project == null) return;
            // Ignore typing in inputs / textarea (except inline text editor —
            // it has its own Esc handling).
            const tgt = e.target;
            if (tgt && (tgt.tagName === 'INPUT' || (tgt.tagName === 'TEXTAREA' && tgt.id !== 'wnTextEditor') || tgt.isContentEditable)) return;
            const kl = window.__keyLetter ? window.__keyLetter(e) : String(e.key || '').toLowerCase();
            const key = kl || String(e.key || '').toLowerCase();
            const ctrl = e.ctrlKey || e.metaKey;

            // Inline text editor shortcuts
            if (tgt?.id === 'wnTextEditor') {
                if (e.key === 'Escape') { e.preventDefault(); finishTextEdit(); }
                else if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); finishTextEdit(); }
                return;
            }

            if (ctrl && key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); return; }
            if ((ctrl && key === 'z' && e.shiftKey) || (ctrl && key === 'y')) { e.preventDefault(); redo(); return; }
            if (ctrl && key === 's') { e.preventDefault(); cmdSave(); return; }
            if (ctrl && key === 'a') {
                e.preventDefault();
                const all = state.konva.layers.content.children.filter((n) => n.hasName?.('wn-obj'));
                state.konva.tr.nodes(all);
                state.konva.layers.content.batchDraw();
                renderProperties();
                return;
            }
            if (ctrl && key === 'd') {
                e.preventDefault();
                const sel = selectedNodes();
                if (sel.length) duplicateNodes(sel);
                return;
            }
            if (ctrl && key === 'c') { e.preventDefault(); copySelection(); return; }
            if (ctrl && key === 'v') { e.preventDefault(); pasteClipboard(); return; }
            if (ctrl && key === 'x') {
                e.preventDefault();
                const sel = selectedNodes();
                if (sel.length) { copySelection(); deleteNodes(sel); }
                return;
            }
            // Arrange, with the bindings Canva, Adobe Express and VistaCreate
            // all share — a user who knows one knows this.
            // «[ ]» by the key's place: the Russian layout prints «х ъ» there.
            const ks = window.__keySign ? window.__keySign(e) : e.key;
            if (ctrl && (ks === ']' || ks === '[')) {
                e.preventDefault();
                const sel = selectedNodes();
                if (!sel.length) return;
                const toEnd = e.altKey;
                sel.forEach((n) => {
                    if (ks === ']') toEnd ? n.moveToTop() : n.moveUp();
                    else toEnd ? n.moveToBottom() : n.moveDown();
                });
                state.konva.layers.content.batchDraw();
                renderLayers();
                markDirty(); pushHistory();
                return;
            }
            if (ctrl && key === 'g' && !e.shiftKey) {
                e.preventDefault();
                groupSelected();
                return;
            }
            if (ctrl && key === 'g' && e.shiftKey) {
                e.preventDefault();
                ungroupSelected();
                return;
            }

            const map = { v: 'select', t: 'text', i: 'image', p: 'draw', s: 'shape', k: 'sticker', g: 'background', h: 'hand', b: 'blocks' };
            if (!ctrl && map[key]) { e.preventDefault(); selectTool(map[key]); return; }

            // Zoom, with the bindings every canvas app shares.
            if (!ctrl && (kl === '0')) { e.preventDefault(); zoomToFit(); return; }
            if (!ctrl && (kl === '1')) { e.preventDefault(); setZoom(1); return; }
            if ((ctrl && (e.key === '=' || e.key === '+')) || (!ctrl && e.key === '+')) { e.preventDefault(); setZoom(state.zoom * 1.2); return; }
            if ((ctrl && e.key === '-') || (!ctrl && e.key === '-')) { e.preventDefault(); setZoom(state.zoom / 1.2); return; }

            if (e.key === 'Delete' || e.key === 'Backspace') {
                const sel = selectedNodes();
                if (sel.length) { e.preventDefault(); deleteNodes(sel); }
                return;
            }
            if (e.key === 'Escape') {
                cancelDrawing();
                clearSelection();
                if (state.tool !== 'select') selectTool('select');
                return;
            }
            // Nudge
            if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                const sel = selectedNodes();
                if (!sel.length) return;
                const step = e.shiftKey ? 20 : 2;
                const dx = e.key === 'ArrowLeft' ? -step : (e.key === 'ArrowRight' ? step : 0);
                const dy = e.key === 'ArrowUp' ? -step : (e.key === 'ArrowDown' ? step : 0);
                sel.forEach((n) => { n.x(n.x() + dx); n.y(n.y() + dy); });
                state.konva.layers.content.batchDraw();
                e.preventDefault();
                // nudge committed once per stop — debounce push
                clearTimeout(installKeyboardShortcuts._pushT);
                installKeyboardShortcuts._pushT = setTimeout(() => { markDirty(); pushHistory(); }, 350);
                return;
            }

            if (key === '+' || key === '=') { setZoom(state.zoom * 1.25); return; }
            if (key === '-' || key === '_') { setZoom(state.zoom / 1.25); return; }
            if (kl === '0') { zoomToFit(); return; }
            if (kl === '1') { setZoom(1); return; }
        });
    }

    // ─── File menu commands ───────────────────────────────────────────
    async function cmdNew() {
        if (state.dirty && !confirm(t('unsaved_warning', 'Discard unsaved changes?'))) return;
        // Force-flush any pending autosave so the in-progress project is
        // recoverable from "Recent" even if the user just Ctrl-clicks New.
        await flushAutoSave();
        leaveWorkspace();
        renderRecentProjects();
    }

    function leaveWorkspace() {
        document.body.classList.remove('wn-workspace-active');
        $('wnWorkspace').hidden = true;
        $('wnWorkspace').classList.remove('active');
        $('wnStart').hidden = false;
        state.project = null;
        state.dirty = false;
        state.history.stack = [];
        state.history.ptr = -1;
        // Free asset URLs (unique to this session — IDB stores blobs not URLs)
        for (const a of state.assets.values()) {
            try { URL.revokeObjectURL(a.url); } catch (e) { /* noop */ }
        }
        state.assets.clear();
        // Tear down the Konva stage so a new buildStage doesn't leak the
        // previous one. Without this, each recovery / template apply piles
        // up an extra Stage in Konva.stages and chews through GPU memory.
        if (state.konva?.stage) {
            try { state.konva.stage.destroy(); } catch (e) { /* noop */ }
        }
        state.konva = null;
    }

    async function cmdSave() {
        if (!state.project) return;
        ae('cmd_save', {});
        try {
            setSaveState('saving');
            const { blob, thumbnail } = await buildProjectZipBlob();
            await idbPersist(blob, thumbnail);
            // Also offer the file as a download so user has an external copy
            downloadBlob(blob, sanitizeFilename(state.project.meta.title) + '.tmb-board');
            setSaveState('saved');
            state.dirty = false;
            toast(t('saved', 'All changes saved'), 'success');
            // Tier-0: a successful save counts as a tool completion
            aeCritical('tool_completed', { action: 'save', sizeKb: Math.round(blob.size / 1024) });
        } catch (e) {
            setSaveState('error');
            aeCritical('save_fail', { err: e.message, code: e.code });
            aeCritical('tool_error', { stage: 'save', err: e.message, code: e.code });
            toast((t('save_error', 'Could not save')) + (e.message ? `: ${e.message}` : ''), 'error');
        }
    }

    async function cmdOpen() {
        ae('cmd_open', {});
        // Single hidden input on the start-screen card; trigger it
        const input = $('wnLoadProjectInput');
        input.value = '';
        input.click();
    }

    async function cmdExportPDF() {
        if (!state.project) return;
        ae('cmd_export_pdf', {});
        await openPrintModal();
    }

    // ─── Collaboration / share ────────────────────────────────────────
    let _isGuest = false;

    async function cmdShare() {
        if (!state.project) return;
        ae('cmd_share', {});
        const modal = $('wnShareModal');
        modal.hidden = false;
        // Reset to idle UI on each open
        $('wnShareIdle').hidden = false;
        $('wnShareRunning').hidden = true;
        $('wnShareStop').hidden = true;
    }

    async function startSharing() {
        if (!window.WNCollab) {
            toast(t('collab_unavailable', 'Collaboration is unavailable in this browser.'), 'error');
            return;
        }
        try {
            const peerId = await window.WNCollab.startHost({
                getProject: () => serializeProjectFull(),
                onPeerJoin:  () => updateShareStatus(),
                onPeerLeave: () => updateShareStatus(),
                onError: (e) => { aeCritical('collab_error', { err: e?.message }); },
            });
            const url = location.origin + location.pathname + '#join=' + encodeURIComponent(peerId);
            $('wnShareLink').value = url;
            $('wnShareIdle').hidden = true;
            $('wnShareRunning').hidden = false;
            $('wnShareStop').hidden = false;
            await renderShareQR(url);
            updateShareStatus();
            ae('collab_started', {});
        } catch (e) {
            aeCritical('collab_start_fail', { err: e?.message });
            toast(t('collab_start_fail', 'Could not start a session — check your connection.'), 'error');
        }
    }

    async function renderShareQR(text) {
        const wrap = $('wnShareQR');
        if (!wrap || !window.QRCode) return;
        // qr-adapter.js exposes a `new QRCode(el, {text, width})` constructor
        // that injects a child <canvas> into `el`. Wipe any previous render.
        wrap.innerHTML = '';
        try {
            new window.QRCode(wrap, { text, width: 200 });
        } catch (e) { /* QR is a nice-to-have */ }
    }

    function updateShareStatus() {
        const s = $('wnShareStatus');
        if (!s) return;
        const n = window.WNCollab?.peerCount() || 0;
        s.textContent = n === 0
            ? t('share_waiting', 'Waiting for helpers to join…')
            : (t('share_connected', ':n connected').replace(':n', n));
    }

    function stopSharing() {
        if (window.WNCollab?.isActive?.()) window.WNCollab.disconnect();
        $('wnShareIdle').hidden = false;
        $('wnShareRunning').hidden = true;
        $('wnShareStop').hidden = true;
        ae('collab_stopped', {});
    }

    function wireShareModal() {
        $('wnShareStart').onclick = startSharing;
        $('wnShareCopy').onclick = async () => {
            const url = $('wnShareLink').value;
            try {
                await navigator.clipboard.writeText(url);
                toast(t('share_copied', 'Link copied'), 'success');
            } catch (e) {
                $('wnShareLink').select();
                document.execCommand('copy');
            }
        };
        $('wnShareClose').onclick = () => { $('wnShareModal').hidden = true; };
        $('wnShareStop').onclick  = () => { stopSharing(); $('wnShareModal').hidden = true; };
    }

    // ─── Guest mode (joining an existing session) ─────────────────────
    async function joinFromHash() {
        const m = location.hash.match(/[#&]join=([A-Za-z0-9_-]+)/);
        if (!m) return false;
        const hostId = decodeURIComponent(m[1]);
        if (!window.WNCollab) return false;

        // Strip the hash so a refresh does not re-trigger the join flow.
        try { history.replaceState(null, '', location.pathname + location.search); } catch (e) {}

        toast(t('guest_connecting', 'Connecting to host…'), 'info');
        try {
            await window.WNCollab.joinHost(hostId, {
                applyProject: async (project) => {
                    // Adopt the host's project. Assets are not transferred via
                    // the data channel in v1 — image elements show placeholders
                    // (handled inside applyProjectFull via _broken flag).
                    await applyProjectFull({ project, assets: new Map() });
                    enterGuestMode();
                },
                onDisconnect: () => {
                    leaveGuestMode();
                    toast(t('guest_disconnected', 'Disconnected from host'), 'warning');
                },
            });
            ae('collab_joined', {});
            return true;
        } catch (e) {
            aeCritical('collab_join_fail', { err: e?.message });
            toast(t('guest_join_fail', 'Could not connect to host. The link may be expired.'), 'error');
            return false;
        }
    }

    function enterGuestMode() {
        _isGuest = true;
        document.body.classList.add('wn-guest-active');
        $('wnGuestBanner').hidden = false;
    }
    function leaveGuestMode() {
        _isGuest = false;
        document.body.classList.remove('wn-guest-active');
        $('wnGuestBanner').hidden = true;
    }

    // ─── Save as user template ────────────────────────────────────────
    async function cmdSaveAsTemplate() {
        if (!state.project || !window.WNStorage) return;
        ae('cmd_save_as_template', {});
        const defaultName = state.project.meta.title || t('untitled', 'Untitled');
        const name = (prompt(t('save_template_prompt', 'Name for this template?'), defaultName) || '').trim();
        if (!name) return;

        // Build a template-shaped JSON: layout + structural attrs, no assets.
        // Image elements are skipped — user templates are layouts to instantiate
        // on top of, not photo collections.
        const project = serializeProjectFull();
        const elements = (project.elements || [])
            .filter((el) => el.elType !== 'image')
            .map((el) => normaliseElementForTemplate(el));

        const tplJson = {
            version: '1.0',
            canvas: project.canvas,
            elements,
        };

        let thumbnail = null;
        try { thumbnail = await window.WNStorage.canvasThumbnail(state.konva.stage, 600); }
        catch (e) { /* noop */ }

        try {
            await window.WNStorage.idbPutTemplate({
                id: ulid(),
                name,
                created_at: Date.now(),
                thumbnail_blob: thumbnail,
                template_json: tplJson,
            });
            toast(t('template_saved', 'Saved as a personal template'), 'success');
            ae('user_template_saved', { elements: elements.length });
        } catch (e) {
            aeCritical('user_template_save_fail', { err: e.message });
            toast(t('save_error', 'Could not save'), 'error');
        }
    }

    // Convert an internal serialised element into the public template-element
    // shape. Internal uses elType/elShape/x,y,scaleX,scaleY/etc.; templates use
    // type/shape/x,y,w,h with scale baked into w/h so they instantiate in any
    // size canvas.
    function normaliseElementForTemplate(el) {
        const sx = el.scaleX || 1;
        const sy = el.scaleY || 1;
        const w  = (el.width  || 0) * sx;
        const h  = (el.height || 0) * sy;
        const base = {
            x: Math.round(el.x || 0),
            y: Math.round(el.y || 0),
            rotation: el.rotation || 0,
            opacity: el.opacity != null ? el.opacity : 1,
        };
        if (el.elType === 'text') {
            return Object.assign(base, {
                type: 'text',
                w: Math.round(w),
                h: Math.round(h),
                text: el.text,
                fontSize: el.fontSize,
                fontFamily: el.fontFamily,
                fontStyle: el.fontStyle,
                fill: el.fill,
                align: el.align,
                lineHeight: el.lineHeight,
            });
        }
        if (el.elType === 'shape') {
            return Object.assign(base, {
                type: 'shape',
                shape: el.elShape,
                w: Math.round(w),
                h: Math.round(h),
                fill: el.fill,
            });
        }
        if (el.elType === 'sticker') {
            // Sticker scale was set so size = scaleX × 100 (viewBox is 100×100).
            return Object.assign(base, {
                type: 'sticker',
                sticker_key: el.elStickerKey,
                size: Math.round(sx * 100),
                color: el.elStickerColor,
            });
        }
        if (el.elType === 'drawing') {
            return Object.assign(base, {
                type: 'drawing',
                points: el.points,
                stroke: el.stroke,
                strokeWidth: el.strokeWidth,
            });
        }
        return null;
    }
    function cmdOpenTemplate() {
        ae('cmd_open_template', {});
        $('wnTemplateModal').hidden = false;
        loadTemplates();
    }

    async function loadTemplates() {
        const grid = $('wnTemplateGrid');
        grid.innerHTML = `<div class="wn-template-loading">${t('loading', 'Loading…')}</div>`;
        const locale = (window.__LOCALE || document.documentElement.lang || 'en').split('-')[0];

        // Load both built-in templates and user-saved ones in parallel.
        const [builtin, user] = await Promise.all([
            fetch(dataUrl('/images/wall-newspaper/templates/_index.json'))
                .then((r) => r.ok ? r.json() : { templates: [] })
                .catch(() => ({ templates: [] })),
            (window.WNStorage?.idbListTemplates ? window.WNStorage.idbListTemplates() : Promise.resolve([]))
                .catch(() => []),
        ]);
        const builtinItems = (builtin.templates || []).filter((tpl) =>
            !tpl.locales || tpl.locales.includes(locale));

        if (!builtinItems.length && !user.length) {
            grid.innerHTML = `<div class="wn-template-loading">${t('templates_coming', 'Templates coming soon.')}</div>`;
            return;
        }

        grid.innerHTML = '';
        // User templates first — most relevant for the user
        for (const rec of user) {
            const card = document.createElement('div');
            card.className = 'wn-template-card wn-template-user';
            const thumbUrl = rec.thumbnail_blob ? URL.createObjectURL(rec.thumbnail_blob) : '';
            card.innerHTML =
                (thumbUrl
                    ? `<img class="wn-template-thumb" src="${thumbUrl}" alt="">`
                    : `<div class="wn-template-thumb" style="display:flex;align-items:center;justify-content:center;font-size:32px;background:#eff6ff;color:#3b82f6">📋</div>`) +
                `<div class="wn-template-name"><span class="wn-template-badge">${escapeHtml(t('template_my_badge', 'My'))}</span> ${escapeHtml(rec.name)}</div>` +
                `<button type="button" class="wn-template-delete" aria-label="${t('btn_delete', 'Delete')}" data-id="${rec.id}">×</button>`;
            // Click anywhere except the delete button → apply
            card.addEventListener('click', (e) => {
                if (e.target.closest('.wn-template-delete')) return;
                applyUserTemplate(rec);
            });
            const delBtn = card.querySelector('.wn-template-delete');
            delBtn.addEventListener('click', async (e) => {
                e.stopPropagation();
                if (!confirm(t('template_delete_confirm', 'Delete this template?'))) return;
                try { await window.WNStorage.idbDeleteTemplate(rec.id); } catch (_) {}
                loadTemplates();
            });
            grid.appendChild(card);
        }
        // Built-in templates after user ones
        for (const tpl of builtinItems) {
            const card = document.createElement('button');
            card.type = 'button';
            card.className = 'wn-template-card';
            const name = tpl.name_key ? t(tpl.name_key, tpl.slug) : (tpl.name || tpl.slug);
            const desc = tpl.desc_key ? t(tpl.desc_key, '') : (tpl.desc || '');
            // The thumbnail shows the theme, so the caption is free to say the
            // thing a 160 px picture cannot: how many photo slots you get and
            // that there is a column to write in. It is DERIVED from the
            // template file, so it cannot describe a layout that changed.
            card.innerHTML =
                `<div class="wn-template-thumb is-loading" data-thumb></div>` +
                `<div class="wn-template-name">${escapeHtml(name)}</div>` +
                (desc ? `<div class="wn-recent-time">${escapeHtml(desc)}</div>` : '') +
                `<div class="wn-template-meta" data-meta></div>`;
            card.onclick = () => applyTemplate(tpl.slug);
            grid.appendChild(card);
            queueTemplateThumb(tpl, card.querySelector('[data-thumb]'));
        }
    }

    /* Thumbnails render one at a time on an idle callback. Ten templates
       rendered synchronously froze the modal for a beat on a phone, and the
       user is looking at the grid the whole time. */
    let _thumbQueue = Promise.resolve();
    const _thumbCache = new Map();

    /**
     * A data file the editor fetches itself, stamped with the deploy.
     *
     * Everything the asset router serves already carries `?v=<mtime>`; these
     * JSON files are fetched straight from `/images/`, so they had no version
     * at all and the intl edge kept a stale copy of a rebuilt template.
     */
    function dataUrl(path) {
        const v = window.WN_ASSET_V;
        if (!v) return path;
        return path + (path.indexOf('?') === -1 ? '?' : '&') + 'v=' + encodeURIComponent(v);
    }

    function templateMetaLine(full) {
        const frames = (full.elements || []).filter((e) => e.type === 'frame').length;
        if (!frames) return '';
        return t('template_meta', 'Photo slots: :n · a column to write in').replace(':n', frames);
    }

    function queueTemplateThumb(tpl, host) {
        if (!host) return;
        const metaHost = host.closest('.wn-template-card')?.querySelector('[data-meta]');
        _thumbQueue = _thumbQueue.then(async () => {
            // The card may have been discarded by a re-render while queued.
            if (!host.isConnected) return;
            try {
                let hit = _thumbCache.get(tpl.slug);
                if (!hit) {
                    const r = await fetch(dataUrl(`/images/wall-newspaper/templates/${tpl.slug}/template.json`));
                    if (!r.ok) throw new Error('missing');
                    const full = await r.json();
                    await whenKonvaReady();
                    const url = await renderTemplateThumb(full, 340);
                    hit = { url, meta: templateMetaLine(full) };
                    if (url) _thumbCache.set(tpl.slug, hit);
                }
                if (metaHost && metaHost.isConnected) metaHost.textContent = hit.meta || '';
                const url = hit.url;
                if (!url || !host.isConnected) return;
                const img = new Image();
                img.className = 'wn-template-thumb';
                img.alt = '';
                img.src = url;
                host.replaceWith(img);
            } catch (e) {
                // A template that cannot be previewed is still applicable, so
                // the card stays — it just falls back to the generic mark.
                if (host.isConnected) {
                    host.classList.remove('is-loading');
                    host.classList.add('is-fallback');
                    host.textContent = '📰';
                }
            }
        });
    }

    async function applyUserTemplate(rec) {
        if (state.dirty && !confirm(t('unsaved_warning', 'Discard unsaved changes?'))) return;
        $('wnTemplateModal').hidden = true;
        ae('user_template_apply', { id: rec.id });

        const tpl = rec.template_json;
        if (!tpl || !tpl.canvas) { toast(t('load_error', 'Could not open file'), 'error'); return; }

        // A template describes a poster; it must not inherit the pages mode
        // just because the visitor last opened the classroom-corner door, or
        // its artwork would be poured across separate sheets with gaps in it.
        // Print geometry is NOT taken from the template: the shipped template
        // files carry margin_mm/overlap_mm of 5 from the old model, where both
        // were decorative and never cut anything. Honouring them now would
        // hand a 5 mm margin to a printer that needs 10, so a template only
        // gets to override them once it declares the v2 geometry.
        state.sizeChoice = {
            mode: tpl.canvas.layout_mode === 'pages' ? 'pages' : 'tiled',
            pages: tpl.canvas.pages || 6,
            cols: tpl.canvas.sheets?.cols || 3,
            rows: tpl.canvas.sheets?.rows || 2,
            orient: tpl.canvas.orient || 'landscape',
            margin_mm: tpl.canvas.geometry === 'v2' ? tpl.canvas.margin_mm : window.WNGeom.DEFAULT_MARGIN_MM,
            overlap_mm: tpl.canvas.geometry === 'v2' ? tpl.canvas.overlap_mm : window.WNGeom.DEFAULT_OVERLAP_MM,
        };
        await createBlankProject();
        if (tpl.canvas.background) {
            state.project.canvas.background = tpl.canvas.background;
            setPaperFill(tpl.canvas.background.color || '#ffffff');
            state.konva.layers.bg.batchDraw();
        }
        state.history.busy = true;
        try {
            const tf = templateFitTransform(tpl.canvas);
            const before = state.konva.layers.content.children.length;
            for (const el of (tpl.elements || [])) {
                instantiateTemplateElement(el, tf);
            }
            fitTemplateNodesToCanvas(
                state.konva.layers.content.children.slice(before).filter((n) => n.hasName?.('wn-obj')));
            state.konva.layers.content.batchDraw();
        } finally {
            state.history.busy = false;
        }
        state.project.meta.title = rec.name;
        $('wnTitle').value = rec.name;
        renderLayers();
        markDirty();
        pushHistory();
    }

    async function applyTemplate(slug) {
        if (state.dirty && !confirm(t('unsaved_warning', 'Discard unsaved changes?'))) return;
        $('wnTemplateModal').hidden = true;
        ae('template_apply', { slug });

        let tpl;
        try {
            const r = await fetch(dataUrl(`/images/wall-newspaper/templates/${slug}/template.json`));
            if (!r.ok) throw new Error('template not found');
            tpl = await r.json();
        } catch (e) {
            aeCritical('template_load_fail', { slug, err: e.message });
            toast(t('load_error', 'Could not open file'), 'error');
            return;
        }

        // Use template canvas size for the new project
        // A template describes a poster; it must not inherit the pages mode
        // just because the visitor last opened the classroom-corner door, or
        // its artwork would be poured across separate sheets with gaps in it.
        // Print geometry is NOT taken from the template: the shipped template
        // files carry margin_mm/overlap_mm of 5 from the old model, where both
        // were decorative and never cut anything. Honouring them now would
        // hand a 5 mm margin to a printer that needs 10, so a template only
        // gets to override them once it declares the v2 geometry.
        state.sizeChoice = {
            mode: tpl.canvas?.layout_mode === 'pages' ? 'pages' : 'tiled',
            pages: tpl.canvas?.pages || 6,
            cols: tpl.canvas?.sheets?.cols || 3,
            rows: tpl.canvas?.sheets?.rows || 2,
            orient: tpl.canvas?.orient || 'landscape',
            margin_mm: tpl.canvas?.geometry === 'v2' ? tpl.canvas.margin_mm : window.WNGeom.DEFAULT_MARGIN_MM,
            overlap_mm: tpl.canvas?.geometry === 'v2' ? tpl.canvas.overlap_mm : window.WNGeom.DEFAULT_OVERLAP_MM,
        };
        await createBlankProject();

        // Override background
        if (tpl.canvas?.background) {
            state.project.canvas.background = tpl.canvas.background;
            setPaperFill(tpl.canvas.background.color || '#ffffff');
            state.konva.layers.bg.batchDraw();
        }

        // Title from template name (localised)
        if (tpl.name_key || tpl.title_key) {
            const tk = tpl.title_key || tpl.name_key;
            const newTitle = t(tk, state.project.meta.title);
            if (newTitle) {
                state.project.meta.title = newTitle;
                $('wnTitle').value = newTitle;
            }
        }

        // Instantiate elements, rescaled onto the board that was actually
        // created — templates carry the old edge-to-edge coordinates.
        state.history.busy = true;
        try {
            const tf = templateFitTransform(tpl.canvas);
            const before = state.konva.layers.content.children.length;
            for (const el of (tpl.elements || [])) {
                instantiateTemplateElement(el, tf);
            }
            fitTemplateNodesToCanvas(
                state.konva.layers.content.children.slice(before).filter((n) => n.hasName?.('wn-obj')));
            state.konva.layers.content.batchDraw();
        } finally {
            state.history.busy = false;
        }
        renderLayers();
        markDirty();
        pushHistory();
    }

    /**
     * How a template's coordinates map onto the board it is being poured into.
     *
     * Templates were authored against the old edge-to-edge geometry, where a
     * 3×2 landscape board was cols × 297 mm. A board now keeps a printer margin
     * and spends a glue band per seam, so the same board is ~9% smaller and the
     * artwork would hang off the right edge. The scale is uniform (the smaller
     * of the two axes) and the result is centred: scaling x and y separately
     * would turn every circular sticker into an ellipse.
     */
    function templateFitTransform(tplCanvas) {
        const cv = state.project.canvas;
        const cols = tplCanvas?.sheets?.cols || cv.sheets.cols;
        const rows = tplCanvas?.sheets?.rows || cv.sheets.rows;
        const orient = tplCanvas?.orient || cv.orient;
        const sheetW = orient === 'landscape' ? 297 : 210;
        const sheetH = orient === 'landscape' ? 210 : 297;
        const srcW = cols * sheetW * PX_PER_MM;
        const srcH = rows * sheetH * PX_PER_MM;
        const s = Math.min(cv.width_px / srcW, cv.height_px / srcH);
        return {
            s,
            dx: (cv.width_px - srcW * s) / 2,
            dy: (cv.height_px - srcH * s) / 2,
        };
    }

    const TPL_IDENTITY = { s: 1, dx: 0, dy: 0 };

    /** Build the Konva node for one template element without adopting it. */
    function buildTemplateNode(el, tf) {
        const Konva = window.Konva;
        const f = tf || TPL_IDENTITY;
        const X = (v) => (v || 0) * f.s + f.dx;
        const Y = (v) => (v || 0) * f.s + f.dy;
        const S = (v) => (v || 0) * f.s;
        let node = null;
        if (el.type === 'text') {
            const text = el.text_i18n_key ? t(el.text_i18n_key, el.text || '') : (el.text || '');
            node = new Konva.Text({
                x: X(el.x), y: Y(el.y),
                width: el.w ? S(el.w) : undefined,
                text,
                fontSize: S(el.fontSize || 96),
                fontFamily: el.fontFamily || 'Inter',
                fontStyle: el.fontStyle || 'normal',
                fill: el.fill || '#111',
                align: el.align || 'left',
                lineHeight: el.lineHeight || 1.2,
                padding: 4,
            });
        } else if (el.type === 'shape') {
            node = makeShape(el.shape, {
                x: X(el.x), y: Y(el.y),
                w: S(el.w || 100), h: S(el.h || 100),
                fill: el.fill || '#5cb3ff',
            });
        } else if (el.type === 'frame') {
            // A photo slot. Until now a template could only SUGGEST where a
            // photo goes by leaving a gap — which is not a suggestion, it is a
            // blank space. The frame says "your class photo, this size, here",
            // and a double click fills it.
            node = makeFrameNode(X(el.x), Y(el.y), S(el.w || 800), S(el.h || 600));
        } else if (el.type === 'sticker') {
            if (!window.WNStickers) return null;
            const def = window.WNStickers.getStickerByKey(el.sticker_key);
            if (!def) return null;
            node = window.WNStickers.buildKonvaGroup(def, {
                x: X(el.x), y: Y(el.y), size: S(el.size || 200), color: el.color || null,
            });
        }
        if (!node) return null;
        if (el.rotation) node.rotation(el.rotation);
        if (el.opacity != null) node.opacity(el.opacity);
        return node;
    }

    /**
     * Draw a template into a small bitmap for the picker.
     *
     * Until now every card in the picker showed the same 📰 glyph on the same
     * pink square: eight holiday layouts that a teacher had to open one by one
     * to tell apart. Rendering the template's own JSON means the thumbnail can
     * never drift from what applying it produces, and no image assets have to
     * be generated, shipped or kept in sync.
     */
    async function renderTemplateThumb(tpl, maxW) {
        const Konva = window.Konva;
        if (!Konva) return null;
        const cols = tpl.canvas?.sheets?.cols || 3;
        const rows = tpl.canvas?.sheets?.rows || 2;
        const orient = tpl.canvas?.orient || 'landscape';
        const sheetW = orient === 'landscape' ? 297 : 210;
        const sheetH = orient === 'landscape' ? 210 : 297;
        const srcW = cols * sheetW * PX_PER_MM;
        const srcH = rows * sheetH * PX_PER_MM;

        const w = maxW || 320;
        const scale = w / srcW;
        const h = Math.round(srcH * scale);

        const host = document.createElement('div');
        host.style.cssText = 'position:absolute;left:-99999px;top:-99999px';
        document.body.appendChild(host);
        try {
            const stage = new Konva.Stage({ container: host, width: Math.round(w), height: h });
            const layer = new Konva.Layer({ listening: false, scaleX: scale, scaleY: scale });
            layer.add(new Konva.Rect({
                x: 0, y: 0, width: srcW, height: srcH,
                fill: tpl.canvas?.background?.color || '#ffffff',
            }));
            for (const el of (tpl.elements || [])) {
                const node = buildTemplateNode(el, TPL_IDENTITY);
                if (node) { node.draggable(false); layer.add(node); }
            }
            stage.add(layer);
            stage.draw();
            const url = stage.toDataURL({ pixelRatio: 1, mimeType: 'image/jpeg', quality: 0.82 });
            stage.destroy();
            return url;
        } catch (e) {
            return null;
        } finally {
            host.remove();
        }
    }


    /**
     * Pull a freshly-poured template inside the board.
     *
     * templateFitTransform() scales by the NOMINAL canvas the template was
     * authored against, which is not the same as the box its elements actually
     * occupy: a text block reflows to the current font metrics and can end up
     * a few millimetres past the paper edge, where it is simply cut off. This
     * measures what was really produced and, only if it overflows, shrinks the
     * lot about the board's centre. Templates that already fit are untouched.
     */
    function fitTemplateNodesToCanvas(nodes) {
        if (!nodes.length) return;
        const cv = state.project.canvas;
        const layer = state.konva.layers.content;
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const n of nodes) {
            const r = n.getClientRect({ relativeTo: layer });
            minX = Math.min(minX, r.x); minY = Math.min(minY, r.y);
            maxX = Math.max(maxX, r.x + r.width); maxY = Math.max(maxY, r.y + r.height);
        }
        if (!Number.isFinite(minX)) return;

        const inset = 4 * PX_PER_MM;          // keep a little paper around it
        const availW = cv.width_px - 2 * inset;
        const availH = cv.height_px - 2 * inset;
        const w = maxX - minX, h = maxY - minY;
        const k = Math.min(1, availW / Math.max(1, w), availH / Math.max(1, h));

        const cx = cv.width_px / 2, cy = cv.height_px / 2;
        const bx = (minX + maxX) / 2, by = (minY + maxY) / 2;
        for (const n of nodes) {
            n.x(cx + (n.x() - bx) * k);
            n.y(cy + (n.y() - by) * k);
            if (k !== 1) {
                n.scaleX(n.scaleX() * k);
                n.scaleY(n.scaleY() * k);
            }
        }
    }

    function instantiateTemplateElement(el, tf) {
        const node = buildTemplateNode(el, tf);
        if (!node) return;
        const type = el.type === 'sticker' ? 'sticker' : el.type;
        tagNode(node, { type, shape: el.shape });
        if (type === 'sticker') {
            node.setAttr('elStickerKey', el.sticker_key);
            const def = window.WNStickers?.getStickerByKey(el.sticker_key);
            node.setAttr('elStickerColor', el.color || def?.defaultColor || null);
        }
        if (type === 'text') node.on('dblclick dbltap', () => startTextEdit(node));
        if (type === 'frame') node.on('dblclick dbltap', () => pickPhotoForFrame(node));
        attachNodeInteractions(node);
        state.konva.layers.content.add(node);
    }


    // ─── Onboarding tour ──────────────────────────────────────────────
    // Two-stage tour: welcome card on the start screen, then 3 workspace
    // cards once the user creates / opens a project. The "onboarded" flag
    // is only set after Skip OR the final step of the workspace tour, so a
    // user who closes the welcome card still gets the workspace explanation.
    /* There is no welcome step any more.
     *
     * It fired on page load and spotlighted `.wn-start-grid`, which sits below
     * the H1, the intro and the how-to — so a first-time visitor got a dark
     * modal over a page they had not read yet, anchored to something off the
     * bottom of the screen. It also said nothing the three start cards do not
     * say themselves. The workspace tour below stays: it fires once the user
     * has chosen to start, and it points at controls that are on screen. */
    const OB_WELCOME = [];
    const OB_WORKSPACE = [
        { titleKey: 'ob_tools_title',  msgKey: 'ob_tools_msg',  target: '.wn-leftrail' },
        { titleKey: 'ob_props_title',  msgKey: 'ob_props_msg',  target: '.wn-rightpanel' },
        { titleKey: 'ob_save_title',   msgKey: 'ob_save_msg',   target: '#wnFileBtn' },
        { titleKey: 'ob_export_title', msgKey: 'ob_export_msg', target: '#wnExportPDF' },
    ];

    let _obSteps = [];
    let _obIndex = 0;
    let _obIsWorkspace = false;

    function isOnboarded() {
        try { return !!window.__toolSettings?.load?.(SLUG, 'onboarded', false); }
        catch (e) { return false; }
    }
    function setOnboarded() {
        try { window.__toolSettings?.save?.(SLUG, 'onboarded', true); } catch (e) { /* noop */ }
    }

    function maybeStartOnboarding(kind) {
        if (isOnboarded()) return;
        _obIsWorkspace = (kind === 'workspace');
        _obSteps = _obIsWorkspace ? OB_WORKSPACE : OB_WELCOME;
        if (!_obSteps.length) return;
        _obIndex = 0;
        // Defer one frame so target elements have settled their layout
        // (especially after the workspace just became visible).
        requestAnimationFrame(() => requestAnimationFrame(showOnboardingStep));
        ae('onboarding_open', { kind });
    }

    function showOnboardingStep() {
        const step = _obSteps[_obIndex];
        if (!step) return;
        const ob = $('wnOnboarding');
        $('wnObTitle').textContent = step.titleKey ? t(step.titleKey, '') : '';
        $('wnObMsg').textContent   = step.msgKey   ? t(step.msgKey,   '') : '';
        $('wnObStep').textContent  = `${_obIndex + 1} / ${_obSteps.length}`;

        $('wnObPrev').hidden = _obIndex === 0;
        $('wnObNext').textContent = (_obIndex === _obSteps.length - 1)
            ? t('ob_done', 'Got it')
            : t('ob_next', 'Next');

        positionOnboarding(step.target);
        ob.hidden = false;
    }

    function positionOnboarding(targetSelector) {
        const sp = $('wnObSpotlight');
        const card = $('wnObCard');
        const ob = $('wnOnboarding');
        const target = targetSelector ? document.querySelector(targetSelector) : null;

        // On narrow viewports (phones) and / or when the target is off-screen,
        // a side-of-target card placement breaks because cardW (340) is wider
        // than half the screen. Use a centered, no-spotlight modal instead.
        const isNarrow = window.innerWidth < 720;

        if (target && !isNarrow) {
            // Make sure the target is in view before measuring. block:'nearest'
            // is non-disruptive — only scrolls if actually off-screen, and
            // CLAUDE.md global rule allows this (no jump-to-top).
            try { target.scrollIntoView({ block: 'nearest', inline: 'nearest' }); } catch (e) {}
            const r = target.getBoundingClientRect();
            sp.style.left   = (r.left - 6)  + 'px';
            sp.style.top    = (r.top  - 6)  + 'px';
            sp.style.width  = (r.width + 12)  + 'px';
            sp.style.height = (r.height + 12) + 'px';
            sp.hidden = false;
            ob.dataset.noSpotlight = 'false';

            const cardW = Math.min(340, window.innerWidth - 28);
            const cardH = card.offsetHeight || 180;
            const m = 14;
            let left, top;
            if (r.right + m + cardW < window.innerWidth) {
                left = r.right + m;
                top = Math.max(m, Math.min(r.top, window.innerHeight - cardH - m));
            } else if (r.bottom + m + cardH < window.innerHeight) {
                top = r.bottom + m;
                left = Math.max(m, Math.min(r.left, window.innerWidth - cardW - m));
            } else if (r.left - m - cardW > 0) {
                left = r.left - m - cardW;
                top = Math.max(m, Math.min(r.top, window.innerHeight - cardH - m));
            } else {
                top = Math.max(m, r.top - m - cardH);
                left = Math.max(m, Math.min(r.left, window.innerWidth - cardW - m));
            }
            card.style.left = left + 'px';
            card.style.top  = top  + 'px';
        } else {
            // Centered, no spotlight — works on every viewport size.
            sp.hidden = true;
            ob.dataset.noSpotlight = 'true';
            const cardW = Math.min(340, window.innerWidth - 28);
            const cardH = card.offsetHeight || 200;
            card.style.left = Math.max(14, (window.innerWidth - cardW) / 2) + 'px';
            card.style.top  = Math.max(14, (window.innerHeight - cardH) / 2) + 'px';
        }
    }

    function closeOnboarding(skipped) {
        $('wnOnboarding').hidden = true;
        // Mark "onboarded" if the user skipped OR finished the workspace tour.
        // Finishing only the welcome step does not flip the flag — the user
        // still gets the workspace tour the moment they open a project.
        if (skipped || _obIsWorkspace) setOnboarded();
        _obSteps = [];
        ae('onboarding_close', { skipped, kind: _obIsWorkspace ? 'workspace' : 'welcome' });
    }

    function wireOnboarding() {
        $('wnObSkip').onclick = () => closeOnboarding(true);
        $('wnObNext').onclick = () => {
            _obIndex++;
            if (_obIndex >= _obSteps.length) closeOnboarding(false);
            else showOnboardingStep();
        };
        $('wnObPrev').onclick = () => {
            _obIndex = Math.max(0, _obIndex - 1);
            showOnboardingStep();
        };
        // Re-position card on viewport resize while a step is showing
        window.addEventListener('resize', () => {
            if (!$('wnOnboarding').hidden && _obSteps[_obIndex]) {
                positionOnboarding(_obSteps[_obIndex].target);
            }
        });
    }

    // ─── Preflight ────────────────────────────────────────────────────
    /**
     * Look for the things that go wrong only after printing.
     *
     * Every problem listed here is invisible on screen and expensive on paper:
     * a phone photo stretched across half a metre looks fine at fit zoom and
     * prints as mush; a caption at 3 mm is legible on a monitor and not on a
     * wall; an element sitting outside the printable area simply is not there
     * when the sheet comes out. The editor knows all of it before the job
     * starts, which is the one thing a tool that only receives a finished
     * image can never do.
     *
     * Each finding carries the node so the list can take you to it.
     */
    const PREFLIGHT_MIN_DPI = 110;      // below this a photo is visibly soft on paper
    const PREFLIGHT_MIN_TEXT_MM = 4;    // cap height a person reads from a metre

    function runPreflight() {
        const out = [];
        if (!state.konva || !state.project) return out;
        const L = boardLayout();
        const ppmm = L.px_per_mm;
        const nodes = state.konva.layers.content.children.filter((n) => n.hasName?.('wn-obj'));

        for (const n of nodes) {
            const type = n.getAttr('elType');
            if (n.visible() === false) continue;
            const r = n.getClientRect({ relativeTo: state.konva.layers.content, skipShadow: true });

            // ── outside the printable area ──
            let covered = 0, straddles = false;
            for (let rr = 0; rr < L.rows; rr++) {
                for (let cc = 0; cc < L.cols; cc++) {
                    if (!L.pageExists(cc, rr)) continue;
                    const t0 = L.tileRectPX(cc, rr);
                    const ox = Math.min(r.x + r.width, t0.x + t0.w) - Math.max(r.x, t0.x);
                    const oy = Math.min(r.y + r.height, t0.y + t0.h) - Math.max(r.y, t0.y);
                    if (ox > 1 && oy > 1) {
                        covered += ox * oy;
                        const fully = r.x >= t0.x - 1 && r.y >= t0.y - 1
                            && r.x + r.width <= t0.x + t0.w + 1 && r.y + r.height <= t0.y + t0.h + 1;
                        if (!fully) straddles = true;
                    }
                }
            }
            const area = Math.max(1, r.width * r.height);
            if (covered < area * 0.02) {
                out.push({ node: n, kind: 'off', level: 'error' });
            } else if (covered < area * 0.98) {
                // On separate pages this tears the element between two pockets;
                // on a glued poster it only means a seam runs through it.
                out.push({ node: n, kind: L.mode === 'pages' ? 'split' : 'seam', level: L.mode === 'pages' ? 'error' : 'warn' });
            }

            // ── a photo asked to cover more paper than it has pixels for ──
            if (type === 'image' && n.image && n.image()) {
                const img = n.image();
                const crop = n.crop && n.crop();
                const srcW = crop && crop.width ? crop.width : img.naturalWidth;
                const printedMM = (n.width() * Math.abs(n.scaleX() || 1)) / ppmm;
                if (srcW && printedMM > 1) {
                    const dpi = srcW / (printedMM / 25.4);
                    if (dpi < PREFLIGHT_MIN_DPI) {
                        out.push({ node: n, kind: 'lowres', level: dpi < PREFLIGHT_MIN_DPI * 0.7 ? 'error' : 'warn', dpi: Math.round(dpi) });
                    }
                }
            }

            // ── type too small to read on a wall ──
            if (type === 'text' && n.fontSize) {
                const mm = (n.fontSize() * Math.abs(n.scaleY() || 1)) / ppmm;
                if (mm < PREFLIGHT_MIN_TEXT_MM) {
                    out.push({ node: n, kind: 'tinytext', level: 'warn', mm: Math.round(mm * 10) / 10 });
                }
            }
        }

        // A photo slot left empty prints as a dashed placeholder rectangle.
        for (const n of nodes) {
            if (n.getAttr('elType') === 'frame') out.push({ node: n, kind: 'emptyframe', level: 'warn' });
        }
        return out;
    }

    function preflightMessage(f) {
        if (f.kind === 'off')        return t('pf_off', 'Outside every page — will not print.');
        if (f.kind === 'split')      return t('pf_split', 'Split across two pages.');
        if (f.kind === 'seam')       return t('pf_seam', 'A seam runs through it.');
        if (f.kind === 'lowres')     return t('pf_lowres', 'Photo is only :dpi dpi at this size — it will look soft.').replace(':dpi', f.dpi);
        if (f.kind === 'tinytext')   return t('pf_tinytext', 'Text is :mm mm — too small to read on a wall.').replace(':mm', f.mm);
        if (f.kind === 'emptyframe') return t('pf_emptyframe', 'Empty photo slot — it prints as a dashed box.');
        return '';
    }

    function preflightLabel(f) {
        const type = f.node.getAttr('elType');
        if (type === 'text' && f.node.text) return f.node.text().slice(0, 28) || t('layer_text', 'Text');
        const fn = TYPE_LABELS[type];
        return fn ? fn() : type;
    }

    /** Select the offending element and bring it on screen. */
    function revealPreflight(f) {
        closePrintModal();
        state.konva.tr.nodes([f.node]);
        const r = f.node.getClientRect({ relativeTo: state.konva.layers.content });
        setZoom(Math.max(state.zoom, 0.35));
        centreOn(r.x + r.width / 2, r.y + r.height / 2);
        renderProperties();
        renderLayers();
        ae('preflight_reveal', { kind: f.kind });
    }

    function renderPreflight() {
        const host = $('wnPreflight');
        if (!host) return;
        const findings = runPreflight();
        _preflightFindings = findings;
        if (!findings.length) {
            host.className = 'wn-preflight is-clean';
            host.innerHTML = `<span class="wn-preflight-ok">${escapeHtml(t('pf_clean', 'Everything fits — nothing will be cut or blurry.'))}</span>`;
            return;
        }
        const errs = findings.filter((f) => f.level === 'error').length;
        host.className = 'wn-preflight' + (errs ? ' is-error' : ' is-warn');

        /* Group by kind. A class roster leaves twenty-four empty photo slots,
           and listing them one by one buries the single low-res photo that
           actually needs attention under two dozen identical lines. */
        const groups = [];
        const byKind = new Map();
        for (let i = 0; i < findings.length; i++) {
            const f = findings[i];
            let g = byKind.get(f.kind);
            if (!g) { g = { kind: f.kind, level: f.level, first: i, items: [] }; byKind.set(f.kind, g); groups.push(g); }
            g.items.push(i);
            if (f.level === 'error') g.level = 'error';
        }
        groups.sort((a, b) => (a.level === b.level ? 0 : a.level === 'error' ? -1 : 1));

        host.innerHTML =
            `<div class="wn-preflight-head">${escapeHtml(
                t('pf_found', 'Before you print: :n things to look at').replace(':n', findings.length))}</div>`
            + '<ul class="wn-preflight-list">'
            + groups.map((g) => {
                const f = findings[g.first];
                const what = g.items.length > 1
                    ? t('pf_count', ':n elements').replace(':n', g.items.length)
                    : preflightLabel(f);
                return `<li class="wn-preflight-item is-${g.level}">`
                    + `<button type="button" data-pf="${g.first}">`
                    + `<span class="wn-pf-what">${escapeHtml(what)}</span>`
                    + `<span class="wn-pf-why">${escapeHtml(preflightMessage(f))}</span>`
                    + '</button></li>';
            }).join('')
            + '</ul>';
        $$('[data-pf]', host).forEach((b) => {
            b.onclick = () => revealPreflight(_preflightFindings[parseInt(b.dataset.pf, 10)]);
        });
    }

    let _preflightFindings = [];

    // ─── Print preview modal ──────────────────────────────────────────
    const _printState = {
        row: 0, col: 0, busy: false,
        /** null = every sheet; otherwise the set of "c,r" keys to print. */
        sheets: null,
        options: {
            withCropMarks: true, withLabels: true, withOverlap: true,
            withRuler: true, withMarks: true, dpi: 200, grayscale: false,
        },
    };

    function printSheetSet() {
        const L = boardLayout();
        if (!_printState.sheets) {
            const all = new Set();
            for (let r = 0; r < L.rows; r++) {
                for (let c = 0; c < L.cols; c++) {
                    if (L.pageExists(c, r)) all.add(c + ',' + r);
                }
            }
            return all;
        }
        return _printState.sheets;
    }

    function printOptions() {
        const chosen = printSheetSet();
        const L = boardLayout();
        const everything = chosen.size === L.pages;
        return Object.assign({}, _printState.options, {
            i18n: printI18n(),
            sheets: everything ? null : chosen,
        });
    }

    /* Captions that end up printed on the paper itself. They are passed down
       rather than read inside printing.js so the exporter stays locale-free. */
    function printI18n() {
        const cv = state.project?.canvas;
        return {
            glue_band_caption: t('print_glue_caption', 'glue: overlap here'),
            ruler_caption: t('print_ruler_caption', '100 mm — print at 100%, no scaling'),
            // Pages print their own name in the margin instead of a grid id.
            page_names: Array.isArray(cv?.page_titles) ? cv.page_titles.slice() : null,
        };
    }

    async function openPrintModal() {
        const modal = $('wnPrintModal');
        syncConsentInset();
        modal.hidden = false;

        // Reset progress, sync checkboxes
        $('wnPrintProgress').hidden = true;
        $('wnPrintProgressFill').style.width = '0%';
        const L0 = boardLayout();
        // Crop marks default OFF for separate pages: a page goes into its
        // pocket as a whole A4 sheet, so the marks would just be printed
        // scribbles. Still offered, because some stands take a trimmed sheet.
        if (!_printState.cropTouched) {
            _printState.options.withCropMarks = L0.mode !== 'pages';
        }
        $('wnOptCropMarks').checked = _printState.options.withCropMarks;
        $('wnOptLabels').checked    = _printState.options.withLabels;
        $('wnOptOverlap').checked   = _printState.options.withOverlap;
        const rulerCb = $('wnOptRuler');
        if (rulerCb) rulerCb.checked = _printState.options.withRuler;
        const marksCb = $('wnOptMarks');
        if (marksCb) marksCb.checked = _printState.options.withMarks;
        $$('.wn-seg-btn[data-dpi]').forEach((b) => {
            const active = parseInt(b.dataset.dpi, 10) === _printState.options.dpi;
            b.classList.toggle('is-active', active);
            b.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        $$('.wn-seg-btn[data-ink]').forEach((b) => {
            const active = (b.dataset.ink === 'bw') === !!_printState.options.grayscale;
            b.classList.toggle('is-active', active);
            b.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        // Separate pages have no assembled sheet to produce.
        const oneBtn = $('wnPrintOne');
        if (oneBtn) oneBtn.hidden = L0.mode === 'pages' || L0.pages <= 1;

        // A board with no seams has nothing to glue — hide the option instead
        // of offering a control that cannot do anything.
        // Anything about gluing is meaningless when the sheets are separate
        // pages, and a "glue overlap" checkbox on a classroom corner would be
        // an instruction to do the wrong thing.
        const noSeams = L0.mode === 'pages' || (L0.cols === 1 && L0.rows === 1) || L0.overlap_mm <= 0;
        const overlapRow = $('wnOptOverlapRow');
        if (overlapRow) overlapRow.hidden = noSeams;
        const marksRow = $('wnOptMarksRow');
        if (marksRow) marksRow.hidden = noSeams;
        const sheetsWrap = $('wnPrintTilePick')?.closest('.wn-print-sheets');
        if (sheetsWrap) sheetsWrap.hidden = L0.pages <= 1;
        const printDesc = $('wnPrintDesc');
        if (printDesc) printDesc.textContent = L0.mode === 'pages'
            ? t('print_desc_pages', '') : t('print_desc', '');
        // "A1, B1… and arrows to the neighbours" describes a tiled poster. A
        // set of pages has neither grid ids nor neighbours, and its label is
        // the page's own name.
        const labelsDesc = $('wnOptLabelsDesc');
        if (labelsDesc) labelsDesc.textContent = L0.mode === 'pages'
            ? t('opt_sheet_labels_desc_pages', '') : t('opt_sheet_labels_desc', '');
        const printTitle = $('wnPrintTitle');
        if (printTitle) printTitle.textContent = L0.mode === 'pages'
            ? t('print_title_pages', '') : t('print_title', '');

        renderPrintSheetMap();

        updatePrintInfo();
        renderPreflight();

        await refreshPrintPreview();
    }

    /**
     * The sheet map: one cell per page, laid out in the board's real shape.
     * Tapping a cell previews it; its checkbox drops it from the job. Keeping
     * both on one control is deliberate — the sheet you are looking at is
     * almost always the sheet you are deciding about.
     */
    function renderPrintSheetMap() {
        const L = boardLayout();
        const pickEl = $('wnPrintTilePick');
        if (!pickEl) return;
        const chosen = printSheetSet();
        pickEl.style.gridTemplateColumns = `repeat(${L.cols}, minmax(0, 1fr))`;
        pickEl.innerHTML = '';

        for (let r = 0; r < L.rows; r++) {
            for (let c = 0; c < L.cols; c++) {
                if (!L.pageExists(c, r)) {
                    // Keep the grid shape so the map still reads as the board.
                    const hole = document.createElement('div');
                    hole.className = 'wn-print-tile-cell is-hole';
                    pickEl.appendChild(hole);
                    continue;
                }
                const key = c + ',' + r;
                const on = chosen.has(key);
                const cell = document.createElement('div');
                cell.className = 'wn-print-tile-cell'
                    + (r === _printState.row && c === _printState.col ? ' is-active' : '')
                    + (on ? '' : ' is-off');

                const pick = document.createElement('button');
                pick.type = 'button';
                pick.className = 'wn-print-tile-name';
                pick.textContent = sheetName(c, r);
                pick.title = t('print_pick_tile', 'Preview this sheet');
                pick.onclick = () => {
                    _printState.row = r;
                    _printState.col = c;
                    renderPrintSheetMap();
                    refreshPrintPreview();
                };

                const box = document.createElement('input');
                box.type = 'checkbox';
                box.className = 'wn-print-tile-box';
                box.checked = on;
                box.setAttribute('aria-label', window.WNGeom.sheetLabel(c, r));
                box.onchange = () => {
                    const next = new Set(printSheetSet());
                    if (box.checked) next.add(key); else next.delete(key);
                    // Never let the job become empty — an export producing zero
                    // pages would download a valid, useless file.
                    if (next.size === 0) { box.checked = true; return; }
                    _printState.sheets = next;
                    renderPrintSheetMap();
                    updatePrintInfo();
                };

                cell.appendChild(box);
                cell.appendChild(pick);
                pickEl.appendChild(cell);
            }
        }

        const all = $('wnPrintSheetsAll');
        if (all) {
            const everything = chosen.size === L.pages;
            all.textContent = everything ? t('print_select_none', 'Only this one') : t('print_select_all', 'All sheets');
            all.onclick = () => {
                _printState.sheets = everything
                    ? new Set([_printState.col + ',' + _printState.row])
                    : null;
                renderPrintSheetMap();
                updatePrintInfo();
            };
        }
    }

    function updatePrintInfo() {
        const info = $('wnPrintInfo');
        if (!info) return;
        const L = boardLayout();
        const chosen = printSheetSet();
        const total = L.pages;
        const line = chosen.size === total
            ? sheetsCountLabel(total)
            : t('print_partial', ':n of :total sheets').replace(':n', chosen.size).replace(':total', total);
        // A pages board has no assembled size — quoting the editing surface
        // would name a rectangle that never exists on any wall.
        const size = L.mode === 'pages'
            ? `${fmtCm(L.usable_w_mm)}×${fmtCm(L.usable_h_mm)} ${escapeHtml(t('cm_label', 'cm'))} ${escapeHtml(t('size_each_page', 'each'))}`
            : `${fmtCm(L.poster_w_mm)}×${fmtCm(L.poster_h_mm)} ${escapeHtml(t('cm_label', 'cm'))}`;
        info.innerHTML =
            `${escapeHtml(line)} <span class="wn-size-info-sep">·</span> ${size} ` +
            `<span class="wn-size-info-sep">·</span> ${_printState.options.dpi} dpi` +
            (_printState.options.grayscale
                ? ` <span class="wn-size-info-sep">·</span> ${escapeHtml(t('opt_ink_bw', 'B&W'))}` : '');
    }

    async function refreshPrintPreview() {
        const img = $('wnPrintPreview');
        const empty = $('wnPrintPreviewEmpty');
        img.style.visibility = 'hidden';
        empty.hidden = false;
        empty.textContent = t('loading', 'Loading…');

        try {
            const contentNodes = state.konva.layers.content.children.filter((n) => n.hasName?.('wn-obj'));
            const url = await window.WNPrinting.tilePreviewURL({
                project: state.project,
                contentNodes,
                row: _printState.row,
                col: _printState.col,
                options: printOptions(),
                maxW: 620,
            });
            img.src = url;
            img.onload = () => {
                empty.hidden = true;
                img.style.visibility = 'visible';
            };
        } catch (e) {
            empty.textContent = t('preview_failed', 'Could not render preview');
            aeCritical('print_preview_fail', { err: e.message });
        }
    }

    function closePrintModal() {
        $('wnPrintModal').hidden = true;
    }

    async function runPrintExport(format) {
        if (_printState.busy) return;
        _printState.busy = true;
        const progressWrap = $('wnPrintProgress');
        const progressFill = $('wnPrintProgressFill');
        const progressLabel = $('wnPrintProgressLabel');
        progressWrap.hidden = false;
        progressFill.style.width = '0%';
        progressLabel.textContent = format === 'single'
            ? t('export_running_single', 'Building one large sheet…')
            : t('export_running', 'Building PDF…');

        const onProgress = (done, total) => {
            const pct = Math.round((done / total) * 100);
            progressFill.style.width = pct + '%';
            progressLabel.textContent = `${done}/${total}`;
        };

        try {
            const contentNodes = state.konva.layers.content.children.filter((n) => n.hasName?.('wn-obj'));
            let blob, ext;
            const start = Date.now();
            if (format === 'pdf') {
                blob = await window.WNPrinting.exportPDF({
                    project: state.project, contentNodes, options: printOptions(), onProgress,
                });
                ext = '.pdf';
            } else if (format === 'single') {
                blob = await window.WNPrinting.exportSinglePDF({
                    project: state.project, contentNodes, options: printOptions(), onProgress,
                });
                ext = '-one-sheet.pdf';
            } else {
                blob = await window.WNPrinting.exportPNGZip({
                    project: state.project, contentNodes, options: printOptions(), onProgress,
                });
                ext = '-pages.zip';
            }
            const elapsed = Date.now() - start;
            // The single-sheet path caps its pixel budget, so it can come out
            // below the requested dpi on a big board. Saying nothing would be
            // the quiet kind of downgrade a user only discovers on paper.
            if (format === 'single' && blob.effDpi && blob.effDpi < _printState.options.dpi - 5) {
                toast(t('export_single_dpi', 'One-sheet file built at :dpi dpi — plenty for a poster this size.')
                    .replace(':dpi', blob.effDpi), 'info');
            }
            ae('export_done', { format, ms: elapsed, sizeKb: Math.round(blob.size / 1024) });
            // Tier-0: successful export is the tool's primary completion event
            aeCritical('tool_completed', { action: 'export', format, ms: elapsed, sizeKb: Math.round(blob.size / 1024) });
            downloadBlob(blob, sanitizeFilename(state.project.meta.title) + ext);
            closePrintModal();
            toast(t('export_done', 'Export ready — check downloads'), 'success');
        } catch (e) {
            aeCritical('export_fail', { format, err: e.message });
            aeCritical('tool_error', { stage: 'export', format, err: e.message });
            toast((t('save_error', 'Could not save')) + (e.message ? `: ${e.message}` : ''), 'error');
            progressLabel.textContent = t('save_error', 'Error');
        } finally {
            _printState.busy = false;
        }
    }

    function wirePrintModal() {
        $('wnPrintCancel').onclick = closePrintModal;
        $('wnPrintPdf').onclick    = () => runPrintExport('pdf');
        $('wnPrintZip').onclick    = () => runPrintExport('zip');
        const refreshOpts = (key) => (e) => {
            _printState.options[key] = e.target.checked;
            refreshPrintPreview();
        };
        $('wnOptCropMarks').onchange = (e) => {
            _printState.cropTouched = true;
            refreshOpts('withCropMarks')(e);
        };
        $('wnOptLabels').onchange    = refreshOpts('withLabels');
        $('wnOptOverlap').onchange   = refreshOpts('withOverlap');
        if ($('wnOptRuler')) $('wnOptRuler').onchange = refreshOpts('withRuler');
        if ($('wnOptMarks')) $('wnOptMarks').onchange = refreshOpts('withMarks');
        $$('.wn-seg-btn[data-dpi]').forEach((b) => {
            b.onclick = () => {
                _printState.options.dpi = parseInt(b.dataset.dpi, 10);
                $$('.wn-seg-btn[data-dpi]').forEach((x) => {
                    const active = x === b;
                    x.classList.toggle('is-active', active);
                    x.setAttribute('aria-pressed', active ? 'true' : 'false');
                });
                updatePrintInfo();
            };
        });
        $$('.wn-seg-btn[data-ink]').forEach((b) => {
            b.onclick = () => {
                _printState.options.grayscale = b.dataset.ink === 'bw';
                $$('.wn-seg-btn[data-ink]').forEach((x) => {
                    const active = x === b;
                    x.classList.toggle('is-active', active);
                    x.setAttribute('aria-pressed', active ? 'true' : 'false');
                });
                updatePrintInfo();
                // The preview is the promise about what comes out of the
                // printer, so it has to change with the ink.
                refreshPrintPreview();
            };
        });
        if ($('wnPrintOne')) $('wnPrintOne').onclick = () => runPrintExport('single');
    }

    // ─── Storage integration ──────────────────────────────────────────
    // serializeProjectFull → builds the project.json payload for export
    function serializeProjectFull() {
        const cv = state.project.canvas;
        const elements = state.konva.layers.content.children
            .filter((n) => n.hasName?.('wn-obj'))
            .map((n, idx) => Object.assign({ z: idx }, serializeNode(n)));
        // Keep the same shape we use elsewhere — a superset of the v1.0 schema.
        return {
            version: '1.0',
            meta: Object.assign({}, state.project.meta, { modified_at: new Date().toISOString() }),
            canvas: cv,
            guides: state.project.guides,
            groups: state.project.groups || [],
            elements,
        };
    }

    async function applyProjectFull({ project, assets }) {
        leaveWorkspace();
        state.project = project;
        state.assets = assets || new Map();
        $('wnTitle').value = project.meta?.title || t('untitled', 'Untitled');

        $('wnStart').hidden = true;
        $('wnWorkspace').hidden = false;
        $('wnWorkspace').classList.add('active');
        document.body.classList.add('wn-workspace-active');

        try { await whenKonvaReady(); }
        catch (e) {
            toast(t('konva_load_fail', 'Could not load graphics engine.'), 'error');
            return;
        }
        buildStage();
        renderPageNav();

        // Re-create elements via the same factories used by undo/redo.
        (project.elements || [])
            .slice()
            .sort((a, b) => (a.z || 0) - (b.z || 0))
            .forEach((el) => deserializeNode(el));
        state.konva.layers.content.batchDraw();

        renderLayers();
        renderProperties();
        installKeyboardShortcuts();
        installCanvasDragDrop();
        installPasteHandler();
        installViewportGestures();
        installContextMenu();
        zoomToFit();
        announce('Project loaded');

        state.history.stack = [];
        state.history.ptr = -1;
        pushHistory();
        setSaveState('saved');
        state.dirty = false;
    }

    async function buildProjectZipBlob() {
        const project = serializeProjectFull();
        const thumbnail = await window.WNStorage.canvasThumbnail(state.konva.stage, 800);
        const blob = await window.WNStorage.exportZip({
            project,
            assets: state.assets,
            thumbnailBlob: thumbnail,
        });
        return { blob, thumbnail, project };
    }

    async function idbPersist(blob, thumbnail) {
        if (!state.project) return;
        await window.WNStorage.idbPut({
            id: state.project.meta.id,
            title: state.project.meta.title,
            modified_at: Date.now(),
            thumbnail_blob: thumbnail || null,
            project_zip_blob: blob,
            size_bytes: blob.size,
            version: state.project.version || '1.0',
        });
        await window.WNStorage.idbPutSetting('last_session_id', state.project.meta.id);
        await window.WNStorage.idbCleanup(state.project.meta.id);
        ae('autosave_ok', { size: blob.size });
    }

    // ─── Auto-save ────────────────────────────────────────────────────
    let _autoSaveTimer = null;
    let _autoSaveBusy  = false;
    let _autoSaveDisabled = false;

    // A save builds the whole ZIP (every photo recompressed) — on a photo-heavy board
    // that outlasts the 2 s debounce. A save that came due meanwhile used to return
    // and was never rescheduled, and the running one then marked the board «saved»
    // with the newer edit missing. Each change bumps state._editSeq; a save marks
    // «saved» only if nothing changed since it started, and one that came due while
    // another was running runs after it.
    let _autoSaveAgain = false;

    function scheduleAutoSave() {
        if (_autoSaveDisabled || !state.project) return;
        state._editSeq = (state._editSeq || 0) + 1;
        setSaveState('dirty');
        clearTimeout(_autoSaveTimer);
        _autoSaveTimer = setTimeout(doAutoSave, 2000);
    }

    async function flushAutoSave() {
        clearTimeout(_autoSaveTimer);
        _autoSaveTimer = null;
        if (state.project && state.dirty) await doAutoSave();
    }

    async function doAutoSave() {
        if (!state.project) return;
        if (_autoSaveBusy) { _autoSaveAgain = true; return; }
        _autoSaveBusy = true;
        const seq = state._editSeq || 0;
        try {
            setSaveState('saving');
            const { blob, thumbnail } = await buildProjectZipBlob();
            await idbPersist(blob, thumbnail);
            if ((state._editSeq || 0) === seq) {
                setSaveState('saved');
                state.dirty = false;
            } else {
                setSaveState('dirty');
                _autoSaveAgain = true;
            }
        } catch (e) {
            // QuotaExceeded → drop oldest project and retry once.
            if (isQuotaError(e) && !doAutoSave._retried) {
                doAutoSave._retried = true;
                try {
                    const all = await window.WNStorage.idbList();
                    const oldest = all.filter((p) => p.id !== state.project.meta.id).slice(-1)[0];
                    if (oldest) await window.WNStorage.idbDelete(oldest.id);
                    _autoSaveBusy = false;
                    return doAutoSave();
                } catch (_) { /* fall through */ }
            }
            doAutoSave._retried = false;
            _autoSaveDisabled = true;
            setSaveState('error');
            aeCritical('autosave_fail', { err: e.message || String(e), code: e.code });
            toast(t('autosave_disabled', 'Auto-save is unavailable in this browser. Use File → Save to keep a copy.'), 'warning');
        } finally {
            _autoSaveBusy = false;
            if (_autoSaveAgain && !_autoSaveDisabled) {
                _autoSaveAgain = false;
                clearTimeout(_autoSaveTimer);
                _autoSaveTimer = setTimeout(doAutoSave, 2000);
            }
        }
    }

    function isQuotaError(e) {
        return e && (e.name === 'QuotaExceededError' || e.code === 'IDB_QUOTA' ||
            (typeof e.message === 'string' && e.message.toLowerCase().includes('quota')));
    }

    let _lastSavedAt = null;
    let _saveTickTimer = null;

    function setSaveState(s) {
        const el = $('wnSaveIndicator');
        if (!el) return;
        el.dataset.state = s; // saved | dirty | saving | error
        if (s === 'saved') _lastSavedAt = Date.now();
        refreshSaveTooltip(s);
        // Tick the tooltip every 20 s while in "saved" so the relative time
        // reads sensibly without manual interaction.
        if (_saveTickTimer) { clearInterval(_saveTickTimer); _saveTickTimer = null; }
        if (s === 'saved') {
            _saveTickTimer = setInterval(() => refreshSaveTooltip('saved'), 20_000);
        }
    }

    function refreshSaveTooltip(s) {
        const el = $('wnSaveIndicator');
        if (!el) return;
        const stateText =
            s === 'saving' ? t('saving', 'Saving…') :
            s === 'saved'  ? t('saved',  'All changes saved') :
            s === 'dirty'  ? t('unsaved','Unsaved changes') :
            s === 'error'  ? t('save_error', 'Save error') : '';
        const labelEl = $('wnSaveLabel');
        // Pill label: short text only — full status (with relative time) lives in the tooltip.
        // The "saved" label is shortened so the pill stays compact in the topbar.
        if (labelEl) labelEl.textContent = stateText;
        if (s === 'saved' && _lastSavedAt) {
            const rel = formatRelativeTime(_lastSavedAt);
            el.title = `${stateText} · ${rel}`;
            el.setAttribute('aria-label', el.title);
        } else {
            el.title = stateText;
            el.setAttribute('aria-label', stateText);
        }
    }

    /**
     * Fill the start screen's template strip.
     *
     * Same live-rendered previews as the picker, capped to the first few, so
     * the landing state shows what the tool produces instead of three empty
     * boxes — and starting from a holiday layout is one click, not two.
     * Non-blocking: if the index cannot be fetched the strip simply stays
     * hidden and the three cards work exactly as before.
     */
    async function renderStartTemplates() {
        const wrap = $('wnStartTemplates');
        const strip = $('wnStartStrip');
        if (!wrap || !strip) return;
        const locale = (window.__LOCALE || document.documentElement.lang || 'en').split('-')[0];
        let items = [];
        try {
            const r = await fetch(dataUrl('/images/wall-newspaper/templates/_index.json'));
            if (!r.ok) return;
            const idx = await r.json();
            items = (idx.templates || []).filter((tpl) => !tpl.locales || tpl.locales.includes(locale));
        } catch (e) { return; }
        if (!items.length) return;

        strip.innerHTML = '';
        for (const tpl of items.slice(0, 6)) {
            const card = document.createElement('button');
            card.type = 'button';
            card.className = 'wn-template-card';
            const name = tpl.name_key ? t(tpl.name_key, tpl.slug) : (tpl.name || tpl.slug);
            card.innerHTML =
                `<div class="wn-template-thumb is-loading" data-thumb></div>` +
                `<div class="wn-template-name">${escapeHtml(name)}</div>`;
            card.onclick = () => applyTemplate(tpl.slug);
            strip.appendChild(card);
            queueTemplateThumb(tpl, card.querySelector('[data-thumb]'));
        }
        wrap.hidden = false;

        const all = $('wnStartTemplatesAll');
        if (all) all.onclick = () => cmdOpenTemplate();
    }

    // ─── Recovery + recent ────────────────────────────────────────────
    async function checkRecovery() {
        if (!window.WNStorage) return;
        let lastId, last;
        try {
            lastId = await window.WNStorage.idbGetSetting('last_session_id');
            if (!lastId) return;
            last = await window.WNStorage.idbGet(lastId);
            if (!last) return;
        } catch (e) { return; }

        // Skip only the very-fresh case (user did a save-then-immediate-refresh
        // — they probably want their state back). The original 2 s gate was
        // too aggressive and hid recovery in the most common case.
        const ageSec = (Date.now() - (last.modified_at || 0)) / 1000;
        if (ageSec < 0.05) return;

        const modal = $('wnRecoveryModal');
        const desc  = $('wnRecoveryDesc');
        const thumbWrap = $('wnRecoveryThumb');
        const restoreBtn = $('wnRecoveryRestore');
        const discardBtn = $('wnRecoveryDiscard');

        const fmt = formatRelativeTime(last.modified_at);
        desc.textContent = (last.title || t('untitled', 'Untitled')) + ' · ' +
            (t('recovery_modified', 'Last modified :time').replace(':time', fmt));

        thumbWrap.innerHTML = '';
        if (last.thumbnail_blob) {
            const img = document.createElement('img');
            img.src = URL.createObjectURL(last.thumbnail_blob);
            img.alt = '';
            img.style.maxWidth = '100%';
            img.style.maxHeight = '180px';
            img.style.borderRadius = '8px';
            img.style.marginBottom = '8px';
            thumbWrap.appendChild(img);
        }
        modal.hidden = false;

        restoreBtn.onclick = async () => {
            modal.hidden = true;
            try {
                const { project, assets } = await window.WNStorage.importZip(last.project_zip_blob);
                await applyProjectFull({ project, assets });
                ae('recovery_restored', {});
            } catch (e) {
                aeCritical('recovery_load_fail', { err: e.message, code: e.code });
                toast(t('load_error', 'Could not open file'), 'error');
            }
        };
        discardBtn.onclick = async () => {
            modal.hidden = true;
            try { await window.WNStorage.idbPutSetting('last_session_id', null); } catch (_) {}
            ae('recovery_discarded', {});
        };
    }

    async function renderRecentProjects() {
        if (!window.WNStorage) return;
        let list = [];
        try { list = await window.WNStorage.idbList(); } catch (e) { return; }
        const root = $('wnRecent');
        const grid = $('wnRecentList');
        if (!list.length) {
            root.hidden = true;
            grid.innerHTML = '';
            return;
        }
        root.hidden = false;
        grid.innerHTML = '';
        list.slice(0, window.WNStorage.KEEP_PROJECTS).forEach((rec) => {
            const card = document.createElement('button');
            card.type = 'button';
            card.className = 'wn-template-card wn-recent-card';
            const thumbUrl = rec.thumbnail_blob ? URL.createObjectURL(rec.thumbnail_blob) : '';
            card.innerHTML =
                (thumbUrl ? `<img class="wn-template-thumb" src="${thumbUrl}" alt="">` :
                            `<div class="wn-template-thumb"></div>`) +
                `<div class="wn-template-name">${escapeHtml(rec.title || t('untitled', 'Untitled'))}</div>` +
                `<div class="wn-recent-time">${escapeHtml(formatRelativeTime(rec.modified_at))}</div>`;
            card.onclick = async () => {
                try {
                    const { project, assets } = await window.WNStorage.importZip(rec.project_zip_blob);
                    await applyProjectFull({ project, assets });
                } catch (e) {
                    aeCritical('recent_load_fail', { err: e.message, code: e.code });
                    toast(t('load_error', 'Could not open file'), 'error');
                }
            };
            grid.appendChild(card);
        });
    }

    function formatRelativeTime(ts) {
        if (!ts) return '';
        const sec = Math.max(1, Math.round((Date.now() - ts) / 1000));
        const min = Math.round(sec / 60);
        const hr  = Math.round(min / 60);
        const day = Math.round(hr / 24);
        if (sec < 60)  return t('time_just_now', 'just now');
        if (min < 60)  return (t('time_min_ago', ':n min ago')).replace(':n', min);
        if (hr  < 24)  return (t('time_hr_ago',  ':n h ago')).replace(':n', hr);
        return (t('time_day_ago', ':n d ago')).replace(':n', day);
    }

    // ─── Loading a .tmb-board file ────────────────────────────────────
    async function loadProjectFromFile(file) {
        try {
            const { project, assets } = await window.WNStorage.importZip(file);
            await applyProjectFull({ project, assets });
            if (project._newer) toast(t('newer_version_warn', 'This file was created in a newer version. Some details may not display correctly.'), 'warning');
            ae('project_loaded', { size: file.size });
        } catch (e) {
            aeCritical('project_load_fail', { err: e.message, code: e.code });
            const code = e.code || '';
            const msg =
                code === 'WRONG_MIMETYPE'  ? t('invalid_file', 'This is not a wall-newspaper project file') :
                code === 'INVALID_ZIP'     ? t('load_error', 'Could not open file') + ' (ZIP)' :
                code === 'CORRUPT_PROJECT' ? t('load_error', 'Could not open file') + ' (project.json)' :
                code === 'MISSING_PROJECT' ? t('load_error', 'Could not open file') + ' (no project.json)' :
                                              t('load_error', 'Could not open file');
            toast(msg, 'error');
        }
    }

    // ─── Download utility ─────────────────────────────────────────────
    function downloadBlob(blob, filename) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 4000);
    }

    function sanitizeFilename(name) {
        const cleaned = String(name || '')
            .replace(/[<>:"/\\|?*\x00-\x1f]/g, '_')
            .replace(/^\.+|\.+$/g, '')
            .trim();
        return cleaned || 'wall-newspaper';
    }

    // ─── Wiring ───────────────────────────────────────────────────────
    function wireStartScreen() {
        // Straight into the size dialog with the pages mode already chosen.
        const corner = $('wnStartCorner');
        if (corner) corner.onclick = () => {
            state.sizeChoice = Object.assign({}, state.sizeChoice, {
                mode: 'pages', pages: state.sizeChoice.pages || 6, orient: 'portrait',
            });
            ae('start_corner', {});
            openSizePicker();
        };

        $('wnStartBlank').onclick = openSizePicker;
        $('wnStartTemplate').onclick = cmdOpenTemplate;
        $('wnLoadProjectInput').onchange = async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            await loadProjectFromFile(file);
            e.target.value = ''; // allow re-pick same file
        };
    }

    function wireWorkspace() {
        $$('.wn-tool').forEach((btn) => {
            btn.onclick = () => selectTool(btn.dataset.tool);
        });
        $('wnZoomIn').onclick = () => setZoom(state.zoom * 1.25);
        $('wnZoomOut').onclick = () => setZoom(state.zoom / 1.25);
        $('wnZoomFit').onclick = zoomToFit;
        $('wnZoomReadout').onclick = () => setZoom(1);
        $('wnUndo').onclick = undo;
        $('wnRedo').onclick = redo;

        // File menu dropdown
        const fileBtn = $('wnFileBtn');
        const fileMenu = $('wnFileMenu');
        fileBtn.onclick = (e) => {
            e.stopPropagation();
            const open = !fileMenu.hidden;
            fileMenu.hidden = open;
            fileBtn.setAttribute('aria-expanded', open ? 'false' : 'true');
        };
        document.addEventListener('click', (e) => {
            if (!fileMenu.hidden && !fileMenu.contains(e.target) && e.target !== fileBtn) {
                fileMenu.hidden = true;
                fileBtn.setAttribute('aria-expanded', 'false');
            }
        });
        $$('button[data-cmd]', fileMenu).forEach((b) => {
            b.onclick = () => {
                fileMenu.hidden = true;
                fileBtn.setAttribute('aria-expanded', 'false');
                const cmd = b.dataset.cmd;
                if (cmd === 'new') cmdNew();
                else if (cmd === 'open') cmdOpen();
                else if (cmd === 'open-template') cmdOpenTemplate();
                else if (cmd === 'save' || cmd === 'save-as') cmdSave();
                else if (cmd === 'save-as-template') cmdSaveAsTemplate();
                else if (cmd === 'share') cmdShare();
                else if (cmd === 'export-pdf' || cmd === 'export-png-zip') cmdExportPDF();
            };
        });

        $('wnExportPDF').onclick = cmdExportPDF;

        // Image input
        $('wnImageInput').onchange = async (e) => {
            const files = Array.from(e.target.files || []);
            // A frame that asked for a photo consumes the first file and any
            // remaining ones land as ordinary photos.
            if (_frameAwaitingPhoto && files.length) {
                const frame = _frameAwaitingPhoto;
                _frameAwaitingPhoto = null;
                if (frame.getParent()) await fillFrameWithBlob(frame, files.shift());
            }
            for (const f of files) await addImageFromBlob(f);
            e.target.value = ''; // allow re-pick same file
        };

        // Inline text editor — commit on blur
        $('wnTextEditor').addEventListener('blur', () => finishTextEdit());

        // Title input
        $('wnTitle').addEventListener('input', () => {
            if (state.project) {
                state.project.meta.title = $('wnTitle').value;
                markDirty();
            }
        });

        window.addEventListener('resize', () => {
            if (state.project && state.konva) zoomToFit();
        });

        window.addEventListener('beforeunload', (e) => {
            // Best-effort flush to IDB so a refresh does not lose the last edits.
            // (Synchronous IDB is impossible; we rely on autosave already firing
            // on most edits — this just covers the 0-2s gap since last debounce.)
            if (state.dirty) {
                e.preventDefault(); e.returnValue = '';
                // Fire-and-forget; the page may unload mid-write but the previous
                // 2s-debounced write is still safe in the store.
                doAutoSave();
            }
        });

        // Tab hide → flush. visibilitychange fires reliably on mobile too.
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden' && state.dirty) {
                flushAutoSave();
            }
        });

        $('wnTemplateCancel').onclick = () => { $('wnTemplateModal').hidden = true; };

        wirePrintModal();
        wireOnboarding();
        wireSettingsPopover();
        wireShareModal();
        wireElToolbar();
        wireMobileDrawer();
        wireZoomUI();
        installSpacePan();
        syncMobileChrome();
        window.addEventListener('resize', syncMobileChrome);
        window.addEventListener('orientationchange', () => setTimeout(syncMobileChrome, 150));
        // Re-position the floating element toolbar on canvas scroll / window resize
        // so it stays anchored to the selection while the user pans or rotates the device.
        const scroll = $('wnCanvasScroll');
        if (scroll) scroll.addEventListener('scroll', positionElToolbar, { passive: true });
        window.addEventListener('resize', positionElToolbar);
        $('wnGuestLeave').onclick = () => {
            if (window.WNCollab?.isActive?.()) window.WNCollab.disconnect();
            leaveGuestMode();
        };
    }

    // ─── Mobile bottom-sheet drawer (properties / layers on phone) ────
    // At <=768px the right panel is hidden by CSS and a fixed FAB toggles
    // a bottom-sheet. Tap outside (backdrop) or scroll the drawer closed
    // to dismiss. Does nothing on desktop.
    function wireMobileDrawer() {
        // The close control lives inside the panel and is positioned by CSS,
        // so it needs nothing but a click handler.
        const closeBtn = $('wnDrawerClose');
        if (closeBtn) closeBtn.onclick = () => {
            document.body.classList.remove('wn-drawer-active');
            $('wnRightPanel')?.classList.remove('wn-drawer-open');
            $('wnDrawerBackdrop')?.classList.remove('is-active');
            $('wnDrawerToggle')?.setAttribute('aria-expanded', 'false');
        };

        const toggle = $('wnDrawerToggle');
        const panel  = $('wnRightPanel');
        const back   = $('wnDrawerBackdrop');
        if (!toggle || !panel || !back) return;
        const open = () => {
            panel.classList.add('wn-drawer-open');
            back.classList.add('is-active');
            document.body.classList.add('wn-drawer-active');
            toggle.setAttribute('aria-expanded', 'true');
            ae('drawer_open', {});
        };
        const close = () => {
            panel.classList.remove('wn-drawer-open');
            back.classList.remove('is-active');
            document.body.classList.remove('wn-drawer-active');
            toggle.setAttribute('aria-expanded', 'false');
        };
        toggle.onclick = () => {
            panel.classList.contains('wn-drawer-open') ? close() : open();
        };
        back.onclick = close;
        // Swipe-down on the drag-handle (top 16px of the drawer) closes it.
        let _touchStartY = null;
        panel.addEventListener('touchstart', (e) => {
            if (!panel.classList.contains('wn-drawer-open')) return;
            // Only handle swipes that start in the top 28px (handle area).
            const r = panel.getBoundingClientRect();
            if (e.touches[0].clientY - r.top < 28) _touchStartY = e.touches[0].clientY;
        }, { passive: true });
        panel.addEventListener('touchmove', (e) => {
            if (_touchStartY == null) return;
            const dy = e.touches[0].clientY - _touchStartY;
            if (dy > 60) { close(); _touchStartY = null; }
        }, { passive: true });
        panel.addEventListener('touchend', () => { _touchStartY = null; }, { passive: true });
    }

    // ─── Settings popover ─────────────────────────────────────────────
    function wireSettingsPopover() {
        const btn = $('wnSettings');
        const pop = $('wnSettingsPopover');
        if (!btn || !pop) return;

        btn.onclick = (e) => {
            e.stopPropagation();
            pop.hidden = !pop.hidden;
        };
        document.addEventListener('click', (e) => {
            if (!pop.hidden && !pop.contains(e.target) && e.target !== btn) {
                pop.hidden = true;
            }
        });

        // Bind each toggle to __toolSettings so the choice persists across
        // sessions. Defaults are the most common preference.
        const TS = window.__toolSettings;
        const compress = $('wnOptCompress');
        const snap     = $('wnOptSnap');
        const borders  = $('wnOptSheetBorders');
        if (compress) compress.checked = TS?.load?.(SLUG, 'image_compress', true) !== false;
        if (snap)     snap.checked     = TS?.load?.(SLUG, 'snap_enabled',  true) !== false;
        if (borders)  borders.checked  = TS?.load?.(SLUG, 'show_seams',    true) !== false;

        compress?.addEventListener('change', () => {
            TS?.save?.(SLUG, 'image_compress', compress.checked);
        });
        snap?.addEventListener('change', () => {
            TS?.save?.(SLUG, 'snap_enabled', snap.checked);
        });
        borders?.addEventListener('change', () => {
            TS?.save?.(SLUG, 'show_seams', borders.checked);
            // Toggle visibility of existing seam guide lines
            if (state.konva) {
                state.konva.layers.overlay.children
                    .filter((n) => n.getAttr?.('isSeam'))
                    .forEach((n) => n.visible(borders.checked));
                state.konva.layers.overlay.batchDraw();
            }
        });
    }

    // ─── Boot ─────────────────────────────────────────────────────────
    async function boot() {
        watchConsentInset();
        wireStartScreen();
        wireWorkspace();
        ae('tool_view', {});
        // Tier-0: a single critical "tool_used" per page-load. Drives the
        // dashboard counters in Pino. Timbrica3D.init also fires its own
        // event but we want a wall-newspaper-specific counter.
        aeCritical('tool_used', {});
        try { window.Timbrica3D?.init?.(SLUG); } catch (e) { /* noop */ }

        // Did the user open a #join=<peerId> link? Try to join the host —
        // if that succeeds, the workspace will appear with the hosted project
        // and we skip the rest of the start flow.
        if (location.hash.includes('join=')) {
            // Wait for collab.js + PeerJS to be ready (they're <script defer>)
            const start = Date.now();
            while (Date.now() - start < 5000 && (!window.WNCollab || !window.Peer)) {
                await new Promise((r) => setTimeout(r, 50));
            }
            const joined = await joinFromHash();
            if (joined) return;
        }

        // Templates render into the start screen as soon as Konva is up; they
        // are previews, so nothing waits on them.
        whenKonvaReady().then(() => renderStartTemplates()).catch(() => {});

        // Show recent projects + recovery offer (non-blocking)
        if (window.WNStorage) {
            try { await renderRecentProjects(); } catch (e) { /* noop */ }
            try { await checkRecovery();        } catch (e) { /* noop */ }
        }

        // No welcome tour on load — see OB_WELCOME.
    }

    /* A narrow, explicit surface for the Playwright suites.
     *
     * The alternative is driving crop, lock, clipboard and camera behaviour
     * through synthetic pointer events, which tests the event plumbing rather
     * than the behaviour and breaks on every layout change. Everything here is
     * already reachable from the UI — this only gives the tests a stable name
     * for it. Nothing on the page reads it. */
    window.__wnTest = {
        fillFrame: (frame, blob) => fillFrameWithBlob(frame, blob),
        renderProperties,
        setNodeLocked,
        copySelection,
        pasteClipboard,
        zoomToFit,
        setZoom,
        panBy,
        insertBlock,
        canvasSize: () => ({ w: state.project?.canvas.width_px || 0, h: state.project?.canvas.height_px || 0 }),
        canvas: () => state.project?.canvas,
        preflight: () => runPreflight().map((f) => ({
            kind: f.kind, level: f.level, dpi: f.dpi, mm: f.mm,
            type: f.node.getAttr('elType'),
        })),
        setRosterCount: (n) => { _rosterCount = n; },
        historyLength: () => state.history.stack.length,
        serialize: (n) => serializeNode(n),
        deserialize: (el) => deserializeNode(el),
        undo,
        redo,
        applyTemplate,
        canvasTitles: () => state.project?.canvas?.page_titles,
        setPageTitle: (i, name) => {
            const cv = state.project?.canvas;
            if (!cv || !Array.isArray(cv.page_titles)) return;
            const before = cv.page_titles[i];
            cv.page_titles[i] = name;
            syncCornerTitle(i, before, name);
            markDirty();
            pushHistory();
            renderPageNav();
            renderSheetLabels();
        },
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }
})();
